// src/routes.ts
var apiRoot = "/api";
var imagePath = (tableLogicalName, recordId, columnName) => `${apiRoot}/files/${encodeURIComponent(tableLogicalName)}/${encodeURIComponent(recordId)}/${encodeURIComponent(columnName)}/image`;
var authRoot = `${apiRoot}/auth`;
var manageRoot = `${authRoot}/manage`;
var tables = {
  /** Template: `/api/table/{tableLogicalName}`. POST creates a record. */
  createRoute: `${apiRoot}/table/{tableLogicalName}`,
  /** Template: `/api/table/{tableLogicalName}/{recordId}`. GET reads, PATCH updates, DELETE removes. */
  readUpdateDeleteRoute: `${apiRoot}/table/{tableLogicalName}/{recordId}`,
  /** Resolved URL for creating a record of the given table. */
  getCreateRoute: (tableLogicalName) => `${apiRoot}/table/${tableLogicalName}`,
  /**
   * Resolved URL for retrieving a specific record, optionally with a column allow-list.
   * @param columns Optional list of column logical names — joined with `,` and sent as `?columns=`.
   */
  getRetrieveRoute: (tableLogicalName, recordId, columns) => {
    const base = `${apiRoot}/table/${tableLogicalName}/${recordId}`;
    return columns && columns.length > 0 ? `${base}?columns=${columns.join(",")}` : base;
  },
  /** Resolved URL for updating a record (PATCH). */
  getUpdateRoute: (tableLogicalName, recordId) => `${apiRoot}/table/${tableLogicalName}/${recordId}`,
  /** Resolved URL for deleting a record (DELETE). */
  getDeleteRoute: (tableLogicalName, recordId) => `${apiRoot}/table/${tableLogicalName}/${recordId}`
};
var manage = {
  /** Base path under which all manage endpoints live (`/api/auth/manage`). */
  root: manageRoot,
  /** GET — read first/last/mobile from the linked Dataverse contact + Identity status flags. */
  profile: `${manageRoot}/profile`,
  /** POST — update first/last/mobile on the linked Dataverse contact. */
  updateProfile: `${manageRoot}/profile`,
  /** POST — add a local password to an account that doesn't have one. */
  setPassword: `${manageRoot}/password/set`,
  /** POST — change the local password (requires the current password). */
  changePassword: `${manageRoot}/password/change`,
  /** POST — start a change-email flow by emailing the new address a confirmation link. */
  changeEmail: `${manageRoot}/email/change`,
  /** POST — re-send the confirmation link to the user's current email. */
  sendEmailConfirmation: `${manageRoot}/email/send-confirmation`,
  /** GET — read 2FA status. */
  twoFactorStatus: `${manageRoot}/2fa`,
  /** GET — shared key + otpauth URI for the QR-code enrollment screen. */
  authenticatorSetup: `${manageRoot}/authenticator/setup`,
  /** POST — verify a code from the authenticator app. */
  verifyAuthenticator: `${manageRoot}/authenticator/verify`,
  /** POST — rotate the authenticator key (also disables 2FA). */
  resetAuthenticator: `${manageRoot}/authenticator/reset`,
  /** POST — disable 2FA on the account. */
  disable2fa: `${manageRoot}/2fa/disable`,
  /** POST — regenerate the user's recovery codes. */
  generateRecoveryCodes: `${manageRoot}/2fa/recovery-codes/generate`,
  /** POST — clear this browser's "trust this device" 2FA cookie. */
  forget2faClient: `${manageRoot}/2fa/forget-browser`,
  /** GET — list the signed-in user's linked external logins + available providers to add. */
  externalLogins: `${manageRoot}/external-logins`,
  /** GET — combined snapshot of sign-in paths (linked external logins + whether a local password exists). */
  loginInfo: `${manageRoot}/login-info`,
  /** GET — kicks off the OAuth flow that links an additional external login. Full-page navigate, do not fetch. */
  linkExternalLogin: `${manageRoot}/external-logins/link`,
  /** GET — OAuth callback target for {@link linkExternalLogin}. */
  linkExternalLoginCallback: `${manageRoot}/external-logins/link/callback`,
  /** POST — remove one external login by provider + key. */
  removeExternalLogin: `${manageRoot}/external-logins/remove`,
  /** GET — the signed-in user's passkeys. */
  passkeys: `${manageRoot}/passkeys`,
  /** POST — the options to pass `navigator.credentials.create()` to make a passkey. */
  passkeyCreationOptions: `${manageRoot}/passkeys/creation-options`,
  /** POST — save the passkey `navigator.credentials.create()` returned. */
  addPasskey: `${manageRoot}/passkeys`,
  /** POST — rename one of the user's passkeys. */
  renamePasskey: `${manageRoot}/passkeys/rename`,
  /** POST — remove one of the user's passkeys. */
  removePasskey: `${manageRoot}/passkeys/remove`,
  /** GET — dump every `[PersonalData]` property + linked external logins. */
  personalData: `${manageRoot}/personal-data`,
  /** POST — permanently delete the user's account. */
  deletePersonalData: `${manageRoot}/personal-data/delete`,
  /** Resolved URL for {@link linkExternalLogin} with the provider + return URL pre-encoded. */
  getLinkExternalLoginRoute: (provider, returnUrl) => {
    const query = `?provider=${encodeURIComponent(provider)}`;
    return returnUrl ? `${manageRoot}/external-logins/link${query}&returnUrl=${encodeURIComponent(returnUrl)}` : `${manageRoot}/external-logins/link${query}`;
  }
};
var auth = {
  /** Base path under which all auth endpoints live (`/api/auth`). */
  root: authRoot,
  /** POST — submit credentials. */
  login: `${authRoot}/login`,
  /** POST — submit the second-factor code after a Login that returned RequiresTwoFactor. */
  loginTwoFactor: `${authRoot}/login/2fa`,
  /** POST — clear the auth cookie. */
  logout: `${authRoot}/logout`,
  /** POST — create a new user account. */
  register: `${authRoot}/register`,
  /** POST — what an invitation link offers the current visitor. */
  invitation: `${authRoot}/invitation`,
  /** POST — accept an invitation as the signed-in contact. */
  invitationAccept: `${authRoot}/invitation/accept`,
  /** POST — create a local login on the invited contact, and accept. */
  invitationRegister: `${authRoot}/invitation/register`,
  /** POST — link the external sign-in in progress to the invited contact, and accept. */
  invitationExternal: `${authRoot}/invitation/external`,
  /** POST — send a password-reset email. */
  forgotPassword: `${authRoot}/forgot-password`,
  /** POST — complete a password reset with the email-link token. */
  resetPassword: `${authRoot}/reset-password`,
  /** POST — confirm a registered user's email with the email-link token. */
  confirmEmail: `${authRoot}/confirm-email`,
  /** POST — send a fresh confirmation email. */
  resendEmailConfirmation: `${authRoot}/resend-email-confirmation`,
  /** POST — the options to pass `navigator.credentials.get()` to sign in with a passkey. */
  passkeyRequestOptions: `${authRoot}/passkey/request-options`,
  /** POST — sign in with the passkey `navigator.credentials.get()` returned. */
  passkeyLogin: `${authRoot}/passkey/login`,
  /** GET — anonymous auth options for the sign-in UI: the local-accounts flag plus the configured external (OAuth) providers. */
  options: `${authRoot}/options`,
  /** GET — kicks off the OAuth flow for the named external provider. Full-page navigate, do not fetch. */
  externalLogin: `${authRoot}/external-login`,
  /** GET — snapshot the in-flight external-login cookie set by the OAuth callback. */
  externalLoginPending: `${authRoot}/external-login/pending`,
  /** POST — complete an in-flight external login by registering / linking the account. */
  externalLoginConfirm: `${authRoot}/external-login/confirm`,
  /** POST — complete an in-flight external login when the user has chosen between multiple matching identities. */
  externalLoginSelect: `${authRoot}/external-login/select`,
  /** GET — write the chooser's "remember my choice" cookie + redirect to `returnUrl`. */
  externalLoginRememberChoice: `${authRoot}/external-login/remember-choice`,
  /** POST — swap the current cookie for the user's alt identity (the contact↔systemuser sibling). */
  switchIdentity: `${authRoot}/switch-identity`,
  /** POST — start impersonating a portal contact. Internal users holding the SystemAdmin role only. */
  impersonate: `${authRoot}/impersonate`,
  /** POST — end an impersonation session. Takes no body; the user to return to comes from the session's claims. */
  stopImpersonation: `${authRoot}/stop-impersonation`,
  /** POST — search portal contacts for the impersonation picker. */
  impersonateSearch: `${authRoot}/impersonate/search`,
  /** GET — snapshot of the current user (or anonymous shape). */
  me: `${authRoot}/me`,
  /** Account-management endpoints under `/api/auth/manage/*`. */
  manage,
  /** Resolved URL for {@link externalLogin} with the provider + return URL pre-encoded. */
  getExternalLoginRoute: (provider, returnUrl) => {
    const query = `?provider=${encodeURIComponent(provider)}`;
    return returnUrl ? `${authRoot}/external-login${query}&returnUrl=${encodeURIComponent(returnUrl)}` : `${authRoot}/external-login${query}`;
  }
};
var api = {
  /** Base path for the API (`/api`). */
  root: apiRoot,
  /** Template — POST a FetchXML query to retrieve multiple records. */
  retrieveMultiple: `${apiRoot}/retrieveMultiple`,
  /** Template — GET table metadata. */
  retrieveTableMetadata: `${apiRoot}/tableMetadata/{tableLogicalName}`,
  /**
   * GET the identity-level metadata of every table — the table-picker list.
   *
   * Under the same prefix as {@link retrieveTableMetadata} despite sharing its
   * verb: routing ranks a literal segment above a parameter one, so `headers` is
   * never read as a table name.
   */
  retrieveTableHeaders: `${apiRoot}/tableMetadata/headers`,
  /**
   * POST a list of tables, each naming the columns and relationships to read — the
   * middle ground between {@link retrieveTableMetadata} (one table in full) and
   * {@link retrieveTableHeaders} (every table's name alone). A POST because the
   * request body is a list of lists; it reads rather than writes.
   */
  retrieveTableMetadataBatch: `${apiRoot}/tableMetadata/batch`,
  /** Template — GET view metadata by view id (Guid-constrained route). */
  retrieveViewMetadata: `${apiRoot}/viewMetadata/{viewId}`,
  /** Template — GET every view metadata record for a table. */
  retrieveViewsForTable: `${apiRoot}/viewMetadata/{tableLogicalName}`,
  /** Template — GET localized strings for a culture. */
  retrieveLocalizedStrings: `${apiRoot}/localizedStrings/{culture}`,
  /** Template — POST a batch of OrganizationRequest payloads. */
  executeMultiple: `${apiRoot}/executeMultiple`,
  /**
   * GET — organization-wide settings sourced from the Dataverse `organization` record:
   * default currency plus file-upload constraints (blocked extensions, max upload size).
   * Replaces the legacy `/api/environmentFileSettings` endpoint.
   */
  organizationSettings: `${apiRoot}/organizationSettings`,
  /** POST — clear all server-side caches. SystemAdmin-only. */
  clearAllCaches: `${apiRoot}/caches/clear`,
  /** GET — every server-side cache name. SystemAdmin-only. */
  cacheNames: `${apiRoot}/caches`,
  /** Template — POST to clear a single named cache. SystemAdmin-only. */
  clearCache: `${apiRoot}/caches/{cacheName}/clear`,
  /**
   * GET — admin localization overview: configured sources plus the per-source
   * load records produced by the most recent warmup. SystemAdmin-only. Backs
   * the LocalizationAdmin component (Blazor-only today; React mirror TBD).
   */
  localizationOverview: `${apiRoot}/localizations/overview`,
  /**
   * GET — JSON of the keys one source contributed to one culture during the most
   * recent warmup. `sourceId` + `culture` are query-string parameters (a sourceId
   * can contain colons/slashes, so they're not path segments). SystemAdmin-only.
   */
  localizationSourceDownload: `${apiRoot}/localizations/sources/download`,
  /**
   * GET — JSON of every key the cache will serve for a culture (post-merge winning
   * values across all sources). `culture` is a query-string parameter. SystemAdmin-only.
   */
  localizationMergedDownload: `${apiRoot}/localizations/merged/download`,
  /**
   * GET — structured list of every key the cache will serve for a culture, its winning
   * value, and the source that supplied it. `culture` is a query-string parameter. Backs
   * the LocalizationAdmin "view merged sources" dialog. SystemAdmin-only.
   */
  localizationMergedEntries: `${apiRoot}/localizations/merged/entries`,
  /** GET — whether translation is configured, the managed solution is installed, and the candidate target languages. SystemAdmin-only. */
  localizationTranslationAvailability: `${apiRoot}/localizations/translation/availability`,
  /** POST — translate an uploaded localization file into one or more target languages. SystemAdmin-only. */
  localizationTranslate: `${apiRoot}/localizations/translation`,
  /**
   * Template — GET the ids of the web roles a Power Pages table permission applies to;
   * PUT replaces them with the ids in the body. Enhanced authorization only. SystemAdmin-only.
   */
  tablePermissionWebRoles: `${apiRoot}/admin/table-permissions/{tablePermissionId}/web-roles`,
  /** Template — GET file metadata + (optionally) content for one record/column. */
  retrieveFileInfo: `${apiRoot}/files/{tableLogicalName}/{recordId}/{columnName}`,
  /** Template — POST record ids; returns file info + content for many records of one column. */
  retrieveFilesBatch: `${apiRoot}/files/{tableLogicalName}/{columnName}/batch`,
  /**
   * POST — builds an archive containing the file payload of every supplied
   * record id for the named table/column. Server returns the binary archive
   * bytes (default) or a JSON envelope when `responseFormat` is `Json`.
   */
  createFileArchive: `${apiRoot}/files/createFileArchive`,
  /**
   * GET — a record's image column as an image, no larger than a size: the
   * avatars on a grid's cards. Answers with the image's bytes, so the URL can
   * be an `<img>`'s source, and the browser keeps each version of the image.
   */
  retrieveImage: `${apiRoot}/files/{tableLogicalName}/{recordId}/{columnName}/image`,
  /** Resolved URL for retrieving multiple records via FetchXML. */
  getRetrieveMultipleRoute: (fetchXml) => `${apiRoot}/retrieveMultiple?fetchXml=${encodeURIComponent(fetchXml)}`,
  /**
   * Template — POST a QueryRequest (viewId / fetchXml input modes,
   * searchText, sorts, paging, filters) and get back a shaped
   * QueryResponse. Server-side IQueryService builds the FetchXML via
   * the shared IFetchXmlQueryComposer and runs the query so the client
   * never composes FetchXML directly.
   */
  query: `${apiRoot}/query`,
  /** POST: a page of several tables' rows merged by start date, each table read through the grid's query and security. */
  multiTableFeed: `${apiRoot}/multitable/feed`,
  /** POST: every row of several tables in a range, each table read through the grid's query and security. */
  multiTableRange: `${apiRoot}/multitable/range`,
  /** POST: a page of each of several tables' rows by their titles, not merged, for a search of several tables. */
  multiTablePages: `${apiRoot}/multitable/pages`,
  /**
   * POST: each table's rows with coordinates, for a map — of a view registered for maps, or the
   * tables given — with what their popups show.
   */
  multiTablePlaces: `${apiRoot}/multitable/places`,
  /** GET: the registered tables the user may read, and the multi-table views the server registered. */
  multiTableTables: `${apiRoot}/multitable/tables`,
  /** GET: the record pages registered with `AddRecordPage` of the tables the user may read. */
  recordPages: `${apiRoot}/recordpages`,
  /**
   * Template — POST a LookupSearchRequest (the table, its view, what the user typed, how many
   * records) and get back a LookupSearchResponse: the matching records by name, searched and
   * secured as a grid's, without a grid's count, per-row permissions, groups or figures.
   */
  lookupSearch: `${apiRoot}/lookups/search`,
  /**
   * Template — POST a GridExportRequest (the grid's query, the file format, the columns to write)
   * and get back the file. The server pages through every matching record, up to its configured
   * maximum, and reports the record count and whether the export was cut short in the
   * `X-PPP-Export-Record-Count` / `X-PPP-Export-Truncated` response headers.
   */
  gridExport: `${apiRoot}/grids/export`,
  /**
   * Template — POST a ChartDataRequest (Aggregate / ViewId / FetchXml
   * input modes) and get back a shaped ChartDataResponse. Server-side
   * IChartService builds the FetchXML and runs the query so the
   * client never composes FetchXML directly.
   */
  chartData: `${apiRoot}/charts/data`,
  /**
   * POST a PowerBIEmbedTokenRequest (a registered report's key) and get back a
   * PowerBIEmbedTokenResponse: the report's embed URL and a token for the current viewer. Who may
   * see the report is decided server-side, from its access rows.
   */
  powerBIEmbedToken: `${apiRoot}/powerbi/embed-token`,
  /**
   * GET a MapSettingsResponse: a map provider's settings from the portal's configuration — Esri's or
   * Google's key, a style or tile URL, or an Azure Maps token issued to the portal's identity. Takes
   * the provider as a `provider` query parameter; without one, the portal's own provider's.
   */
  mapSettings: `${apiRoot}/maps/settings`,
  /**
   * A page to send the browser to, not a call: GET with a `returnUrl`, it connects the signed-in
   * viewer's work account to Power BI — through Microsoft Entra — for items shown with their own
   * Power BI access, and returns them to that page.
   */
  powerBIConnect: `${apiRoot}/powerbi/connect`,
  timeZones: `${apiRoot}/time-zones`,
  /** Resolved URL for retrieving metadata for one view. */
  getRetrieveViewMetadataRoute: (viewId) => `${apiRoot}/viewMetadata/${viewId}`,
  /** Resolved URL for retrieving every view metadata record for a table. */
  getRetrieveViewsForTableRoute: (tableLogicalName) => `${apiRoot}/viewMetadata/${encodeURIComponent(tableLogicalName)}`,
  /** Resolved URL for retrieving metadata for one table. Mirrors C# behavior of not encoding the segment. */
  getRetrieveTableMetadataRoute: (tableLogicalName) => `${apiRoot}/tableMetadata/${tableLogicalName}`,
  /**
   * Resolved URL for retrieving the current user's combined
   * `TableSecurityPermission` mask for a single table. Routes through
   * the server-side `ITablePermissionCache.GetPermissionForUserAsync`
   * lookup so the client can read the table-level answer without firing
   * a grid query.
   * `QueryResponse.tablePermissions` carries the same value when a
   * grid query happens to be in flight; this endpoint covers the other
   * cases (custom toolbars, conditional UI elsewhere on the page).
   */
  getRetrieveTablePermissionsRoute: (tableLogicalName) => `${apiRoot}/permissions/table/${tableLogicalName}`,
  /**
   * Resolved URL for the current user's operations on several tables at once: the tables named,
   * or with none every table the user has any permission on. Mirrors C#
   * `Routes.GetRetrieveTablesPermissionsRoute`.
   */
  getRetrieveTablesPermissionsRoute: (tableLogicalNames) => {
    const query = (tableLogicalNames ?? []).map((name) => `tableNames=${encodeURIComponent(name)}`).join("&");
    return query ? `${apiRoot}/permissions/tables?${query}` : `${apiRoot}/permissions/tables`;
  },
  /**
   * Resolved URL for the operations the current user may run — those named, or with none every one
   * they may run.
   */
  getRetrieveOperationsPermissionsRoute: (operations) => {
    const query = (operations ?? []).map((name) => `operations=${encodeURIComponent(name)}`).join("&");
    return query ? `${apiRoot}/permissions/operations?${query}` : `${apiRoot}/permissions/operations`;
  },
  /** Resolved URL for retrieving localized strings for a culture. */
  getRetrieveLocalizedStringsRoute: (culture) => `${apiRoot}/localizedStrings/${culture}`,
  /** Resolved URL for the localization-bundle manifest endpoint (version + supported locales). */
  getLocalizationBundleManifestRoute: () => `/localizations/version`,
  /**
   * Resolved URL for the per-locale thumbprints endpoint. Returns the
   * content-derived thumbprint for the default bundle plus every loaded
   * table / view at the requested locale. Mirrors C#
   * `Routes.Localizations.GetThumbprintsRoute`.
   */
  getLocalizationThumbprintsRoute: (locale) => `/localizations/${encodeURIComponent(locale)}/thumbprints`,
  /**
   * Resolved URL for the default localization-bundle file (everything outside
   * `tables.*` / `choices.*`) for a given locale + thumbprint. The thumbprint
   * is purely for cache-busting — the server returns the current bundle for
   * the locale regardless of what thumbprint is in the URL. Mirrors C#
   * `Routes.Localizations.GetDefaultBundleRoute`.
   */
  getLocalizationBundleRoute: (locale, thumbprint) => `/localizations/default/${locale}.${thumbprint}.json`,
  /**
   * Resolved URL for the per-table localization-bundle file. Returns every
   * `tables.{name}.*` string for the table plus the global `choices.*`
   * strings any of its columns reference. Mirrors C#
   * `Routes.Localizations.GetTableBundleRoute`.
   */
  getLocalizationTableBundleRoute: (tableName, locale, thumbprint) => `/localizations/tables/${encodeURIComponent(tableName)}/${locale}.${thumbprint}.json`,
  /**
   * Resolved URL for the per-view localization-bundle file. Returns every
   * `tables.{owningTable}.views.{viewId}.*` string. Owning table is resolved
   * server-side from the view id. Mirrors C# `Routes.Localizations.GetViewBundleRoute`.
   */
  getLocalizationViewBundleRoute: (viewId, locale, thumbprint) => `/localizations/views/${viewId}/${locale}.${thumbprint}.json`,
  /** Resolved URL for executing multiple requests. */
  getExecuteMultipleRoute: (returnResponses) => `${apiRoot}/executeMultiple?returnResponses=${returnResponses}`,
  /** Resolved URL for retrieving file info on one record/column. */
  getRetrieveFileInfoRoute: (tableLogicalName, recordId, columnName, includeData) => `${apiRoot}/files/${encodeURIComponent(tableLogicalName)}/${recordId}/${encodeURIComponent(columnName)}?includeData=${includeData}`,
  /** Resolved URL for the batch retrieve-files endpoint. Body is a JSON array of record ids. */
  getRetrieveFilesBatchRoute: (tableLogicalName, columnName, includeData) => `${apiRoot}/files/${encodeURIComponent(tableLogicalName)}/${encodeURIComponent(columnName)}/batch?includeData=${includeData}`,
  /**
   * Resolved URL for a record's image at a size. `version` is the image
   * column's timestamp (`{column}_timestamp`): with it the browser keeps the
   * image until it changes; without it, it asks again each time.
   */
  getRetrieveImageRoute: (tableLogicalName, recordId, columnName, version, size) => {
    const path = `${imagePath(tableLogicalName, recordId, columnName)}?size=${size}`;
    return version === null || version === void 0 ? path : `${path}&v=${encodeURIComponent(String(version))}`;
  },
  /**
   * Resolved URL for a record's image at the size it was stored: an image column's full-size
   * image, or a file column's image file, as it is.
   */
  getRetrieveFullImageRoute: (tableLogicalName, recordId, columnName, version) => {
    const path = `${imagePath(tableLogicalName, recordId, columnName)}?full=true`;
    return version === null || version === void 0 ? path : `${path}&v=${encodeURIComponent(String(version))}`;
  },
  /** Resolved URL for clearing one named cache. */
  getClearCacheRoute: (cacheName) => `${apiRoot}/caches/${encodeURIComponent(cacheName)}/clear`,
  /** Concrete URL for {@link api.tablePermissionWebRoles}. */
  getTablePermissionWebRolesRoute: (tablePermissionId) => `${apiRoot}/admin/table-permissions/${encodeURIComponent(tablePermissionId)}/web-roles`,
  /** Table-scoped CRUD routes. */
  tables,
  /** SPA-facing authentication endpoints. */
  auth
};
var ui = {
  localization: {
    /** Template — POST to set the culture cookie + redirect. */
    culture: "Culture/{culture}",
    /** Resolved URL for setting the culture cookie. Mirrors C# behavior of not encoding either segment. */
    getCultureRoute: (culture, returnUrl) => `Culture/${culture}?redirectUri=${returnUrl}`
  }
};
var Routes = {
  api,
  ui
};

// src/generated/result-codes.ts
var AcceptInvitationResult = {
  /** Accepted: the invitation's roles were granted, and the user is signed in. */
  Accepted: 0,
  /** The link doesn't work. See Invalid. */
  Invalid: 1,
  /** Nobody is signed in, or no external sign-in is in progress. */
  SignInRequired: 2,
  /** Someone other than the invited contact is signed in. */
  SignedInAsSomeoneElse: 3,
  /** The invited contact already has a login of their own; they sign in with it to accept, rather than adding a new one through the invitation. */
  AlreadyRegistered: 4,
  /** The account can't sign in now — locked out, or not allowed to sign in. */
  LockedOut: 5,
  /** The login couldn't be set up; see Errors. */
  Failed: 6
};
var AddPasskeyResult = {
  /** The passkey was saved; the user can sign in with it. */
  Added: 0,
  /** The passkey wasn't saved: the browser's answer didn't check out — made for another site or for a challenge this server didn't issue, or already saved for somebody. */
  Failed: 1,
  /** The user already has as many passkeys as one user may (MaxPasskeys). */
  LimitReached: 2
};
var ArchiveFormat = {
  /** Standard ZIP archive (application/zip, .zip extension). Built via ZipArchive with Optimal. */
  Zip: 0
};
var CaptchaForms = {
  /** No form. */
  None: 0,
  /** Signing in with an email address and password (POST /api/auth/login). */
  Login: 1,
  /** Creating a local account (POST /api/auth/register). */
  Register: 2,
  /** Asking for a password-reset link (POST /api/auth/forgot-password). */
  ForgotPassword: 4,
  /** Asking for another email-confirmation link (POST /api/auth/resend-email-confirmation). */
  ResendEmailConfirmation: 8,
  /** Completing a sign-in through an external provider with an address the provider didn't verify (POST /api/auth/external-login/confirm), where the portal mails a confirmation link to that address. A sign-in whose address the provider verified completes without one. */
  ExternalLogin: 16
};
var CaptchaProvider = {
  /** No CAPTCHA: no form shows a challenge and the server checks none. The default. */
  None: 0,
  /** Cloudflare Turnstile. Usually solved without the visitor doing anything. */
  Turnstile: 1,
  /** Google reCAPTCHA v2, the "I'm not a robot" checkbox. */
  ReCaptchaV2: 2,
  /** hCaptcha, the checkbox widget. */
  HCaptcha: 3,
  /** Google reCAPTCHA v3, which shows the visitor nothing to solve: the page asks Google for a token when the form is submitted, and the server refuses one whose score is too low. */
  ReCaptchaV3: 4
};
var ChangeEmailResult = {
  /** A confirmation email was sent to the new address. The change does not take effect until the user clicks the link. */
  ConfirmationEmailSent: 0,
  /** The submitted email matches the user's current email. Nothing was sent. The client should show a "this is already your email" message. */
  SameAsCurrentEmail: 1
};
var ChangePasswordResult = {
  /** The password was changed. The auth cookie has been refreshed to pick up the new security stamp so other sessions are invalidated. */
  Success: 0,
  /** The supplied old password did not match the current password. The client should show an "incorrect current password" error and allow retry. */
  IncorrectOldPassword: 1,
  /** The new password did not satisfy the host's IdentityOptions.Password rules. Errors carries the per-rule messages so the client can show actionable feedback. */
  InvalidPassword: 2
};
var CloseRecordAction = {
  /** An opportunity won (WinOpportunity). */
  Win: 0,
  /** An opportunity lost (LoseOpportunity). */
  Lose: 1,
  /** A case resolved (CloseIncident). */
  Resolve: 2,
  /** A quote won (WinQuote). */
  WinQuote: 3,
  /** A quote closed as lost or canceled (CloseQuote). */
  CloseQuote: 4,
  /** An order fulfilled (FulfillSalesOrder). */
  FulfillOrder: 5,
  /** An order canceled (CancelSalesOrder). */
  CancelOrder: 6
};
var ColumnSecurityPermission = {
  /** The column can be neither read nor written. */
  None: 0,
  /** The column's real value is returned. */
  Read: 1,
  /** The column can be included in a create or update. */
  Write: 2,
  /** Full access — the default for every column not listed in ColumnPermissions. */
  All: 3
};
var ColumnSourceType = {
  /** Stored as written. The default. */
  Simple: 0,
  /** Computed from a formula over this record's own columns. */
  Calculated: 1,
  /** Aggregated from related records. */
  Rollup: 2
};
var ColumnType = {
  /** A Boolean attribute. Value = 0. */
  Boolean: 0,
  /** An attribute that represents a customer. Value = 1. */
  Customer: 1,
  /** A date/time attribute. Value = 2. */
  DateTime: 2,
  /** A decimal attribute. Value = 3. */
  Decimal: 3,
  /** A double attribute. Value = 4. */
  Double: 4,
  /** An integer attribute. Value = 5. */
  Integer: 5,
  /** A lookup attribute. Value = 6. */
  Lookup: 6,
  /** A memo attribute. Value = 7. */
  Memo: 7,
  /** A money attribute. Value = 8. */
  Money: 8,
  /** An owner attribute. Value = 9. */
  Owner: 9,
  /** A partylist attribute. Value = 10. */
  PartyList: 10,
  /** A picklist (single-select choice) attribute. Value = 11. */
  Choice: 11,
  /** A state attribute. Value = 12. */
  State: 12,
  /** A status attribute. Value = 13. */
  Status: 13,
  /** A string attribute. Value = 14. */
  String: 14,
  /** A unique identifier (GUID) attribute. Value = 15. */
  Uniqueidentifier: 15,
  /** An attribute that contains calendar rules. Value = 16. */
  CalendarRules: 16,
  /** An attribute that is created by the system at run time. Value = 17. */
  Virtual: 17,
  /** A big integer (long) attribute. Value = 18. */
  BigInt: 18,
  /** A managed property attribute. Value = 19. */
  ManagedProperty: 19,
  /** An entity name attribute. Value = 20. */
  EntityName: 20,
  /** A multi-select choice attribute. Value = 40. PowerPortalsPro-specific (not present in Dataverse AttributeTypeCode). */
  MultiSelectChoice: 40,
  /** A file attachment attribute. Value = 41. PowerPortalsPro-specific (not present in Dataverse AttributeTypeCode). */
  File: 41,
  /** An image attribute. Value = 42. PowerPortalsPro-specific (not present in Dataverse AttributeTypeCode). */
  Image: 42,
  /** The records related to a row through a 1:N or N:N relationship, as a list. Value = 43. PowerPortalsPro-specific: not a Dataverse column at all, but a grid column whose value is loaded alongside the row page — see RelatedRecordsColumn. */
  RelatedRecords: 43
};
var ConfirmExternalLoginResult = {
  /** The external login was linked (creating a new local account if needed) and the user has been signed in. The auth cookie is set on the response. */
  SignedIn: 0,
  /** The account was created but the host requires email confirmation. A confirmation link has been sent; the user is NOT signed in yet. */
  ConfirmationEmailSent: 1,
  /** No external-login cookie was found — the user landed on the confirm endpoint without a fresh OAuth callback. The client should redirect back to the login page. */
  NoPendingExternalLogin: 2,
  /** Identity rejected the create / add-login operation (e.g. duplicate email already linked to a different account). Errors carries the per-rule messages. */
  Failure: 3,
  /** The account exists and the external login resolved to it, but Identity will not sign it in — it is locked out, or some other SignIn requirement the host configured is unmet. The client should send the user to the lockout page rather than showing a generic failure. Signing in through an external provider is not a way around a lockout: the checks that guard password sign-in guard this path too. */
  LockedOut: 4,
  /** Nobody on the portal matches this sign-in, and the portal doesn't create new users (AuthClientOptions.SignUpEnabled is off), so it was refused. The client should say that only existing contacts can sign in, rather than showing a generic failure. Only returned when the provider verified the submitted address, so the person told is the one who owns it. For any other address the refusal is mailed to it and the reply is ConfirmationEmailSent, as it is for an address that has a contact. */
  SignUpDisabled: 5
};
var DateGrouping = {
  /** No date grouping — the column is grouped by its raw value. */
  None: 0,
  /** Each day. A chart's label: "15 Jan 2024". */
  Day: 1,
  /** Each ISO 8601 week, Monday to Sunday, numbered in the year that holds its Thursday. A chart's label: "W1 2025", the week that starts on 30 December 2024. */
  Week: 2,
  /** Each month. A chart's label: "Jan 2024". */
  Month: 3,
  /** Each calendar quarter. A chart's label: "Q1 2024". */
  Quarter: 4,
  /** Each calendar year. A chart's label: "2024". */
  Year: 5,
  /** Each of the organization's fiscal periods, as its fiscal settings divide the year. A chart's label: the period as the organization names it, such as "Quarter 3 FY2026". */
  FiscalPeriod: 6,
  /** Each of the organization's fiscal years. A chart's label: the year as the organization names it, such as "FY2026". */
  FiscalYear: 7,
  /** Each minute, or each span of RangeSize (a grid's RangeSize) minutes from the hour. The column must hold a time. A chart's label: the date and time the span starts. */
  Minute: 8,
  /** Each hour, or each span of RangeSize (a grid's RangeSize) hours from midnight. The column must hold a time. A chart's label: the date and time the span starts. */
  Hour: 9,
  /** The minute of the hour, every hour's folded together, or spans of RangeSize minutes. A chart only; the column must hold a time. A chart's label: ":15". */
  MinuteOfHour: 10,
  /** The hour of the day, every day's folded together — a time-of-day profile — or spans of RangeSize hours. A chart only; the column must hold a time. A chart's label: the time the span starts, such as "2:00 PM". */
  HourOfDay: 11,
  /** The day of the week, every week's folded together, from the culture's first day of the week. A chart only. A chart's label: the day's short name, such as "Mon". */
  DayOfWeek: 12,
  /** The day of the month, every month's folded together. A chart only. A chart's label: "15". */
  DayOfMonth: 13,
  /** The ISO 8601 week of the year, every year's folded together. A chart only. A chart's label: "W15". */
  WeekOfYear: 14,
  /** The month of the year, every year's folded together. A chart only. A chart's label: "Jan". */
  MonthOfYear: 15,
  /** The quarter of the year, every year's folded together. A chart only. A chart's label: "Q1". */
  QuarterOfYear: 16
};
var DateTimeBehavior = {
  /** Only the date portion is stored; no time component. */
  DateOnly: 1,
  /** The value is stored in UTC and converted to the user's local time zone for display. */
  UserLocal: 2,
  /** The value is stored and displayed exactly as entered, without time zone conversion. */
  TimeZoneIndependent: 3
};
var DeletePersonalDataResult = {
  /** The account was deleted and the auth cookie has been cleared. */
  Success: 0,
  /** The user has a local password set, and one was supplied, but it did not match. The client should show an "incorrect password" error and allow retry. */
  IncorrectPassword: 1,
  /** The user has a local password set but the request body's password field was empty. The client should prompt for the password and resubmit. Distinct from IncorrectPassword so the UI can choose between "please enter your password" and "that password is wrong." */
  RequireLocalPassword: 2
};
var DistanceUnit = {
  /** Statute miles, of 1,609.344 meters. */
  Miles: 0,
  /** Kilometers. */
  Kilometers: 1
};
var EntityRole = {
  /** The record is on the relationship's first side: an associate made with this role stores it in the intersect table's first column. */
  Referencing: 0,
  /** The record is on the relationship's second side: an associate made with this role stores it in the intersect table's second column. */
  Referenced: 1
};
var ExternalLoginCandidateKind = {
  /** The candidate is a Dataverse contact record. */
  Contact: 0,
  /** The candidate is a Dataverse systemuser record. */
  SystemUser: 1
};
var FileArchiveResponseFormat = {
  /** Returns the raw archive bytes as the response body, with Content-Type: application/zip (or the matching MIME for the chosen ArchiveFormat) and a Content-Disposition: attachment; filename=… header. The caller hands the response directly to the browser's download path — no base64 round-trip. Default mode. */
  BinaryStream: 0,
  /** Returns a JSON envelope (FileArchiveJsonResponse) that wraps the archive's bytes as base64. Useful when the consumer wants to inspect headers / filename / size programmatically before triggering a download, or when the host environment can't stream binary responses (some test harnesses, some service workers). Trades ~33% wire-size overhead for the JSON wrapper. */
  Json: 1
};
var GridExportFormat = {
  /** An Excel workbook (.xlsx). */
  Excel: 0,
  /** A comma-separated values file (.csv), UTF-8: numbers and dates as plain values, everything else as the grid shows it. */
  Csv: 1
};
var ImpersonateResult = {
  /** The target contact was signed in and the auth cookie has been swapped. */
  Started: 0,
  /** The current request is unauthenticated. */
  NotAuthenticated: 1,
  /** The caller may not impersonate: they don't satisfy [AuthorizeSystemAdmin], which wants a real internal systemuser holding an administrator security role. A contact backed by a stub systemuser is still a portal contact and can never impersonate, whatever its roles are named. */
  NotAuthorized: 2,
  /** No active contact exists with the requested id. Also returned when the id belongs to a systemuser — the lookup only ever consults the contact store, so an internal user's id simply doesn't resolve. */
  TargetNotFound: 3,
  /** The contact exists but the host's impersonation target filter rejected it. */
  TargetNotImpersonatable: 4,
  /** The session is already impersonating. Nesting isn't supported — it would make the return path order-dependent — so stop the current impersonation first. */
  AlreadyImpersonating: 5
};
var InvitationLookupResult = {
  /** The link doesn't work: it is unknown, used, revoked, expired, or for another portal. Which one is deliberately not said. */
  Invalid: 0,
  /** The invited contact has no login yet: they create one, with a password or an external sign-in. */
  Register: 1,
  /** The invited contact already has a login, and nobody is signed in: they sign in, then accept. */
  SignIn: 2,
  /** The invited contact is signed in: they can accept. */
  Accept: 3,
  /** Someone other than the invited contact is signed in: they sign out to continue. */
  SignedInAsSomeoneElse: 4
};
var JoinOperator = {
  /** Inner join — only matching rows are returned. */
  Inner: 0,
  /** Left outer join — unmatched parent rows are returned with null link columns. */
  LeftOuter: 1,
  /** Natural join. */
  Natural: 2,
  /** Match first row using cross apply. */
  MatchFirstRowUsingCrossApply: 3,
  /** In join — semi-join used for subqueries. */
  In: 4,
  /** Exists join — semi-join evaluating existence. */
  Exists: 5,
  /** Any-match semi-join. */
  Any: 6,
  /** Inverse of Any. */
  NotAny: 7,
  /** All-match semi-join. */
  All: 8,
  /** Inverse of All. */
  NotAll: 9
};
var LocalizationSourceKind = {
  /** Strings derived from Dataverse table metadata — display names, descriptions, choice option labels, and view names, drawn from whatever language packs are installed in the environment. Loads first in the warmup. */
  TableMetadata: 0,
  /** JSON files stored in Dataverse as web resources under the /PowerPortalsPro/Localization/ path prefix. Loads after table metadata. */
  WebResource: 1,
  /** JSON or HTML files loaded from a directory or explicit path registered via String) / String). Loads LAST, so its values override anything an earlier source supplied for the same key. */
  FolderFile: 2
};
var LocalizationSourceStatus = {
  /** Load completed successfully; key counts and cultures are populated. */
  Loaded: 0,
  /** Load threw an exception; Error captures the message. */
  Failed: 1,
  /** Source was discovered but produced no strings (empty file, no matching metadata, etc.). */
  Skipped: 2
};
var LoginResult = {
  /** Credentials were accepted and the auth cookie has been set. The client should navigate to the post-login destination (typically ReturnUrl). */
  Success: 0,
  /** Credentials were accepted but the account requires a second factor. The server has sent the 2FA code (e.g., via email) and is waiting for a follow-up call to POST /api/auth/login/2fa. No auth cookie is set yet. */
  RequiresTwoFactor: 1,
  /** The email/password pair did not match any account, or the password was wrong. The client should show a generic "invalid credentials" message — the server deliberately does not distinguish between "no such email" and "wrong password" to avoid account-enumeration attacks. */
  InvalidCredentials: 2,
  /** The account exists and the password matched, but the email address has not been confirmed and the host configured SignIn.RequireConfirmedAccount. The client should redirect to the registration-confirmation flow. */
  EmailNotConfirmed: 3,
  /** The account is locked out (too many failed attempts, manual lockout, etc.). The client should show the lockout page. */
  LockedOut: 4
};
var MemoFormat = {
  /** Plain text. The default. */
  Text: 0,
  /** Plain text shown in a larger box. */
  TextArea: 1,
  /** HTML, written and shown with a rich-text editor. */
  RichText: 2,
  /** The body of an email: HTML Dataverse's email activities write. */
  Email: 3,
  /** A JSON document. */
  Json: 4
};
var MultiTableFailureReason = {
  /** Something went wrong reading it. */
  Error: 0,
  /** Its view no longer exists, or the user may not read it. */
  ViewNotFound: 1,
  /** Its view's query or the table's registration can't be read as asked. */
  InvalidQuery: 2,
  /** Dataverse took too long. */
  Timeout: 3
};
var MultiTableOrderType = {
  /** Text, compared ignoring case and accents. */
  Text: 0,
  /** A whole number. */
  WholeNumber: 1,
  /** A decimal number. */
  Decimal: 2,
  /** A floating-point number. */
  Float: 3,
  /** An amount of money. */
  Currency: 4,
  /** A date with no time. */
  DateOnly: 5,
  /** A date and time, kept in the user's time zone. */
  UserLocalDateTime: 6,
  /** A date and time kept as entered, in no time zone. */
  TimeZoneIndependentDateTime: 7
};
var MultiTablePageOrder = {
  /** The first order of each table's view's query, else its title. */
  View: 0,
  /** Each table's title. */
  Title: 1,
  /** When each row was last changed. */
  ModifiedOn: 2,
  /** When each row was made. */
  CreatedOn: 3,
  /** The rows whose title matches the search first, then the rest, each by title — ascending, an exact match before the longer ones it begins. Without a search, by title. */
  BestMatch: 4
};
var MultiTableSeries = {
  /** Each occurrence on its own, as a calendar shows them. */
  Occurrences: 0,
  /** The series once, dated by its first occurrence, for a feed a weekly meeting would fill. */
  OneItem: 1
};
var MultiTableUses = {
  /** For none. */
  None: 0,
  /** Timelines, and what reads a feed of rows by date. */
  Timeline: 1,
  /** Calendars, and what reads the rows of a date range. */
  Calendar: 2,
  /** Searches of several tables, which read pages of each table by its title. */
  Search: 4,
  /** Maps, which read the rows of each table with their coordinates. */
  Map: 8,
  /** Every control. */
  All: 15
};
var MultiTableViewType = {
  /** A Dataverse view (savedquery). */
  SystemView: 8944e5,
  /** A user's personal Dataverse view (userquery). */
  PersonalView: 894400001,
  /** A view a portal user saved (ppp_savedview). */
  SavedView: 894400002,
  /** FetchXML given with the table. */
  FetchXml: 894400003
};
var PortalUserType = {
  /** A portal contact under the classic (portal-side) security model — no stub systemuser; permission handlers enforce access. */
  Contact: 0,
  /** A real internal Dataverse systemuser (e.g. Entra ID staff sign-in). */
  SystemUser: 1,
  /** A portal contact whose Dataverse access is enforced through the system-managed (C2) stub systemuser provisioned for it under the Dataverse-native security model. Must never be presented as an internal user. */
  ContactStubUser: 2
};
var QueryAggregate = {
  /** No figure. */
  None: 0,
  /** The total. Number and money columns. */
  Sum: 1,
  /** The mean of the records that have a value. Number and money columns. */
  Average: 2,
  /** The smallest value, or the earliest for a date. Number, money and date columns. */
  Minimum: 4,
  /** The largest value, or the latest for a date. Number, money and date columns. */
  Maximum: 8,
  /** How many records have a value in the column. Any column but a file or image. */
  Count: 16
};
var QueryGroupingStatus = {
  /** The rows are grouped, and Groups lists every group. */
  Grouped: 0,
  /** The column has more distinct values than a grid groups by (see MaximumGroups). The rows come back ungrouped. */
  TooManyGroups: 1,
  /** The rows can't be grouped by the column: it isn't one a grid groups by, the user may not read it, or Dataverse refused the query. The rows come back ungrouped. */
  Unavailable: 2
};
var RegisterResult = {
  /** The account was created and a confirmation email has been sent. The user is NOT signed in yet — they must click the confirmation link first. */
  ConfirmationEmailSent: 0,
  /** The account was created and the user has been signed in directly (the host did not configure SignIn.RequireConfirmedAccount). */
  SignedIn: 1,
  /** An account with the same email already exists. The server returns this rather than silently succeeding to give the client an opportunity to surface a clear error, at the cost of allowing email enumeration. Hosts that prefer the enumeration- resistant path should treat this as success client-side. */
  EmailAlreadyInUse: 2
};
var RelationshipFilterMode = {
  /** Return records currently associated with ParentRecord via the named relationship. For one-to-many, AND-merges a referencingColumn == parentRecord.Id condition. For many-to-many, AND-merges an intersect link-entity plus a condition on the parent-side intersect column. */
  IncludeExistingRecords: 0,
  /** Return records that are NOT currently associated with ParentRecord. Many-to-many only — AND-merges a LEFT OUTER intersect link-entity (filtered to the parent's intersect rows) and an outer null-check on the parent-side intersect column, so unrelated records survive but related records get filtered out. */
  ExcludeExistingRecords: 1
};
var RequiredLevel = {
  /** No requirements are specified. */
  None: 0,
  /** The column is required by the system and cannot be null. */
  SystemRequired: 1,
  /** The column is required by the application and should have a value. */
  ApplicationRequired: 2,
  /** It is recommended that the column has a value, but it is not enforced. */
  Recommended: 3
};
var ResetPasswordResult = {
  /** The password was reset and the user can now sign in with the new password. */
  Success: 0,
  /** The token was missing, malformed, expired, or already used. The client should redirect the user to the "request a new reset link" flow. */
  InvalidOrExpiredToken: 1,
  /** The new password did not satisfy the host's password rules. The accompanying ProblemDetails (in the failed-validation HTTP 400) carries the per-rule error messages from IdentityResult.Errors. */
  InvalidPassword: 2
};
var SearchKind = {
  /** Begins with the text, or contains it after a leading *: text, lookups and choices. */
  Text: 0,
  /** An operator-prefixed comparison with a number (>10, =5). */
  Number: 1,
  /** An operator-prefixed comparison with a day or a year (>2026, <=2019-06-30). */
  Date: 2
};
var SortDirection = {
  /** Smallest values first (A → Z, 0 → 9). */
  Ascending: 1,
  /** Largest values first (Z → A, 9 → 0). */
  Descending: 2
};
var StopImpersonationResult = {
  /** Impersonation ended and the auth cookie is back to the internal user. */
  Stopped: 0,
  /** The session wasn't impersonating — there was nothing to stop. */
  NotImpersonating: 1,
  /** The internal user recorded on the session no longer resolves (disabled or removed in Dataverse mid-session). The impersonated session is signed out rather than left running, so the caller ends up anonymous and must sign in again. */
  ImpersonatorNotFound: 2,
  /** The current request is unauthenticated. */
  NotAuthenticated: 3
};
var SwitchIdentityResult = {
  /** The alt identity was signed in and the auth cookie has been swapped. */
  Switched: 0,
  /** The current principal has no alt-identity claim — there is nothing to switch to. */
  NoAltIdentity: 1,
  /** The alt identity referenced by the claim no longer exists in Dataverse (or is disabled). */
  AltIdentityNotFound: 2,
  /** The current request is unauthenticated. */
  NotAuthenticated: 3,
  /** The alt identity exists but Identity will not sign it in — locked out, or some other SignIn requirement the host configured is unmet. Switching identity must not be a way to reach an account that could not be signed into directly. Also returned while impersonating: the alt identity is the impersonated contact's, not the administrator's. */
  AltIdentityNotAllowed: 4
};
var TableSecurityPermission = {
  /** No permissions. */
  None: 0,
  /** The record can be read. */
  Read: 1,
  /** The record (or table) can have new records created. */
  Create: 2,
  /** The record can be modified. */
  Write: 4,
  /** The record can be deleted. */
  Delete: 8,
  /** The record can be appended to other records (used as the child of a relationship). */
  Append: 16,
  /** Other records can be appended to this record (used as the parent of a relationship). */
  AppendTo: 32,
  /** Every permission — the complete mask. */
  All: 63
};
var TextFormat = {
  /** Text, or a format none of the others name. */
  Text: 0,
  /** An email address. */
  Email: 1,
  /** A web address. */
  Url: 2,
  /** A phone number. */
  Phone: 3
};
var ViewType = {
  /** A public view visible to all users. */
  Public: 0,
  /** An advanced find view. */
  AdvancedFind: 1,
  /** A view used for displaying associated records. */
  Associated: 2,
  /** A view used for Quick Find search. */
  QuickFind: 4,
  /** A view used for displaying team or queue members. */
  Members: 32,
  /** A view used in lookup dialogs. */
  Lookup: 64,
  /** A view used for the lookup display format. */
  LookupDisplay: 2048
};

// src/captcha.ts
function isCaptchaRequired(settings, form) {
  if (!settings) return false;
  const provider = settings.provider ?? CaptchaProvider.None;
  const forms = settings.forms ?? CaptchaForms.None;
  return provider !== CaptchaProvider.None && !!settings.siteKey && form !== CaptchaForms.None && (forms & form) === form;
}
var CAPTCHA_ACTIONS = {
  [CaptchaForms.Login]: "login",
  [CaptchaForms.Register]: "register",
  [CaptchaForms.ForgotPassword]: "forgot_password",
  [CaptchaForms.ResendEmailConfirmation]: "resend_email_confirmation",
  [CaptchaForms.ExternalLogin]: "external_login"
};
function captchaAction(form) {
  const action = CAPTCHA_ACTIONS[form];
  if (!action) throw new Error(`Not a single form: ${form}.`);
  return action;
}

// src/time-zone.ts
var partFormatters = /* @__PURE__ */ new Map();
function partFormatter(timeZone) {
  let format = partFormatters.get(timeZone);
  if (!format) {
    format = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric"
    });
    partFormatters.set(timeZone, format);
  }
  return format;
}
function wallClockOf(instant, timeZone) {
  const parts = partFormatter(timeZone).formatToParts(instant);
  const part = (type) => Number(parts.find((x) => x.type === type)?.value ?? 0);
  return {
    year: part("year"),
    month: part("month"),
    day: part("day"),
    hour: part("hour") % 24,
    minute: part("minute"),
    second: part("second")
  };
}
function utcMsOf(clock) {
  return Date.UTC(clock.year, clock.month - 1, clock.day, clock.hour, clock.minute, clock.second);
}
function toZonedWallClock(instant, timeZone) {
  const clock = wallClockOf(instant, timeZone);
  return new Date(clock.year, clock.month - 1, clock.day, clock.hour, clock.minute, clock.second, instant.getMilliseconds());
}
function fromZonedWallClock(wall, timeZone) {
  const instant = fromWallClock(
    {
      year: wall.getFullYear(),
      month: wall.getMonth() + 1,
      day: wall.getDate(),
      hour: wall.getHours(),
      minute: wall.getMinutes(),
      second: wall.getSeconds()
    },
    timeZone
  );
  return new Date(instant.getTime() + wall.getMilliseconds());
}
function fromWallClock(clock, timeZone) {
  let wanted = { ...clock };
  for (let attempt = 0; attempt < 96; attempt++) {
    const asUtc = utcMsOf(wanted);
    let instant = asUtc - (utcMsOf(wallClockOf(new Date(asUtc), timeZone)) - asUtc);
    instant = asUtc - (utcMsOf(wallClockOf(new Date(instant), timeZone)) - instant);
    if (utcMsOf(wallClockOf(new Date(instant), timeZone)) === asUtc) {
      return new Date(instant);
    }
    const next = new Date(asUtc + 15 * 60 * 1e3);
    wanted = {
      year: next.getUTCFullYear(),
      month: next.getUTCMonth() + 1,
      day: next.getUTCDate(),
      hour: next.getUTCHours(),
      minute: next.getUTCMinutes(),
      second: next.getUTCSeconds()
    };
  }
  return new Date(utcMsOf(wanted));
}

// src/errors.ts
var PowerPortalsProError = class extends Error {
  status;
  url;
  title;
  detail;
  clrType;
  problem;
  constructor(message, init) {
    super(message);
    this.name = "PowerPortalsProError";
    this.status = init.status;
    this.url = init.url;
    this.title = init.title;
    this.detail = init.detail;
    this.clrType = init.clrType;
    this.problem = init.problem;
  }
};
var UnauthorizedAccessError = class extends PowerPortalsProError {
  constructor(message, init) {
    super(message, init);
    this.name = "UnauthorizedAccessError";
  }
};
var ArgumentError = class extends PowerPortalsProError {
  constructor(message, init) {
    super(message, init);
    this.name = "ArgumentError";
  }
};
var ArgumentNullError = class extends ArgumentError {
  constructor(message, init) {
    super(message, init);
    this.name = "ArgumentNullError";
  }
};
var InvalidOperationError = class extends PowerPortalsProError {
  constructor(message, init) {
    super(message, init);
    this.name = "InvalidOperationError";
  }
};
var CaptchaVerificationError = class extends PowerPortalsProError {
  constructor(message, init) {
    super(message, init);
    this.name = "CaptchaVerificationError";
  }
};
var PowerBIConnectionRequiredError = class extends PowerPortalsProError {
  constructor(message, init) {
    super(message, init);
    this.name = "PowerBIConnectionRequiredError";
  }
};
function mapProblemDetails(problem, fallbackStatus, url) {
  const message = problem.detail ?? problem.title ?? `${problem.status ?? fallbackStatus} ${problem.title ?? "HTTP error"}${url ? ` on ${url}` : ""}`;
  const init = {
    status: problem.status ?? fallbackStatus,
    url,
    title: problem.title,
    detail: problem.detail,
    clrType: problem.exceptionType,
    problem
  };
  switch (problem.exceptionType) {
    case "System.UnauthorizedAccessException":
      return new UnauthorizedAccessError(message, init);
    case "System.ArgumentNullException":
      return new ArgumentNullError(message, init);
    case "System.ArgumentException":
      return new ArgumentError(message, init);
    case "System.InvalidOperationException":
      return new InvalidOperationError(message, init);
    case "PowerPortalsPro.Web.Common.Exceptions.CaptchaVerificationException":
      return new CaptchaVerificationError(message, init);
    case "PowerPortalsPro.Web.Common.Exceptions.PowerBIConnectionRequiredException":
      return new PowerBIConnectionRequiredError(message, init);
    default:
      return new PowerPortalsProError(message, init);
  }
}

// src/transport.ts
var Transport = class {
  baseUrl;
  fetchImpl;
  constructor(options = {}) {
    this.baseUrl = (options.baseUrl ?? "").replace(/\/+$/, "");
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }
  /**
   * The address of `path` on the server this transport sends to — for a page the browser is sent
   * to rather than a request this sends.
   */
  url(path) {
    return this.baseUrl + path;
  }
  /** Sends a request and returns the parsed JSON response body. */
  async sendJson(method, path, options = {}) {
    const response = await this.send(method, path, options);
    return await response.json();
  }
  /**
   * Sends a request that may return 204 No Content. Returns `null` for the empty case
   * so the caller can branch without inspecting status codes.
   */
  async sendNullableJson(method, path, options = {}) {
    const response = await this.send(method, path, options);
    if (response.status === 204) return null;
    const text = await response.text();
    if (text.length === 0) return null;
    return JSON.parse(text);
  }
  /** Sends a request and discards the response body. */
  async sendVoid(method, path, options = {}) {
    const response = await this.send(method, path, options);
    await response.arrayBuffer().catch(() => void 0);
  }
  /**
   * Low-level send. Throws `PowerPortalsProError` (or a subclass) on non-2xx. Most
   * callers should use {@link sendJson}, {@link sendNullableJson}, or {@link sendVoid}.
   */
  async send(method, path, options = {}) {
    const url = this.baseUrl + path;
    const hasBody = options.body !== void 0;
    const headers = {
      Accept: "application/json",
      ...hasBody ? { "Content-Type": "application/json" } : {},
      ...options.headers ?? {}
    };
    const init = {
      method,
      credentials: "include",
      headers,
      ...hasBody ? { body: JSON.stringify(options.body) } : {},
      ...options.signal ? { signal: options.signal } : {}
    };
    const response = await this.fetchImpl(url, init);
    if (response.ok) return response;
    throw await this.errorFromResponse(response, url);
  }
  async errorFromResponse(response, url) {
    const contentType = response.headers.get("Content-Type") ?? "";
    if (contentType.toLowerCase().includes("application/problem+json")) {
      try {
        const problem = await response.json();
        return mapProblemDetails(problem, response.status, url);
      } catch {
      }
    }
    let detail;
    try {
      const text = await response.text();
      if (text.length > 0) detail = text;
    } catch {
    }
    return new PowerPortalsProError(
      `${response.status} ${response.statusText} on ${url}`,
      {
        status: response.status,
        url,
        title: response.statusText,
        detail,
        clrType: void 0,
        problem: void 0
      }
    );
  }
};

// src/passkeys/webauthn.ts
var PasskeyCanceledError = class extends Error {
  constructor() {
    super("No passkey was chosen.");
    this.name = "PasskeyCanceledError";
  }
};
function isPasskeySupported() {
  return typeof window !== "undefined" && typeof window.PublicKeyCredential === "function" && typeof navigator !== "undefined" && !!navigator.credentials;
}
function isPasskeyCancellation(error) {
  if (error instanceof PasskeyCanceledError) return true;
  const name = error?.name;
  return name === "NotAllowedError" || name === "AbortError";
}
async function getPasskeyCredentialJson(options, signal) {
  const credential = await navigator.credentials.get({
    publicKey: toRequestOptions(options),
    ...signal ? { signal } : {}
  });
  if (!credential) throw new PasskeyCanceledError();
  return JSON.stringify(credentialToJson(credential));
}
async function createPasskeyCredentialJson(options, signal) {
  const credential = await navigator.credentials.create({
    publicKey: toCreationOptions(options),
    ...signal ? { signal } : {}
  });
  if (!credential) throw new PasskeyCanceledError();
  return JSON.stringify(credentialToJson(credential));
}
function toRequestOptions(json) {
  const parse = globalThis.PublicKeyCredential?.parseRequestOptionsFromJSON;
  if (typeof parse === "function") return parse(json);
  const allowCredentials = json["allowCredentials"];
  return {
    ...json,
    challenge: fromBase64Url(json["challenge"]),
    ...allowCredentials ? { allowCredentials: allowCredentials.map(toDescriptor) } : {}
  };
}
function toCreationOptions(json) {
  const parse = globalThis.PublicKeyCredential?.parseCreationOptionsFromJSON;
  if (typeof parse === "function") return parse(json);
  const user = json["user"];
  const excludeCredentials = json["excludeCredentials"];
  return {
    ...json,
    challenge: fromBase64Url(json["challenge"]),
    user: { ...user, id: fromBase64Url(user["id"]) },
    ...excludeCredentials ? { excludeCredentials: excludeCredentials.map(toDescriptor) } : {}
  };
}
function toDescriptor(descriptor) {
  return { ...descriptor, id: fromBase64Url(descriptor["id"]) };
}
function credentialToJson(credential) {
  const toJson = credential.toJSON;
  if (typeof toJson === "function") return toJson.call(credential);
  const response = credential.response;
  const json = {
    id: credential.id,
    rawId: toBase64Url(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults?.() ?? {},
    ...credential.authenticatorAttachment ? { authenticatorAttachment: credential.authenticatorAttachment } : {}
  };
  if ("attestationObject" in response) {
    const attestation = response;
    json["response"] = {
      clientDataJSON: toBase64Url(attestation.clientDataJSON),
      attestationObject: toBase64Url(attestation.attestationObject),
      transports: attestation.getTransports?.() ?? []
    };
  } else {
    const assertion = response;
    json["response"] = {
      clientDataJSON: toBase64Url(assertion.clientDataJSON),
      authenticatorData: toBase64Url(assertion.authenticatorData),
      signature: toBase64Url(assertion.signature),
      userHandle: assertion.userHandle ? toBase64Url(assertion.userHandle) : null
    };
  }
  return json;
}
function toBase64Url(buffer) {
  const bytes = buffer instanceof ArrayBuffer ? new Uint8Array(buffer) : new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function fromBase64Url(value) {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

// src/auth-client.ts
function bodyAndSignal(body, signal) {
  return {
    ...body !== void 0 ? { body } : {},
    ...signal ? { signal } : {}
  };
}
var AuthClient = class {
  /**
   * The underlying transport. Exposed so advanced consumers can hit auth endpoints not
   * yet wrapped by a typed method, or share a single transport with a sibling
   * {@link PowerPortalsProClient}.
   */
  transport;
  constructor(transportOrOptions) {
    this.transport = transportOrOptions instanceof Transport ? transportOrOptions : new Transport(transportOrOptions);
  }
  // --- Login + 2FA + logout --------------------------------------------------------
  /**
   * Submits credentials. On success the auth cookie is set and the next
   * {@link getCurrentUserAsync} will report the user as signed in. The returned
   * `result` enum tells the caller whether to navigate (Success), prompt for 2FA
   * (RequiresTwoFactor), or show a generic error (others).
   */
  loginAsync(request, signal) {
    return this.transport.sendJson("POST", Routes.api.auth.login, bodyAndSignal(request, signal));
  }
  /** Submits the 2FA code after a {@link loginAsync} that returned `RequiresTwoFactor`. */
  verifyTwoFactorAsync(request, signal) {
    return this.transport.sendJson("POST", Routes.api.auth.loginTwoFactor, bodyAndSignal(request, signal));
  }
  /** Clears the auth cookie. Idempotent — calling on an already-anonymous session is a no-op. */
  logoutAsync(signal) {
    return this.transport.sendVoid("POST", Routes.api.auth.logout, bodyAndSignal(void 0, signal));
  }
  // --- Registration + email confirmation -------------------------------------------
  /**
   * Creates a new user. When the host configured `RequireConfirmedAccount` (the default),
   * the response carries `RegisterResult.ConfirmationEmailSent` and the user is NOT
   * signed in yet — the client should surface "check your email."
   */
  registerAsync(request, signal) {
    return this.transport.sendJson("POST", Routes.api.auth.register, bodyAndSignal(request, signal));
  }
  // --- Invitations ---------------------------------------------------------------------
  /** What the invitation link with this code offers the current visitor: register, sign in, accept, or nothing. */
  getInvitationAsync(request, signal) {
    return this.transport.sendJson("POST", Routes.api.auth.invitation, bodyAndSignal(request, signal));
  }
  /** Accepts an invitation as the signed-in contact. On success the sign-in is refreshed with the new roles. */
  acceptInvitationAsync(request, signal) {
    return this.transport.sendJson("POST", Routes.api.auth.invitationAccept, bodyAndSignal(request, signal));
  }
  /**
   * Creates a local login on the invited contact — with the contact's own email address — and
   * accepts the invitation. On success the user is signed in. Works whether or not the portal
   * takes registrations.
   */
  registerInvitationAsync(request, signal) {
    return this.transport.sendJson("POST", Routes.api.auth.invitationRegister, bodyAndSignal(request, signal));
  }
  /** Links the external sign-in in progress to the invited contact, and accepts. On success the user is signed in. */
  acceptInvitationWithExternalLoginAsync(request, signal) {
    return this.transport.sendJson("POST", Routes.api.auth.invitationExternal, bodyAndSignal(request, signal));
  }
  /** Confirms a registered email via the registration-link token. */
  confirmEmailAsync(request, signal) {
    return this.transport.sendJson("POST", Routes.api.auth.confirmEmail, bodyAndSignal(request, signal));
  }
  /** Sends a fresh confirmation email. Always 200 — the server doesn't reveal whether the email exists. */
  resendEmailConfirmationAsync(request, signal) {
    return this.transport.sendVoid("POST", Routes.api.auth.resendEmailConfirmation, bodyAndSignal(request, signal));
  }
  // --- Password reset --------------------------------------------------------------
  /** Requests a password-reset email. Always 200 — no enumeration. */
  requestPasswordResetAsync(request, signal) {
    return this.transport.sendVoid("POST", Routes.api.auth.forgotPassword, bodyAndSignal(request, signal));
  }
  /** Completes a password reset using the token from the reset email link. */
  resetPasswordAsync(request, signal) {
    return this.transport.sendJson("POST", Routes.api.auth.resetPassword, bodyAndSignal(request, signal));
  }
  // --- Session ---------------------------------------------------------------------
  /**
   * Snapshot of the current user. Anonymous requests get a non-null `CurrentUserResponse`
   * with `isAuthenticated: false` and the rest of the fields empty — call this on app
   * startup to decide whether to render signed-in UI vs. the login flow.
   */
  getCurrentUserAsync(signal) {
    return this.transport.sendJson("GET", Routes.api.auth.me, bodyAndSignal(void 0, signal));
  }
  /**
   * Anonymous auth options for the sign-in UI: whether the portal accepts local
   * username/password accounts (`localAccountsEnabled`) plus the configured external
   * providers — one round-trip the login page uses to decide whether to render the
   * local form / register + reset links and which provider buttons to show.
   */
  getAuthOptionsAsync(signal) {
    return this.transport.sendJson(
      "GET",
      Routes.api.auth.options,
      bodyAndSignal(void 0, signal)
    );
  }
  // --- External login flow ---------------------------------------------------------
  /**
   * Reads the in-flight external-login cookie set by an OAuth callback. Returns
   * `null` when no callback is in flight (the endpoint replies 204) — the client
   * should redirect back to login in that case.
   */
  getPendingExternalLoginAsync(signal) {
    return this.transport.sendNullableJson(
      "GET",
      Routes.api.auth.externalLoginPending,
      bodyAndSignal(void 0, signal)
    );
  }
  /**
   * Completes an in-flight external login: registers a new local account (or links
   * the external login to an existing account with the same email) and signs the
   * user in.
   */
  confirmExternalLoginAsync(request, signal) {
    return this.transport.sendJson(
      "POST",
      Routes.api.auth.externalLoginConfirm,
      bodyAndSignal(request, signal)
    );
  }
  /**
   * Completes an in-flight external login when the OAuth identity matched more than
   * one portal user (typically a `systemuser` AND a `contact`). Body carries only
   * the `ExternalLoginCandidateKind` the user picked from the chooser; the server
   * re-resolves the underlying record so a malicious caller can't supply an arbitrary id.
   */
  selectExternalLoginAsync(request, signal) {
    return this.transport.sendJson(
      "POST",
      Routes.api.auth.externalLoginSelect,
      bodyAndSignal(request, signal)
    );
  }
  /**
   * Swaps the current auth cookie for the user's alt identity (the contact↔systemuser
   * sibling). The alt id is read from the cookie's claims server-side, so the client
   * supplies no parameters.
   */
  switchIdentityAsync(signal) {
    return this.transport.sendJson(
      "POST",
      Routes.api.auth.switchIdentity,
      bodyAndSignal(void 0, signal)
    );
  }
  // --- Impersonation ---------------------------------------------------------------
  /**
   * Starts impersonating a portal contact, swapping the auth cookie for theirs. Restricted
   * server-side to internal users holding the `SystemAdmin` role; the target is resolved through
   * the contact store alone, so a systemuser id can't be supplied to assume a staff identity.
   *
   * On `ImpersonateResult.Started` the session IS that contact — `/api/auth/me` reports the
   * contact's id, roles and user type, with only `isImpersonating` / `impersonator*` betraying
   * who is behind it. Reload or re-fetch the user after this resolves; anything already rendered
   * was rendered for the previous identity.
   */
  impersonateAsync(targetUserId, signal) {
    return this.transport.sendJson(
      "POST",
      Routes.api.auth.impersonate,
      bodyAndSignal({ targetUserId }, signal)
    );
  }
  /**
   * Ends an impersonation session and signs the internal user back in. Takes no parameters — the
   * identity to return to is read from the session's own claims server-side, so a caller can't
   * choose who it becomes on the way back.
   */
  stopImpersonationAsync(signal) {
    return this.transport.sendJson(
      "POST",
      Routes.api.auth.stopImpersonation,
      bodyAndSignal(void 0, signal)
    );
  }
  /**
   * Searches portal contacts that may be impersonated. A query that parses as a GUID is an exact
   * user-id lookup; otherwise it matches full name and email columns, and queries shorter than
   * three characters return nothing.
   *
   * Returns an empty result rather than failing when the caller isn't permitted to impersonate,
   * so a picker renders "no matches" without learning which condition applied.
   */
  searchImpersonationTargetsAsync(query, signal) {
    return this.transport.sendJson(
      "POST",
      Routes.api.auth.impersonateSearch,
      bodyAndSignal({ query }, signal)
    );
  }
  // --- Profile ---------------------------------------------------------------------
  /** Reads the signed-in user's manage-profile snapshot (Identity status + linked contact fields). */
  getProfileAsync(signal) {
    return this.transport.sendJson("GET", Routes.api.auth.manage.profile, bodyAndSignal(void 0, signal));
  }
  /** Updates first/last/mobile on the signed-in user's linked Dataverse contact. */
  updateProfileAsync(request, signal) {
    return this.transport.sendVoid("POST", Routes.api.auth.manage.updateProfile, bodyAndSignal(request, signal));
  }
  // --- Password (set + change) -----------------------------------------------------
  /** Adds a local password to an account that doesn't have one (typically external-login-only). */
  setPasswordAsync(request, signal) {
    return this.transport.sendJson("POST", Routes.api.auth.manage.setPassword, bodyAndSignal(request, signal));
  }
  /**
   * Changes the local password (requires the current password). On success the auth
   * cookie is refreshed so other sessions are invalidated by the security stamp rotation.
   */
  changePasswordAsync(request, signal) {
    return this.transport.sendJson(
      "POST",
      Routes.api.auth.manage.changePassword,
      bodyAndSignal(request, signal)
    );
  }
  // --- Email (change + resend confirmation) ----------------------------------------
  /** Starts a change-email flow by sending a confirmation link to the new address. */
  changeEmailAsync(request, signal) {
    return this.transport.sendJson("POST", Routes.api.auth.manage.changeEmail, bodyAndSignal(request, signal));
  }
  /** Re-sends the confirmation link to the user's current (unconfirmed) email. */
  sendEmailConfirmationAsync(signal) {
    return this.transport.sendJson(
      "POST",
      Routes.api.auth.manage.sendEmailConfirmation,
      bodyAndSignal(void 0, signal)
    );
  }
  // --- Passkeys ---------------------------------------------------------------------
  /**
   * Signs in with a passkey: asks the server for a challenge, lets the browser offer the person
   * this site's passkeys, and sends back the one they chose. On `Success` the auth cookie is set.
   * Throws if the person closes the browser's dialog — test with `isPasskeyCancellation` and
   * show nothing for that.
   */
  async signInWithPasskeyAsync(signal) {
    const options = await this.getPasskeyRequestOptionsAsync(signal);
    const credentialJson = await getPasskeyCredentialJson(options, signal);
    return this.passkeyLoginAsync({ credentialJson }, signal);
  }
  /**
   * Makes a passkey for the signed-in user and saves it: asks the server for creation options,
   * lets the browser make the passkey, and sends it back with the name the user gave it. Throws
   * if the person closes the browser's dialog, as {@link signInWithPasskeyAsync} does.
   */
  async createPasskeyAsync(name, signal) {
    const options = await this.getPasskeyCreationOptionsAsync(signal);
    const credentialJson = await createPasskeyCredentialJson(options, signal);
    return this.addPasskeyAsync({ credentialJson, name: name ?? null }, signal);
  }
  /** The options to pass `navigator.credentials.get()` to sign in with a passkey — the first half of {@link signInWithPasskeyAsync}. */
  getPasskeyRequestOptionsAsync(signal) {
    return this.transport.sendJson("POST", Routes.api.auth.passkeyRequestOptions, bodyAndSignal(void 0, signal));
  }
  /** Signs in with the passkey the browser returned — the second half of {@link signInWithPasskeyAsync}. */
  passkeyLoginAsync(request, signal) {
    return this.transport.sendJson("POST", Routes.api.auth.passkeyLogin, bodyAndSignal(request, signal));
  }
  /** The signed-in user's passkeys, oldest first. */
  getPasskeysAsync(signal) {
    return this.transport.sendJson("GET", Routes.api.auth.manage.passkeys, bodyAndSignal(void 0, signal));
  }
  /** The options to pass `navigator.credentials.create()` to make a passkey — the first half of {@link createPasskeyAsync}. */
  getPasskeyCreationOptionsAsync(signal) {
    return this.transport.sendJson("POST", Routes.api.auth.manage.passkeyCreationOptions, bodyAndSignal(void 0, signal));
  }
  /** Saves the passkey the browser made — the second half of {@link createPasskeyAsync}. */
  addPasskeyAsync(request, signal) {
    return this.transport.sendJson("POST", Routes.api.auth.manage.addPasskey, bodyAndSignal(request, signal));
  }
  /** Renames one of the signed-in user's passkeys. */
  renamePasskeyAsync(request, signal) {
    return this.transport.sendJson("POST", Routes.api.auth.manage.renamePasskey, bodyAndSignal(request, signal));
  }
  /** Removes one of the signed-in user's passkeys. */
  removePasskeyAsync(request, signal) {
    return this.transport.sendJson("POST", Routes.api.auth.manage.removePasskey, bodyAndSignal(request, signal));
  }
  // --- 2FA management --------------------------------------------------------------
  /** Reads the user's two-factor configuration snapshot (enabled, has authenticator, recovery codes left). */
  getTwoFactorStatusAsync(signal) {
    return this.transport.sendJson(
      "GET",
      Routes.api.auth.manage.twoFactorStatus,
      bodyAndSignal(void 0, signal)
    );
  }
  /** Reads (and creates if missing) the authenticator key + QR-code URI for TOTP enrollment. */
  getAuthenticatorSetupAsync(signal) {
    return this.transport.sendJson(
      "GET",
      Routes.api.auth.manage.authenticatorSetup,
      bodyAndSignal(void 0, signal)
    );
  }
  /**
   * Verifies a code from the authenticator app to finish enabling 2FA. On success and
   * when the user has zero existing recovery codes, a fresh batch is returned in
   * `recoveryCodes`.
   */
  verifyAuthenticatorAsync(request, signal) {
    return this.transport.sendJson(
      "POST",
      Routes.api.auth.manage.verifyAuthenticator,
      bodyAndSignal(request, signal)
    );
  }
  /** Rotates the authenticator key and disables 2FA — the user must re-enroll to re-enable. */
  resetAuthenticatorAsync(signal) {
    return this.transport.sendJson(
      "POST",
      Routes.api.auth.manage.resetAuthenticator,
      bodyAndSignal(void 0, signal)
    );
  }
  /** Disables 2FA on the account. */
  disable2faAsync(signal) {
    return this.transport.sendJson("POST", Routes.api.auth.manage.disable2fa, bodyAndSignal(void 0, signal));
  }
  /** Regenerates recovery codes — invalidates the previous set. The new codes are returned once and only here. */
  generateRecoveryCodesAsync(signal) {
    return this.transport.sendJson(
      "POST",
      Routes.api.auth.manage.generateRecoveryCodes,
      bodyAndSignal(void 0, signal)
    );
  }
  /** Clears this browser's "remember this machine" 2FA cookie. */
  forget2faClientAsync(signal) {
    return this.transport.sendJson(
      "POST",
      Routes.api.auth.manage.forget2faClient,
      bodyAndSignal(void 0, signal)
    );
  }
  // --- External logins management --------------------------------------------------
  /** Lists the user's linked external logins plus available providers to add. */
  getExternalLoginsAsync(signal) {
    return this.transport.sendJson(
      "GET",
      Routes.api.auth.manage.externalLogins,
      bodyAndSignal(void 0, signal)
    );
  }
  /**
   * Reads the combined sign-in-paths snapshot (linked external logins + whether the
   * account has a local password). Used by the manage-external-logins UI to decide
   * whether unlinking a given external login would lock the user out.
   */
  getCurrentLoginInfoAsync(signal) {
    return this.transport.sendJson(
      "GET",
      Routes.api.auth.manage.loginInfo,
      bodyAndSignal(void 0, signal)
    );
  }
  /** Removes one external login from the signed-in user. */
  removeExternalLoginAsync(request, signal) {
    return this.transport.sendJson(
      "POST",
      Routes.api.auth.manage.removeExternalLogin,
      bodyAndSignal(request, signal)
    );
  }
  // --- Personal data ---------------------------------------------------------------
  /** Returns every `[PersonalData]`-marked Identity property + linked external logins. */
  getPersonalDataAsync(signal) {
    return this.transport.sendJson(
      "GET",
      Routes.api.auth.manage.personalData,
      bodyAndSignal(void 0, signal)
    );
  }
  /**
   * Permanently deletes the user's account. When the account has a local password,
   * `request.password` is required — `RequireLocalPassword` in the response means
   * the client should prompt and retry.
   */
  deletePersonalDataAsync(request, signal) {
    return this.transport.sendJson(
      "POST",
      Routes.api.auth.manage.deletePersonalData,
      bodyAndSignal(request, signal)
    );
  }
};

// src/column-permissions.ts
function getColumnPermissions(record, columnName) {
  const columnPermissions = record?.columnPermissions?.[columnName];
  return columnPermissions ?? ColumnSecurityPermission.All;
}
function canReadColumn(record, columnName) {
  return (getColumnPermissions(record, columnName) & ColumnSecurityPermission.Read) === ColumnSecurityPermission.Read;
}
function canWriteColumn(record, columnName) {
  return (getColumnPermissions(record, columnName) & ColumnSecurityPermission.Write) === ColumnSecurityPermission.Write;
}
function shareResponseColumnPermissions(response) {
  const shared = response.columnPermissions;
  if (shared) {
    for (const record of response.tableRecords) record.columnPermissions ??= shared;
  }
  return response;
}

// src/powerportalspro-client.ts
var PowerPortalsProClient = class {
  /**
   * The underlying transport. Exposed so advanced consumers can hit endpoints not yet
   * wrapped by a typed method (caches, executeMultiple, associate/disassociate, …).
   * Cookie auth, JSON in/out, and problem+json error rehydration apply automatically
   * because the transport owns those concerns.
   */
  transport;
  /**
   * Table-permission requests in flight, by table. A create form, the toolbar above it and every
   * new-record panel of a subgrid all ask the same question as they mount; they share one request
   * rather than each sending their own. Only while it is in flight: an answer is never kept, so a
   * sign-in or sign-out can never be answered with the permissions of whoever came before.
   */
  tablePermissionsInFlight = /* @__PURE__ */ new Map();
  tablesPermissionsInFlight = /* @__PURE__ */ new Map();
  operationsPermissionsInFlight = /* @__PURE__ */ new Map();
  constructor(transportOrOptions) {
    this.transport = transportOrOptions instanceof Transport ? transportOrOptions : new Transport(transportOrOptions);
  }
  // --- Records (CRUD + FetchXML) ---------------------------------------------------
  /**
   * Creates a new record. URL is derived from `record.tableName`. The server
   * marks every property as modified before applying so column values
   * arriving from JSON (which carry no dirty flags) round-trip correctly.
   */
  createRecordAsync(record, signal) {
    return this.transport.sendJson(
      "POST",
      Routes.api.tables.getCreateRoute(record.tableName),
      { body: record, ...signal ? { signal } : {} }
    );
  }
  /**
   * Reads a single record. `columns` (logical names) narrows the projection to a subset;
   * omit it for the table's full set of columns visible to the caller.
   */
  retrieveRecordAsync(tableLogicalName, recordId, columns, signal) {
    return this.transport.sendJson(
      "GET",
      Routes.api.tables.getRetrieveRoute(tableLogicalName, recordId, columns),
      signal ? { signal } : {}
    );
  }
  // --- Localization translation (admin) --------------------------------------------
  /**
   * Probes whether the localization-translation feature is usable: whether a translation
   * provider is configured, whether the managed solution is installed, and the candidate
   * target languages (the portal's cultures annotated with provider-translatability).
   * SystemAdmin-gated.
   */
  getTranslationAvailabilityAsync(signal) {
    return this.transport.sendJson(
      "GET",
      Routes.api.localizationTranslationAvailability,
      signal ? { signal } : {}
    );
  }
  /**
   * Translates an uploaded localization file into one or more target languages, reusing the
   * Dataverse translation memory. Returns one file per language (base64 `content`) plus a zip
   * of all and translated-vs-reused counts. SystemAdmin-gated.
   */
  translateLocalizationFileAsync(request, signal) {
    return this.transport.sendJson(
      "POST",
      Routes.api.localizationTranslate,
      { body: request, ...signal ? { signal } : {} }
    );
  }
  // --- Localization admin overview (admin) -----------------------------------------
  /**
   * Reads the admin localization overview: the configured warmup pipeline plus the
   * per-source load records (load order, source kind, cultures touched, key counts,
   * how many keys overrode an earlier source, duration, status) from the most recent
   * warmup. Backs the `<LocalizationAdmin />` panel. SystemAdmin-gated.
   */
  getLocalizationOverviewAsync(signal) {
    return this.transport.sendJson(
      "GET",
      Routes.api.localizationOverview,
      signal ? { signal } : {}
    );
  }
  /**
   * Downloads the JSON one source contributed to one culture during the most recent
   * warmup — a translation-handoff snapshot. Returns `null` when the source/culture
   * pair isn't tracked (the server answers 404 — cache cleared, source removed, or
   * the culture has no entries for that source). SystemAdmin-gated.
   */
  downloadLocalizationSourceAsync(sourceId, culture, signal) {
    const query = new URLSearchParams({ sourceId, culture }).toString();
    return this.downloadLocalizationFile(
      `${Routes.api.localizationSourceDownload}?${query}`,
      signal
    );
  }
  /**
   * Downloads the merged JSON of every key the cache will serve for a culture (the
   * post-merge winning value across all sources) — a complete-snapshot reference.
   * Returns `null` when the culture has no entries (404). SystemAdmin-gated.
   */
  downloadMergedLocalizationsAsync(culture, signal) {
    const query = new URLSearchParams({ culture }).toString();
    return this.downloadLocalizationFile(
      `${Routes.api.localizationMergedDownload}?${query}`,
      signal
    );
  }
  /**
   * Reads the merged localization keys for a culture as a structured list — every key the
   * cache will serve, its winning value, and which source supplied it. Same data as
   * {@link downloadMergedLocalizationsAsync} but per-key annotated with provenance, for the
   * `<LocalizationAdmin />` "view merged sources" dialog. Sorted by key. SystemAdmin-gated.
   */
  getMergedLocalizationEntriesAsync(culture, signal) {
    const query = new URLSearchParams({ culture }).toString();
    return this.transport.sendJson(
      "GET",
      `${Routes.api.localizationMergedEntries}?${query}`,
      signal ? { signal } : {}
    );
  }
  /**
   * Shared binary-download path for the two localization download endpoints. Reads
   * the raw bytes via `response.arrayBuffer()` and derives the filename from the
   * `Content-Disposition` header (matching {@link createFileArchiveAsync}). Maps a
   * 404 to `null` so callers can show a "nothing to download" message instead of
   * surfacing it as an error.
   */
  async downloadLocalizationFile(path, signal) {
    try {
      const response = await this.transport.send("GET", path, signal ? { signal } : {});
      const fileName = parseContentDispositionFileName(response.headers.get("Content-Disposition")) ?? "localization.json";
      const buffer = await response.arrayBuffer();
      return { fileName, data: new Uint8Array(buffer) };
    } catch (error) {
      if (error instanceof PowerPortalsProError && error.status === 404) return null;
      throw error;
    }
  }
  // --- Cache administration (admin) ------------------------------------------------
  /**
   * Lists every registered server-side cache by name — the values that
   * {@link clearCacheAsync} accepts. Backs the per-cache buttons on an admin
   * "clear caches" page; render them sorted client-side for a stable order.
   * SystemAdmin-gated.
   */
  getCacheNamesAsync(signal) {
    return this.transport.sendJson(
      "GET",
      Routes.api.cacheNames,
      signal ? { signal } : {}
    );
  }
  /**
   * Clears every registered server-side cache and returns one
   * {@link CacheClearResult} per cache (name, success flag, error, elapsed ms).
   * Per-cache failures are reported in the result rows rather than thrown, so a
   * single misbehaving cache doesn't abort the rest — inspect `succeeded` on each
   * row. SystemAdmin-gated.
   */
  clearAllCachesAsync(signal) {
    return this.transport.sendJson(
      "POST",
      Routes.api.clearAllCaches,
      signal ? { signal } : {}
    );
  }
  /**
   * Clears a single named cache (case-insensitive match against
   * {@link getCacheNamesAsync}). Returns the {@link CacheClearResult}, or `null`
   * when no cache by that name is registered (the server answers 404). A clear
   * that runs but throws internally comes back as a result row with
   * `succeeded: false` — that's distinct from the not-found `null`.
   * SystemAdmin-gated.
   */
  async clearCacheAsync(name, signal) {
    try {
      return await this.transport.sendJson(
        "POST",
        Routes.api.getClearCacheRoute(name),
        signal ? { signal } : {}
      );
    } catch (error) {
      if (error instanceof PowerPortalsProError && error.status === 404) return null;
      throw error;
    }
  }
  // --- Table permission administration (admin) ---------------------------------------
  /**
   * The ids of the Power Pages web roles a table permission applies to, under
   * enhanced authorization. Read from the permission's own record, which is where
   * Power Pages keeps them — the `mspp_entitypermission` virtual table's
   * relationship to `mspp_webrole` reads empty even when roles are linked.
   * Returns `null` when no table permission of the portal's site has that id (the
   * server answers 404). SystemAdmin-gated.
   */
  async getTablePermissionWebRolesAsync(tablePermissionId, signal) {
    try {
      return await this.transport.sendJson(
        "GET",
        Routes.api.getTablePermissionWebRolesRoute(tablePermissionId),
        signal ? { signal } : {}
      );
    } catch (error) {
      if (error instanceof PowerPortalsProError && error.status === 404) return null;
      throw error;
    }
  }
  /**
   * Replaces the Power Pages web roles a table permission applies to, under
   * enhanced authorization. Written to the permission's own record, which is what
   * makes Power Pages re-sync the security roles paired with those web roles — so
   * the change reaches Dataverse privileges, typically within a minute. Every id
   * being added must be a web role of the portal's site. Returns `false` when no table
   * permission of the site has that id (the server answers 404). SystemAdmin-gated.
   */
  async setTablePermissionWebRolesAsync(tablePermissionId, webRoleIds, signal) {
    try {
      await this.transport.sendVoid(
        "PUT",
        Routes.api.getTablePermissionWebRolesRoute(tablePermissionId),
        { body: webRoleIds, ...signal ? { signal } : {} }
      );
      return true;
    } catch (error) {
      if (error instanceof PowerPortalsProError && error.status === 404) return false;
      throw error;
    }
  }
  /**
   * Runs a FetchXML query and returns the matching records plus paging info.
   * The query is the FetchXML XML literal — the same format Dataverse natively accepts.
   */
  async retrieveRecordsAsync(fetchXml, signal) {
    return shareResponseColumnPermissions(
      await this.transport.sendJson(
        "GET",
        Routes.api.getRetrieveMultipleRoute(fetchXml),
        signal ? { signal } : {}
      )
    );
  }
  /**
   * Loads grid data via the server-side <c>IQueryService</c>. The request
   * picks a base query — a stored Dataverse view (<c>viewId</c>) or a
   * caller-supplied FetchXML (<c>fetchXml</c>) — and the server applies
   * the framework's <c>*</c>/<c>%</c> wildcard convention, AND-merges
   * the search filter into the source's existing filter, dispatches to
   * per-column-type search semantics (string <c>like</c>, choice label
   * match → integer <c>in</c>, lookup link-entity + primary-name match,
   * etc.), and projects each column to a {@link ResolvedColumn} so the
   * client can render headers + dispatch cells without a separate
   * metadata round-trip.
   *
   * At least one of <c>request.viewId</c> or <c>request.fetchXml</c> must
   * be supplied (neither returns a 400). Both may be supplied together — the
   * <c>fetchXml</c> is the query and the <c>viewId</c> names the view for
   * localized column-title resolution.
   *
   * Mirrors how the is consumed
   * `@powerportalspro/react`'s `useQueryData` hook calls this method
   * internally, so most consumers don't invoke it directly.
   */
  async loadQueryAsync(request, signal) {
    return shareResponseColumnPermissions(
      await this.transport.sendJson(
        "POST",
        Routes.api.query,
        { body: request, ...signal ? { signal } : {} }
      )
    );
  }
  /**
   * The records a lookup offers for what the user typed, by name. POSTs to
   * `/api/lookups/search`: the server searches the view's columns as a grid's search box does
   * and secures the records as a grid's, without a grid's count, per-row permissions, groups or
   * figures. With no `viewId`, the table's lookup view; with none, the records' id and name.
   */
  async searchLookupAsync(request, signal) {
    return shareResponseColumnPermissions(
      await this.transport.sendJson(
        "POST",
        Routes.api.lookupSearch,
        { body: request, ...signal ? { signal } : {} }
      )
    );
  }
  /**
   * Exports every record a grid's query matches — not just one page — as a file in
   * `request.format` (Excel today). The server pages through the records itself, up to its
   * configured maximum, with the same table, row and column security as
   * {@link loadQueryAsync}. `request.columns` picks and orders the columns written without
   * changing which records the query selects.
   */
  async exportGridAsync(request, signal) {
    const response = await this.transport.send(
      "POST",
      Routes.api.gridExport,
      { body: request, ...signal ? { signal } : {} }
    );
    const contentType = response.headers.get("Content-Type")?.split(";")[0]?.trim() ?? "application/octet-stream";
    const fileName = parseContentDispositionFileName(response.headers.get("Content-Disposition")) ?? `Export${contentType === XLSX_CONTENT_TYPE ? ".xlsx" : ""}`;
    const recordCount = Number.parseInt(response.headers.get("X-PPP-Export-Record-Count") ?? "", 10);
    const buffer = await response.arrayBuffer();
    return {
      fileName,
      contentType,
      data: new Uint8Array(buffer),
      recordCount: Number.isFinite(recordCount) ? recordCount : 0,
      isTruncated: response.headers.get("X-PPP-Export-Truncated")?.toLowerCase() === "true"
    };
  }
  /**
   * Updates an existing record. URL is derived from `record.tableName` and `record.id`
   * `record.id` must be set. For a new record, use {@link createRecordAsync} instead.
   */
  updateRecordAsync(record, signal) {
    if (!record.id) {
      throw new Error(
        "PowerPortalsProClient.updateRecordAsync requires record.id. Use createRecordAsync for new records."
      );
    }
    return this.transport.sendJson(
      "PATCH",
      Routes.api.tables.getUpdateRoute(record.tableName, record.id),
      { body: record, ...signal ? { signal } : {} }
    );
  }
  /**
   * Executes a batch of {@link OrganizationRequest}s as a single round-trip.
   * The MainContext save flow uses this to ship every dirty descendant's
   * requests in one shot so a multi-record save lands transactionally
   * server-side.
   *
   * When `returnResponses` is `true` (default), the response array carries
   * one `OrganizationResponse` per request in submission order — useful for
   * `CreateRequest` cases that need the newly-allocated record id. Pass
   * `false` for fire-and-forget batches to skip the response payload.
   */
  executeMultipleAsync(requests, options) {
    const returnResponses = options?.returnResponses ?? true;
    return this.transport.sendJson(
      "POST",
      Routes.api.getExecuteMultipleRoute(returnResponses),
      {
        body: requests,
        ...options?.signal ? { signal: options.signal } : {}
      }
    );
  }
  /** Deletes the record with the given id from the given table. */
  deleteRecordAsync(tableLogicalName, recordId, signal) {
    return this.transport.sendJson(
      "DELETE",
      Routes.api.tables.getDeleteRoute(tableLogicalName, recordId),
      signal ? { signal } : {}
    );
  }
  // --- Charts ----------------------------------------------------------------------
  /**
   * Loads chart data via the server-side <c>IChartService</c>. The
   * request body picks one of three input modes — typed aggregate
   * config, saved-view id, or raw FetchXML — plus the label/value
   * columns to shape the response. Server builds the FetchXML, runs the
   * query, performs the multi-series pivot, and applies combined
   * date-label formatting using the request culture.
   *
   * Mirrors how the is consumed
   * `@powerportalspro/react-charts`'s `DataverseChartDataSource` family
   * calls this method internally, so most consumers don't invoke it
   * directly.
   */
  loadChartAsync(request, signal) {
    return this.transport.sendJson(
      "POST",
      Routes.api.chartData,
      { body: request, ...signal ? { signal } : {} }
    );
  }
  /**
   * A map provider's settings from the portal's configuration, via the server-side
   * <c>IMapService</c>: Esri's or Google's key, a style or tile URL, or for Azure Maps an access token
   * issued to the portal's identity, so no page holds a key. `provider` is the name the map knows it
   * by, such as `google`; without one, the portal's own provider's. `@powerportalspro/react-maps`'
   * `Map` calls this itself, and again before an Azure Maps token expires.
   */
  getMapSettingsAsync(provider, signal) {
    const route = provider ? `${Routes.api.mapSettings}?${new URLSearchParams({ provider })}` : Routes.api.mapSettings;
    return this.transport.sendJson("GET", route, signal ? { signal } : {});
  }
  /**
   * An embed token for the Power BI report registered under `request.reportKey`, for the current
   * viewer, via the server-side <c>IPowerBIService</c>. The server decides from the report's
   * access rows whether the viewer may see it, and answers 403 alike for a report that isn't
   * registered and one that isn't theirs. `@powerportalspro/react-powerbi`'s `PowerBIReport` calls
   * this itself, and again before each token expires.
   */
  getPowerBIEmbedTokenAsync(request, signal) {
    return this.transport.sendJson(
      "POST",
      Routes.api.powerBIEmbedToken,
      { body: request, ...signal ? { signal } : {} }
    );
  }
  /**
   * Where to send the browser to connect the viewer's work account to Power BI, for an item shown
   * with their own Power BI access — the answer to a `PowerBIConnectionRequiredError`. Microsoft
   * Entra's sign-in comes in between, and the viewer is returned to `returnUrl`, a path on this site.
   */
  powerBIConnectUrl(returnUrl) {
    return `${this.transport.url(Routes.api.powerBIConnect)}?returnUrl=${encodeURIComponent(returnUrl)}`;
  }
  // --- Several tables at once ------------------------------------------------------
  /**
   * A page of rows of several tables, merged newest first — or oldest first — by each table's start
   * date: a view's tables, or every table the server registered, narrowed to a record and a search.
   * Pass the response's `next` back as `cursor` for the next page. A table that can't be read is
   * named in `failures`, and the rest still come. The twin of the .NET
   * `IMultiTableQueryService.LoadFeedAsync`.
   */
  loadMultiTableFeedAsync(request, signal) {
    return this.transport.sendJson("POST", Routes.api.multiTableFeed, {
      body: request,
      ...signal ? { signal } : {}
    });
  }
  /**
   * Every row of several tables between two moments, by start, as a calendar shows them. The twin of
   * the .NET `IMultiTableQueryService.LoadRangeAsync`.
   */
  loadMultiTableRangeAsync(request, signal) {
    return this.transport.sendJson("POST", Routes.api.multiTableRange, {
      body: request,
      ...signal ? { signal } : {}
    });
  }
  /**
   * A page of each of several tables' rows, in order of their titles and not merged, for a search of
   * several tables: every table registered for search, or a view's. Merge the pages with
   * `MultiTablePageMerger` and send the `reads` it names for more. The twin of the .NET
   * `IMultiTableQueryService.LoadPagesAsync`.
   */
  loadMultiTablePagesAsync(request, signal) {
    return this.transport.sendJson("POST", Routes.api.multiTablePages, {
      body: request,
      ...signal ? { signal } : {}
    });
  }
  /**
   * Each table's rows with coordinates, for a map — every table registered for maps, or a view's —
   * with where each is and what its popup shows, up to the server's limits. The twin of the .NET
   * `IMultiTableQueryService.LoadPlacesAsync`.
   */
  loadMultiTablePlacesAsync(request, signal) {
    return this.transport.sendJson("POST", Routes.api.multiTablePlaces, {
      body: request,
      ...signal ? { signal } : {}
    });
  }
  /**
   * The registered tables the user may read, how each is shown, and the multi-table views the server
   * registered. The twin of the .NET `IMultiTableQueryService.GetTablesAsync`.
   */
  getMultiTableTablesAsync(signal) {
    return this.transport.sendJson("GET", Routes.api.multiTableTables, {
      ...signal ? { signal } : {}
    });
  }
  // --- Record pages ----------------------------------------------------------------
  /**
   * The record pages registered with `AddRecordPage` of the tables the user may read — where a
   * control opens a record, or makes a new one, when it isn't given a page of its own. Asked afresh
   * every call; `RecordPagesCache` keeps the answer for the signed-in user. The twin of the .NET
   * `IRecordPageService.GetRecordPagesAsync`.
   */
  getRecordPagesAsync(signal) {
    return this.transport.sendJson("GET", Routes.api.recordPages, signal ? { signal } : {});
  }
  /**
   * The time zones a user can choose, as Dataverse lists them — each with Dataverse's code (what a
   * contact's `adx_timezone` holds) and its name in the user's language — in the order of their
   * offsets from UTC.
   */
  getTimeZonesAsync(signal) {
    return this.transport.sendJson("GET", Routes.api.timeZones, signal ? { signal } : {});
  }
  // --- Metadata --------------------------------------------------------------------
  /** Reads the metadata describing the columns and relationships of a single Dataverse table. */
  retrieveTableMetadataAsync(tableLogicalName, signal) {
    return this.transport.sendJson(
      "GET",
      Routes.api.getRetrieveTableMetadataRoute(tableLogicalName),
      signal ? { signal } : {}
    );
  }
  /**
   * Lists every table in the environment with just its identity — logical name,
   * schema name, display name and a few flags.
   *
   * The cheap counterpart to {@link retrieveTableMetadataAsync}, for populating a
   * table picker. Retrieving full metadata for every table to fill a dropdown
   * costs tens of seconds; this is one request, served from an in-process cache
   * on the server after the first call.
   *
   * Returns every table, including ones a person would never query directly.
   * Filter on `isValidForAdvancedFind` for the list Dataverse itself considers
   * meaningful.
   */
  retrieveTableHeadersAsync(signal) {
    return this.transport.sendJson(
      "GET",
      Routes.api.retrieveTableHeaders,
      signal ? { signal } : {}
    );
  }
  /**
   * Reads several tables at once, each narrowed to the columns and relationships
   * its request names.
   *
   * For wanting a little from each of many tables. Resolving the display names
   * behind a table's relationships is the case this was built for: those lookup
   * columns live on the tables at the *other* end, so doing it through
   * {@link retrieveTableMetadataAsync} is a round trip per related table, each one
   * returning a whole table to read one label from.
   *
   * Narrowing changes which columns come back, not what each carries — a named
   * column arrives as fully described as it would from a whole-table read. Columns
   * the signed-in user may not read are withheld here exactly as they are there.
   *
   * A table the environment doesn't have is simply absent from the result, so match
   * the results back by `tableName` rather than by position.
   */
  retrieveTableMetadataBatchAsync(requests, signal) {
    return this.transport.sendJson(
      "POST",
      Routes.api.retrieveTableMetadataBatch,
      { body: requests, ...signal ? { signal } : {} }
    );
  }
  /**
   * Returns the current user's combined table-level `TableSecurityPermission`
   * bitmask for `tableLogicalName` — the bitwise union of Read / Create /
   * Write / Delete / Append / AppendTo flags any registered
   * `ITablePermissionProvider` allows for that user on that table. Routed
   * server-side through the cached `ITablePermissionCache` lookup.
   *
   * Wrapped by the `useTablePermissions(tableName)` React hook with
   * in-flight-promise dedup; reach for the raw client only when you're
   * outside React (e.g. a stand-alone script) or need an imperative
   * one-off check.
   *
   * Returns `0` (`TableSecurityPermission.None`) when the user has no
   * permissions on the table; consumers compare via bit math (e.g.
   * `(permissions & TableSecurityPermission.Create) === TableSecurityPermission.Create`).
   */
  async retrieveTablePermissionsAsync(tableLogicalName, signal) {
    const permissions = await this.retrieveUserTablePermissionsAsync(tableLogicalName, signal);
    return permissions.operations;
  }
  /**
   * The current user's operations on several tables in one request, by table logical name and
   * sorted by it — the mask {@link retrieveTablePermissionsAsync} gives for each. With names, each
   * is listed, `0` (`TableSecurityPermission.None`) where the user has nothing; with none, every
   * table the user has any permission on, and only those. What a lookup that can point at many
   * tables asks before offering them. Callers asking about the same tables at once share one
   * request, as the lookups of a form do.
   */
  retrieveTablesPermissionsAsync(tableLogicalNames, signal) {
    const key = (tableLogicalNames ?? []).join(",");
    let request = this.tablesPermissionsInFlight.get(key);
    if (!request) {
      request = this.transport.sendJson("GET", Routes.api.getRetrieveTablesPermissionsRoute(tableLogicalNames), {}).finally(() => this.tablesPermissionsInFlight.delete(key));
      this.tablesPermissionsInFlight.set(key, request);
    }
    return signal ? untilAborted(request, signal) : request;
  }
  /**
   * The operations the current user may run — registered on the server with `AddOperationAccess`,
   * `AddOperationHandler` or a portal role's `WithOperationAccess` / `WithOperationHandler` — each
   * with its grants: the parameters a request for it may carry, and the records they must point to
   * (judged with `operationGrantsAllow`). With names, each is listed, with no grants where the user
   * may not run it (or it isn't registered); with none, every operation the user may run. What an
   * `<OperationButton>` asks, through `useOperationAccess`, which keeps the answer. Callers asking
   * about the same operations at once share one request. The server judges every request again
   * when it is sent: this shapes what is offered and secures nothing.
   */
  retrieveOperationAccessAsync(operations, signal) {
    const key = (operations ?? []).join("");
    let request = this.operationsPermissionsInFlight.get(key);
    if (!request) {
      request = this.transport.sendJson("GET", Routes.api.getRetrieveOperationsPermissionsRoute(operations), {}).finally(() => this.operationsPermissionsInFlight.delete(key));
      this.operationsPermissionsInFlight.set(key, request);
    }
    return signal ? untilAborted(request, signal) : request;
  }
  /**
   * The current user's operations on a table together with `newRecordColumnPermissions`: the
   * column permissions a record they create starts with, in the shape every record the server
   * returns carries as `columnPermissions`. A record that exists is stamped with what an update may
   * change; one being created has no stamp, so a form in create mode puts this on it and its
   * editors render the columns the user may not set as read-only. `<RecordContext>` does that
   * itself; reach for this directly only when building a new record by hand.
   */
  retrieveUserTablePermissionsAsync(tableLogicalName, signal) {
    let request = this.tablePermissionsInFlight.get(tableLogicalName);
    if (!request) {
      request = this.transport.sendJson("GET", Routes.api.getRetrieveTablePermissionsRoute(tableLogicalName), {}).finally(() => this.tablePermissionsInFlight.delete(tableLogicalName));
      this.tablePermissionsInFlight.set(tableLogicalName, request);
    }
    return signal ? untilAborted(request, signal) : request;
  }
  /** Reads metadata for a single view by its Dataverse view id (a GUID). */
  retrieveViewMetadataAsync(viewId, signal) {
    return this.transport.sendJson(
      "GET",
      Routes.api.getRetrieveViewMetadataRoute(viewId),
      signal ? { signal } : {}
    );
  }
  /**
   * Lists every view metadata record for a table — used by view-pickers and default-view
   * resolution. Server-side caching means this is a single Dataverse hit per table per
   * server lifetime.
   */
  retrieveViewsForTableAsync(tableLogicalName, signal) {
    return this.transport.sendJson(
      "GET",
      Routes.api.getRetrieveViewsForTableRoute(tableLogicalName),
      signal ? { signal } : {}
    );
  }
  // --- Localization ----------------------------------------------------------------
  /**
   * Retrieves the version manifest — just the supported-locale codes.
   * Clients fetch this on app startup, resolve which locale to use, then
   * call {@link retrieveLocalizationThumbprintsAsync} for the per-resource
   * thumbprints at that locale. Cached `no-cache` server-side so a new
   * release is detected on next page load.
   */
  retrieveLocalizationBundleManifestAsync(signal) {
    return this.transport.sendJson(
      "GET",
      Routes.api.getLocalizationBundleManifestRoute(),
      signal ? { signal } : {}
    );
  }
  /**
   * Retrieves the per-resource thumbprints for a single locale — the
   * default-bundle thumbprint plus thumbprints for every loaded table / view.
   * Returned from a separate endpoint (vs being baked into the version
   * manifest) so the manifest stays small even at large environment scale.
   * Cached `no-cache` server-side; the per-resource immutable URLs do the
   * heavy caching once these are read.
   */
  retrieveLocalizationThumbprintsAsync(locale, signal) {
    return this.transport.sendJson(
      "GET",
      Routes.api.getLocalizationThumbprintsRoute(locale),
      signal ? { signal } : {}
    );
  }
  /**
   * Resolved URL for the default localization-bundle file at the given
   * locale + thumbprint. Convenience wrapper around the {@link Routes}
   * helper.
   */
  getLocalizationBundleUrl(locale, thumbprint) {
    return Routes.api.getLocalizationBundleRoute(locale, thumbprint);
  }
  /**
   * Fetches the default localization-bundle file (everything outside
   * `tables.*` / `choices.*`) for the given locale + thumbprint. Returns
   * the nested-object shape the source JSON files use
   * (`{ app: { buttons: { … } } }`).
   *
   * Per-table and per-view strings come from
   * {@link retrieveLocalizationTableBundleAsync} and
   * {@link retrieveLocalizationViewBundleAsync} — fetched in parallel by the
   * runtime when source-generator-emitted `tables.{name}` / `views.{viewId}`
   * tokens are passed to the localizer's prefix-load hook.
   *
   * The thumbprint is purely cosmetic (server doesn't validate it). Pass
   * the thumbprint from the manifest so the URL is unique per release and
   * the browser's HTTP cache serves subsequent loads from disk.
   */
  retrieveLocalizationBundleAsync(locale, thumbprint, signal) {
    return this.transport.sendJson(
      "GET",
      Routes.api.getLocalizationBundleRoute(locale, thumbprint),
      signal ? { signal } : {}
    );
  }
  /**
   * Fetches a per-table localization bundle. Returns every `tables.{name}.*`
   * string for the table plus the global `choices.*` strings any of its
   * columns reference. Owning-side resolution: the server reads the table's
   * column metadata to find which global option sets to include.
   *
   * Pass the thumbprint from the matching locale entry of the manifest so
   * the URL is unique per release; the browser caches the response under
   * `Cache-Control: immutable, max-age=31536000`.
   */
  retrieveLocalizationTableBundleAsync(tableName, locale, thumbprint, signal) {
    return this.transport.sendJson(
      "GET",
      Routes.api.getLocalizationTableBundleRoute(tableName, locale, thumbprint),
      signal ? { signal } : {}
    );
  }
  /**
   * Fetches a per-view localization bundle. Returns every
   * `tables.{owningTable}.views.{viewId}.*` string. The owning table is
   * resolved server-side from the view id, so the client only needs to pass
   * the id (lowercased GUID, no braces).
   *
   * Pass the thumbprint from the matching locale entry of the manifest so
   * the URL is unique per release; the browser caches the response under
   * `Cache-Control: immutable, max-age=31536000`.
   */
  retrieveLocalizationViewBundleAsync(viewId, locale, thumbprint, signal) {
    return this.transport.sendJson(
      "GET",
      Routes.api.getLocalizationViewBundleRoute(viewId, locale, thumbprint),
      signal ? { signal } : {}
    );
  }
  /**
   * Retrieves the FULL localized-strings catalog for a culture. The server
   * already merges Dataverse-metadata-derived strings (table / column /
   * choice-option labels) with the framework JSON files and any consumer
   * override files (last-wins). Use this to seed the React-side localizer
   * with one round-trip.
   *
   * Returned shape is a nested object that mirrors the source JSON files
   * `{ app: { buttons: { save: { label: "Save" } } }, tables: { contact: ... } }`.
   * The client typically flattens this to dotted-key form for `t()` lookup
   * (see {@link flattenStrings} in `@powerportalspro/react`).
   *
   * @param culture culture code matching what server-side ASP.NET request
   * localization recognizes — e.g. `"en"`, `"es"`, `"fr-CA"`. Falls back
   * to the default culture server-side when the value isn't supported.
   */
  retrieveLocalizedStringsAsync(culture, signal) {
    return this.transport.sendJson(
      "GET",
      Routes.api.getRetrieveLocalizedStringsRoute(culture),
      signal ? { signal } : {}
    );
  }
  // --- Files -----------------------------------------------------------------------
  /**
   * Reads file metadata for one record/column pair, optionally including the binary
   * payload. When `includeData` is false (the default), the response carries name,
   * size, and content type but not the bytes — useful for rendering a download link
   * that fetches lazily on click.
   */
  getFileInfoAsync(tableLogicalName, recordId, columnName, includeData = false, signal) {
    return this.transport.sendJson(
      "GET",
      Routes.api.getRetrieveFileInfoRoute(tableLogicalName, recordId, columnName, includeData),
      signal ? { signal } : {}
    );
  }
  /**
   * Batched read for many records of the same table/column — feeds the FileGrid's
   * "Download All" / "Download Selected" flows in one round-trip. Permission checks
   * still apply per-record server-side, so dropped rows (deleted, unauthorized,
   * file-missing) just don't appear in the response.
   */
  getFileInfosAsync(tableLogicalName, recordIds, columnName, includeData = false, signal) {
    return this.transport.sendJson(
      "POST",
      Routes.api.getRetrieveFilesBatchRoute(tableLogicalName, columnName, includeData),
      { body: recordIds, ...signal ? { signal } : {} }
    );
  }
  /**
   * Builds an archive containing the file payload of every supplied record id for the
   * named table/column. Replaces the older client-side "fetch each file + zip in the
   * browser" path with a single server-side step — no base64 round-trip on the wire.
   *
   * Two response shapes, selected by `request.responseFormat`:
   *
   * - **BinaryStream** (default): the server returns the raw archive bytes; this
   * method reads them via `response.arrayBuffer()` and wraps the result in a
   * {@link FileArchiveResult}, deriving `fileName` from the `Content-Disposition`
   * header and `contentType` from `Content-Type`. Most consumers want this path.
   * - **Json**: the server returns a {@link FileArchiveJsonResponse} envelope with
   * the archive bytes base64-encoded. Useful for test harnesses, service workers,
   * or callers that need to inspect filename/size before triggering a download.
   *
   * Either way, the returned `data` is empty (`Uint8Array(0)`) when none of the
   * supplied records produced a usable file — the caller can surface a "nothing to
   * download" message instead of streaming an empty archive.
   */
  async createFileArchiveAsync(request, signal) {
    if (request.responseFormat === 1) {
      const json = await this.transport.sendJson(
        "POST",
        Routes.api.createFileArchive,
        { body: request, ...signal ? { signal } : {} }
      );
      return {
        fileName: json.fileName,
        contentType: json.contentType,
        data: base64ToUint8Array(json.data)
      };
    }
    const response = await this.transport.send(
      "POST",
      Routes.api.createFileArchive,
      { body: request, ...signal ? { signal } : {} }
    );
    const fileName = parseContentDispositionFileName(response.headers.get("Content-Disposition")) ?? `${request.tableName}-files.zip`;
    const contentType = response.headers.get("Content-Type")?.split(";")[0]?.trim() ?? "application/octet-stream";
    const buffer = await response.arrayBuffer();
    return { fileName, contentType, data: new Uint8Array(buffer) };
  }
  /**
   * Reads the environment-wide file upload settings (blocked extension list, max
   * upload size in KB). UI consumers use this to validate uploads client-side before
   * the round-trip, mirroring what the server enforces.
   */
  /**
   * Reads the org-wide settings sourced from the Dataverse `organization`
   * record: default currency plus file-upload constraints
   * (`blockedFileExtensions`, `maxUploadFileSizeInBytes`). Replaces the legacy
   * `getEnvironmentFileSettingsAsync` — file fields now live on this combined
   * response.
   * <para>
   * Primary consumers:
   * </para>
   * - Create-mode `<MoneyEdit>` reads `defaultCurrency` to render the right
   * symbol on brand-new records before save.
   * - File editors / FileGrid read the two file fields to reject invalid
   * uploads client-side before the round-trip.
   */
  getOrganizationSettingsAsync(signal) {
    return this.transport.sendJson(
      "GET",
      Routes.api.organizationSettings,
      signal ? { signal } : {}
    );
  }
};
var XLSX_CONTENT_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
function base64ToUint8Array(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
function parseContentDispositionFileName(header) {
  if (!header) return null;
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1]);
    } catch {
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1] ?? null;
}
function untilAborted(promise, signal) {
  if (signal.aborted) return Promise.reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
    signal.addEventListener("abort", onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener("abort", onAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener("abort", onAbort);
        reject(error);
      }
    );
  });
}

// src/browser-time-zone.ts
var BROWSER_TIME_ZONE_COOKIE = "ppp-time-zone";
function writeBrowserTimeZoneCookie(document2) {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!timeZone) return;
    const secure = document2.location?.protocol === "https:" ? ";secure" : "";
    document2.cookie = `${BROWSER_TIME_ZONE_COOKIE}=${encodeURIComponent(timeZone)};path=/;max-age=31536000;samesite=lax${secure}`;
  } catch {
  }
}

// src/invitation-links.ts
var INVITATION_EXTERNAL_SIGN_IN_PARAMETER = "pppInvitationExternal";
function withInvitationExternalSignIn(invitationPageUrl) {
  return `${invitationPageUrl}${invitationPageUrl.includes("?") ? "&" : "?"}${INVITATION_EXTERNAL_SIGN_IN_PARAMETER}=1`;
}
function isInvitationExternalSignInReturn(returnUrl) {
  if (!returnUrl) return false;
  const index = returnUrl.indexOf("?");
  if (index < 0) return false;
  return returnUrl.slice(index + 1).split("&").some((pair) => safeDecode(pair.split("=")[0] ?? "").toLowerCase() === INVITATION_EXTERNAL_SIGN_IN_PARAMETER.toLowerCase());
}
function safeDecode(text) {
  try {
    return decodeURIComponent(text);
  } catch {
    return text;
  }
}

// src/record-pages.ts
var RECORD_PAGE_ID_PLACEHOLDER = "{id}";
var RECORD_PAGES_LIFETIME_MS = 2 * 60 * 1e3;
var RECORD_PAGES_RETRY_MS = 15 * 1e3;
function findRecordPage(pages, table) {
  if (!table) return void 0;
  const wanted = table.toLowerCase();
  return pages?.pages?.find((page) => page.table?.toLowerCase() === wanted);
}
function recordPageUrl(page, id) {
  return rootRecordPageUrl(page.url.split(RECORD_PAGE_ID_PLACEHOLDER).join(id.replace(/[{}]/g, "")));
}
function recordPageNewUrl(page) {
  return page?.newUrl ? rootRecordPageUrl(page.newUrl) : void 0;
}
function rootRecordPageUrl(url) {
  if (!url || url.startsWith("/") || /^[a-z][a-z\d+.-]*:/i.test(url)) return url;
  return `${appBasePath()}${url}`;
}
function appBasePath() {
  const href = typeof document === "undefined" ? null : document.querySelector("base[href]")?.getAttribute("href");
  if (!href) return "/";
  try {
    const path = new URL(href, document.baseURI).pathname;
    return path.endsWith("/") ? path : path.slice(0, path.lastIndexOf("/") + 1);
  } catch {
    return "/";
  }
}
var RecordPagesCache = class {
  constructor(ppp, options = {}) {
    this.ppp = ppp;
    this.lifetimeMs = options.lifetimeMs ?? RECORD_PAGES_LIFETIME_MS;
    this.retryMs = options.retryMs ?? RECORD_PAGES_RETRY_MS;
    this.now = options.now ?? Date.now;
  }
  ppp;
  lifetimeMs;
  /** How long a failed request is kept before the server is asked again, in milliseconds. */
  retryMs;
  now;
  entry = null;
  /**
   * The record pages for `user` — whoever is signed in, by any key that tells users apart; `null`
   * for an anonymous visitor. Never rejects for the server's sake: a failure answers no pages.
   * `signal` ends only this caller's wait, never the request others share.
   */
  async getAsync(user, signal) {
    const who = user ?? null;
    let entry = this.entry;
    if (!entry || entry.user !== who || this.now() - entry.askedAt >= (entry.failed ? this.retryMs : this.lifetimeMs)) {
      entry = { user: who, request: this.ppp.getRecordPagesAsync(), askedAt: this.now() };
      this.entry = entry;
    }
    try {
      return await (signal ? untilAborted2(entry.request, signal) : entry.request);
    } catch (error) {
      if (signal?.aborted) throw error;
      entry.failed = true;
      return { pages: [] };
    }
  }
  /**
   * Whether the answer kept for `user` is a failure's: no pages for now, and the server asked again
   * by the first {@link getAsync} once {@link retryMs} have passed.
   */
  hasFailed(user) {
    return !!this.entry && this.entry.user === (user ?? null) && !!this.entry.failed;
  }
  /** Forgets the answer kept, so the next {@link getAsync} asks the server. */
  clear() {
    this.entry = null;
  }
};
function untilAborted2(promise, signal) {
  if (signal.aborted) return Promise.reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
    signal.addEventListener("abort", onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener("abort", onAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener("abort", onAbort);
        reject(error);
      }
    );
  });
}

// src/column-values.ts
var EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
var DateTimeColumnType = 2;
var FileColumnType = 41;
var ImageColumnType = 42;
function readColumnRawValue(record, columnName, metadata) {
  const properties = record?.properties;
  if (!properties || !canReadColumn(record, columnName)) return null;
  if (metadata?.$type === FileColumnType || metadata?.$type === ImageColumnType) {
    return readFileColumn(properties, columnName, metadata.$type === ImageColumnType);
  }
  const value = unwrapColumnValue(properties[columnName]);
  if (value instanceof Date && isDateOnly(metadata)) {
    return new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
  }
  return value;
}
function isDateOnly(metadata) {
  return metadata?.$type === DateTimeColumnType && metadata.dateTimeBehavior === DateTimeBehavior.DateOnly;
}
function hasColumn(record, columnName, metadata) {
  if (record && !canReadColumn(record, columnName) && record.formattedValues?.[columnName] !== void 0) {
    return true;
  }
  const properties = record?.properties;
  if (!properties) return false;
  if (columnName in properties) return true;
  return metadata?.$type === ImageColumnType && `${columnName}id` in properties;
}
function unwrapColumnValue(property) {
  if (property == null) return null;
  const value = property.value;
  const type = "$type" in property ? property.$type : void 0;
  switch (type) {
    case "RelatedRecordValue":
      return unwrapColumnValue(value);
    case 0:
      return typeof value === "boolean" ? value : null;
    case 2: {
      if (value == null || value === "") return null;
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    case 3:
    case 4:
    case 5:
    case 8:
    case 11:
      return toNumber(value);
    case 18:
      return value == null || value === "" ? null : String(value);
    case 6: {
      const lookup = property;
      if (!lookup.value) return null;
      return {
        id: lookup.value,
        tableName: lookup.tableName,
        name: lookup.name ?? null
      };
    }
    case 40: {
      const choices = property.value;
      if (!choices) return null;
      return choices.map((c) => toNumber(c.value)).filter((n) => n !== null);
    }
    case "FileValue": {
      const file = property;
      return {
        id: file.id && file.id !== EMPTY_GUID ? file.id : null,
        fileName: file.fileName || null
      };
    }
    default:
      return value ?? null;
  }
}
function readFileColumn(properties, columnName, isImage) {
  const data = properties[columnName];
  if (data && "$type" in data && data.$type === "FileValue") {
    return unwrapColumnValue(data);
  }
  const idProperty = isImage ? properties[`${columnName}id`] : data;
  const id = idProperty?.value;
  if (typeof id !== "string" || id === "" || id === EMPTY_GUID) return null;
  const name = isImage ? null : properties[`${columnName}_name`]?.value ?? null;
  return { id, fileName: typeof name === "string" && name !== "" ? name : null };
}
function toNumber(value) {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isNaN(n) ? null : n;
}

// src/column-display.ts
function resolveColumnDisplayValue(record, columnName, _metadata) {
  const formatted = record.formattedValues?.[columnName];
  if (formatted !== void 0 && formatted !== null) {
    return formatted;
  }
  let property = record.properties?.[columnName];
  if (!property) return null;
  if (property.$type === "RelatedRecordValue") {
    const wrapper = property;
    if (!wrapper.value) return null;
    const innerFormatted = wrapper.value.formattedValue;
    if (innerFormatted) return innerFormatted;
    property = wrapper.value;
  }
  const t = property.$type;
  switch (t) {
    case 14: {
      const v = property.value;
      return v ?? null;
    }
    case 6: {
      const lookup = property;
      return lookup.name ?? lookup.value ?? null;
    }
    case 0:
    case 2:
    case 3:
    case 4:
    case 5:
    case 8:
    case 11:
    case 18: {
      const raw = property.value;
      return raw === void 0 || raw === null ? null : String(raw);
    }
    case 15: {
      const companion = record.properties?.[`${columnName}_name`];
      if (companion?.value) return companion.value;
      const v = property.value;
      return v ?? null;
    }
    case 40: {
      const list = property.value;
      if (!list || list.length === 0) return null;
      return list.map((c) => String(c.value)).join(", ");
    }
    case "FileValue": {
      const fv = property;
      return fv.fileName ?? null;
    }
    default: {
      return null;
    }
  }
}

// src/related-records.ts
function relatedRecordsKey(relationshipName, role) {
  if (role === void 0 || role === null) return `@${relationshipName}`;
  return `@${relationshipName}:${role === EntityRole.Referencing ? "referencing" : "referenced"}`;
}
function readRelatedRecords(record, key) {
  if (!record || !canReadColumn(record, key)) return null;
  const property = record.properties?.[key];
  if (!property || !("$type" in property) || property.$type !== "RelatedRecordsValue") return null;
  return property;
}
function relatedRecordName(value, record) {
  const formatted = record.formattedValues?.[value.primaryNameColumn];
  if (formatted) return formatted;
  const name = record.properties?.[value.primaryNameColumn]?.value;
  return typeof name === "string" ? name : "";
}

// src/party-list.ts
function readPartyList(record, column) {
  if (!record || !canReadColumn(record, column)) return null;
  const property = record.properties?.[column];
  if (!property || property.$type !== 10 || !Array.isArray(property.value)) return null;
  return property.value;
}
function activityPartyName(party) {
  return party.party?.name || party.address || party.displayName || "";
}

// src/record-id.ts
var EMPTY_GUID2 = "00000000-0000-0000-0000-000000000000";
function getRecordReferenceId(record) {
  if (!record) return void 0;
  if (hasSavedId(record)) return record.id;
  return record._idForCreate ?? void 0;
}
function hasSavedId(record) {
  return !!record?.id && record.id !== EMPTY_GUID2;
}

// src/resolved-columns.ts
function keepingGroupability(columns, earlier) {
  if (earlier.length === 0 || columns.some((c) => c.canGroupBy != null)) return columns;
  const said = /* @__PURE__ */ new Map();
  for (const c of earlier) {
    const name = c.columnName.toLowerCase();
    if (!said.has(name)) said.set(name, c.canGroupBy);
  }
  return columns.map((c) => {
    const name = c.columnName.toLowerCase();
    return said.has(name) ? { ...c, canGroupBy: said.get(name) ?? null } : c;
  });
}

// src/paging-cookies.ts
var PagingCookies = class {
  key;
  byPage = /* @__PURE__ */ new Map();
  lastPage;
  decided;
  /**
   * Store the cookie the response for `page` came back with. A response with
   * none takes back the one the page had: it was minted before.
   */
  record(key, page, cookie) {
    if (this.key !== key) {
      this.key = key;
      this.byPage = /* @__PURE__ */ new Map();
    }
    this.lastPage = page;
    if (cookie) this.byPage.set(page, cookie);
    else this.byPage.delete(page);
  }
  /**
   * The cookie to send when requesting `page` under `key`: the one page
   * `page - 1` returned, or `undefined` when it isn't known (page 1, a
   * non-sequential jump, or a key the store has no cookies for).
   */
  cookieFor(key, page) {
    if (this.key !== key || page <= 1) return void 0;
    return this.byPage.get(page - 1);
  }
  /**
   * The cookie to send when requesting `page` under `key` from the page that
   * landed last, when it's next to it — Next and Previous: that page's own
   * cookie, the freshest there is. `undefined` for a page reached any other
   * way — a jump, a link — which is asked for by number. Page 1 is always
   * asked for by number: a cursor buys nothing there, and the plain query is
   * exact even after records were added or removed.
   *
   * Asked again for the page it last answered for, under the same key — a
   * re-render, a refresh — it answers the same, so the request for the page
   * on screen doesn't change once that page has landed.
   */
  cookieBeside(key, page) {
    if (this.decided?.key === key && this.decided.page === page) return this.decided.cookie;
    const cookie = this.key !== key || page <= 1 || this.lastPage === void 0 || Math.abs(page - this.lastPage) !== 1 ? void 0 : this.byPage.get(this.lastPage);
    this.decided = { key, page, cookie };
    return cookie;
  }
};

// src/fetchxml/enums.ts
var AggregateType = {
  /** No aggregation. */
  None: 0,
  /** Count of rows (including nulls). */
  Count: 1,
  /** Count of rows where the column is not null. */
  CountColumn: 2,
  /** Sum of values. */
  Sum: 3,
  /** Arithmetic mean of values. */
  Avg: 4,
  /** Minimum value. */
  Min: 5,
  /** Maximum value. */
  Max: 6
};
var LogicalOperator = {
  /** All conditions must be true. */
  And: 0,
  /** At least one condition must be true. */
  Or: 1
};
var OrderType = {
  /** Ascending order. */
  Ascending: 0,
  /** Descending order. */
  Descending: 1
};
var ConditionOperator = {
  /** Equal to the specified value. */
  Equal: 0,
  /** Not equal to the specified value. */
  NotEqual: 1,
  /** Greater than the specified value. */
  GreaterThan: 2,
  /** Less than the specified value. */
  LessThan: 3,
  /** Greater than or equal to the specified value. */
  GreaterEqual: 4,
  /** Less than or equal to the specified value. */
  LessEqual: 5,
  /** Column value matches a SQL LIKE pattern. */
  Like: 6,
  /** Column value does not match a SQL LIKE pattern. */
  NotLike: 7,
  /** Column value is in the supplied list. */
  In: 8,
  /** Column value is not in the supplied list. */
  NotIn: 9,
  /** Column value is between two bounds (inclusive). */
  Between: 10,
  /** Column value is not between two bounds. */
  NotBetween: 11,
  /** Column value is null. */
  Null: 12,
  /** Column value is not null. */
  NotNull: 13,
  /** Date column value is yesterday. */
  Yesterday: 14,
  /** Date column value is today. */
  Today: 15,
  /** Date column value is tomorrow. */
  Tomorrow: 16,
  /** Date column value is in the last seven days. */
  Last7Days: 17,
  /** Date column value is in the next seven days. */
  Next7Days: 18,
  /** Date column value is in the last week. */
  LastWeek: 19,
  /** Date column value is in the current week. */
  ThisWeek: 20,
  /** Date column value is in the next week. */
  NextWeek: 21,
  /** Date column value is in the last month. */
  LastMonth: 22,
  /** Date column value is in the current month. */
  ThisMonth: 23,
  /** Date column value is in the next month. */
  NextMonth: 24,
  /** Date column value is on the specified day. */
  On: 25,
  /** Date column value is on or before the specified day. */
  OnOrBefore: 26,
  /** Date column value is on or after the specified day. */
  OnOrAfter: 27,
  /** Date column value is in the last year. */
  LastYear: 28,
  /** Date column value is in the current year. */
  ThisYear: 29,
  /** Date column value is in the next year. */
  NextYear: 30,
  /** Date column value is in the last X hours. */
  LastXHours: 31,
  /** Date column value is in the next X hours. */
  NextXHours: 32,
  /** Date column value is in the last X days. */
  LastXDays: 33,
  /** Date column value is in the next X days. */
  NextXDays: 34,
  /** Date column value is in the last X weeks. */
  LastXWeeks: 35,
  /** Date column value is in the next X weeks. */
  NextXWeeks: 36,
  /** Date column value is in the last X months. */
  LastXMonths: 37,
  /** Date column value is in the next X months. */
  NextXMonths: 38,
  /** Date column value is in the last X years. */
  LastXYears: 39,
  /** Date column value is in the next X years. */
  NextXYears: 40,
  /** Value equals the current user id. */
  EqualUserId: 41,
  /** Value does not equal the current user id. */
  NotEqualUserId: 42,
  /** Value equals the current business unit id. */
  EqualBusinessId: 43,
  /** Value does not equal the current business unit id. */
  NotEqualBusinessId: 44,
  /** Record is a child of the specified record. */
  ChildOf: 45,
  /** Column value matches the specified bitmask. */
  Mask: 46,
  /** Column value does not match the specified bitmask. */
  NotMask: 47,
  /** Column value matches the specified mask select. */
  MasksSelect: 48,
  /** String column contains the specified value. */
  Contains: 49,
  /** String column does not contain the specified value. */
  DoesNotContain: 50,
  /** Value equals the current user's language. */
  EqualUserLanguage: 51,
  /** Date column value is not on the specified day. */
  NotOn: 52,
  /** Date column value is older than X months. */
  OlderThanXMonths: 53,
  /** String column begins with the specified value. */
  BeginsWith: 54,
  /** String column does not begin with the specified value. */
  DoesNotBeginWith: 55,
  /** String column ends with the specified value. */
  EndsWith: 56,
  /** String column does not end with the specified value. */
  DoesNotEndWith: 57,
  /** Column value is in the current fiscal year. */
  ThisFiscalYear: 58,
  /** Column value is in the current fiscal period. */
  ThisFiscalPeriod: 59,
  /** Column value is in the next fiscal year. */
  NextFiscalYear: 60,
  /** Column value is in the next fiscal period. */
  NextFiscalPeriod: 61,
  /** Column value is in the last fiscal year. */
  LastFiscalYear: 62,
  /** Column value is in the last fiscal period. */
  LastFiscalPeriod: 63,
  /** Column value is in the last X fiscal years. */
  LastXFiscalYears: 64,
  /** Column value is in the last X fiscal periods. */
  LastXFiscalPeriods: 65,
  /** Column value is in the next X fiscal years. */
  NextXFiscalYears: 66,
  /** Column value is in the next X fiscal periods. */
  NextXFiscalPeriods: 67,
  /** Column value is in the specified fiscal year. */
  InFiscalYear: 68,
  /** Column value is in the specified fiscal period. */
  InFiscalPeriod: 69,
  /** Column value is in the specified fiscal period and year. */
  InFiscalPeriodAndYear: 70,
  /** Column value is in or before the specified fiscal period and year. */
  InOrBeforeFiscalPeriodAndYear: 71,
  /** Column value is in or after the specified fiscal period and year. */
  InOrAfterFiscalPeriodAndYear: 72,
  /** Value equals a team the user belongs to. */
  EqualUserTeams: 73,
  /** Value equals the current user id or a team the user belongs to. */
  EqualUserOrUserTeams: 74,
  /** Row is below the specified record in the hierarchy. */
  Under: 75,
  /** Row is not below the specified record in the hierarchy. */
  NotUnder: 76,
  /** Row equals or is below the specified record in the hierarchy. */
  UnderOrEqual: 77,
  /** Row is above the specified record in the hierarchy. */
  Above: 78,
  /** Row equals or is above the specified record in the hierarchy. */
  AboveOrEqual: 79,
  /** Value equals the current user id or is in the user's hierarchy. */
  EqualUserOrUserHierarchy: 80,
  /** Value equals the current user id, is in the hierarchy, or is in a team the user belongs to. */
  EqualUserOrUserHierarchyAndTeams: 81,
  /** Date column value is older than X years. */
  OlderThanXYears: 82,
  /** Date column value is older than X weeks. */
  OlderThanXWeeks: 83,
  /** Date column value is older than X days. */
  OlderThanXDays: 84,
  /** Date column value is older than X hours. */
  OlderThanXHours: 85,
  /** Date column value is older than X minutes. */
  OlderThanXMinutes: 86,
  /** Multi-select picklist contains all of the specified values. */
  ContainValues: 87,
  /** Multi-select picklist does not contain any of the specified values. */
  DoesNotContainValues: 88,
  /** Value equals the business id of the current role. */
  EqualRoleBusinessId: 89
};

// src/fetchxml/operator-strings.ts
var CONDITION_OPERATOR_TO_FETCH_XML = {
  [ConditionOperator.Equal]: "eq",
  [ConditionOperator.NotEqual]: "ne",
  [ConditionOperator.GreaterThan]: "gt",
  [ConditionOperator.LessThan]: "lt",
  [ConditionOperator.GreaterEqual]: "ge",
  [ConditionOperator.LessEqual]: "le",
  [ConditionOperator.Like]: "like",
  [ConditionOperator.NotLike]: "not-like",
  [ConditionOperator.In]: "in",
  [ConditionOperator.NotIn]: "not-in",
  [ConditionOperator.Between]: "between",
  [ConditionOperator.NotBetween]: "not-between",
  [ConditionOperator.Null]: "null",
  [ConditionOperator.NotNull]: "not-null",
  [ConditionOperator.Yesterday]: "yesterday",
  [ConditionOperator.Today]: "today",
  [ConditionOperator.Tomorrow]: "tomorrow",
  [ConditionOperator.Last7Days]: "last-seven-days",
  [ConditionOperator.Next7Days]: "next-seven-days",
  [ConditionOperator.LastWeek]: "last-week",
  [ConditionOperator.ThisWeek]: "this-week",
  [ConditionOperator.NextWeek]: "next-week",
  [ConditionOperator.LastMonth]: "last-month",
  [ConditionOperator.ThisMonth]: "this-month",
  [ConditionOperator.NextMonth]: "next-month",
  [ConditionOperator.On]: "on",
  [ConditionOperator.OnOrBefore]: "on-or-before",
  [ConditionOperator.OnOrAfter]: "on-or-after",
  [ConditionOperator.LastYear]: "last-year",
  [ConditionOperator.ThisYear]: "this-year",
  [ConditionOperator.NextYear]: "next-year",
  [ConditionOperator.LastXHours]: "last-x-hours",
  [ConditionOperator.NextXHours]: "next-x-hours",
  [ConditionOperator.LastXDays]: "last-x-days",
  [ConditionOperator.NextXDays]: "next-x-days",
  [ConditionOperator.LastXWeeks]: "last-x-weeks",
  [ConditionOperator.NextXWeeks]: "next-x-weeks",
  [ConditionOperator.LastXMonths]: "last-x-months",
  [ConditionOperator.NextXMonths]: "next-x-months",
  [ConditionOperator.LastXYears]: "last-x-years",
  [ConditionOperator.NextXYears]: "next-x-years",
  [ConditionOperator.EqualUserId]: "eq-userid",
  [ConditionOperator.NotEqualUserId]: "ne-userid",
  [ConditionOperator.EqualBusinessId]: "eq-businessid",
  [ConditionOperator.NotEqualBusinessId]: "ne-businessid",
  [ConditionOperator.ChildOf]: "child-of",
  [ConditionOperator.Mask]: "mask",
  [ConditionOperator.NotMask]: "not-mask",
  [ConditionOperator.MasksSelect]: "masks-select",
  [ConditionOperator.Contains]: "like",
  [ConditionOperator.DoesNotContain]: "not-like",
  [ConditionOperator.EqualUserLanguage]: "eq-userlanguage",
  [ConditionOperator.NotOn]: "ne",
  [ConditionOperator.OlderThanXMonths]: "olderthan-x-months",
  [ConditionOperator.BeginsWith]: "like",
  [ConditionOperator.DoesNotBeginWith]: "not-like",
  [ConditionOperator.EndsWith]: "like",
  [ConditionOperator.DoesNotEndWith]: "not-like",
  [ConditionOperator.ThisFiscalYear]: "this-fiscal-year",
  [ConditionOperator.ThisFiscalPeriod]: "this-fiscal-period",
  [ConditionOperator.NextFiscalYear]: "next-fiscal-year",
  [ConditionOperator.NextFiscalPeriod]: "next-fiscal-period",
  [ConditionOperator.LastFiscalYear]: "last-fiscal-year",
  [ConditionOperator.LastFiscalPeriod]: "last-fiscal-period",
  [ConditionOperator.LastXFiscalYears]: "last-x-fiscal-years",
  [ConditionOperator.LastXFiscalPeriods]: "last-x-fiscal-periods",
  [ConditionOperator.NextXFiscalYears]: "next-x-fiscal-years",
  [ConditionOperator.NextXFiscalPeriods]: "next-x-fiscal-periods",
  [ConditionOperator.InFiscalYear]: "in-fiscal-year",
  [ConditionOperator.InFiscalPeriod]: "in-fiscal-period",
  [ConditionOperator.InFiscalPeriodAndYear]: "in-fiscal-period-and-year",
  [ConditionOperator.InOrBeforeFiscalPeriodAndYear]: "in-or-before-fiscal-period-and-year",
  [ConditionOperator.InOrAfterFiscalPeriodAndYear]: "in-or-after-fiscal-period-and-year",
  [ConditionOperator.EqualUserTeams]: "eq-userteams",
  [ConditionOperator.EqualUserOrUserTeams]: "eq-useroruserteams",
  [ConditionOperator.Under]: "under",
  [ConditionOperator.NotUnder]: "not-under",
  [ConditionOperator.UnderOrEqual]: "eq-or-under",
  [ConditionOperator.Above]: "above",
  [ConditionOperator.AboveOrEqual]: "eq-or-above",
  [ConditionOperator.EqualUserOrUserHierarchy]: "eq-useroruserhierarchy",
  [ConditionOperator.EqualUserOrUserHierarchyAndTeams]: "eq-useroruserhierarchyandteams",
  [ConditionOperator.OlderThanXYears]: "olderthan-x-years",
  [ConditionOperator.OlderThanXWeeks]: "olderthan-x-weeks",
  [ConditionOperator.OlderThanXDays]: "olderthan-x-days",
  [ConditionOperator.OlderThanXHours]: "olderthan-x-hours",
  [ConditionOperator.OlderThanXMinutes]: "olderthan-x-minutes",
  [ConditionOperator.ContainValues]: "contain-values",
  [ConditionOperator.DoesNotContainValues]: "not-contain-values",
  [ConditionOperator.EqualRoleBusinessId]: "eq-rolebusinessid"
};
function buildReverseMap(forward) {
  const reverse = /* @__PURE__ */ new Map();
  for (const key of Object.keys(forward).map((k) => Number(k)).sort((a, b) => a - b)) {
    const value = forward[key];
    if (!reverse.has(value)) reverse.set(value, key);
  }
  return reverse;
}
var FETCH_XML_TO_CONDITION_OPERATOR = buildReverseMap(CONDITION_OPERATOR_TO_FETCH_XML);
function conditionOperatorToFetchXml(operator) {
  const value = CONDITION_OPERATOR_TO_FETCH_XML[operator];
  if (value === void 0) {
    throw new Error(`The '${operator}' condition operator is not supported.`);
  }
  return value;
}
function conditionOperatorFromFetchXml(value) {
  return FETCH_XML_TO_CONDITION_OPERATOR.get(value);
}
var ALL_FETCH_XML_OPERATOR_STRINGS = [
  ...FETCH_XML_TO_CONDITION_OPERATOR.keys()
];
var AGGREGATE_TO_FETCH_XML = {
  [AggregateType.None]: null,
  [AggregateType.Count]: "count",
  [AggregateType.CountColumn]: "countcolumn",
  [AggregateType.Sum]: "sum",
  [AggregateType.Avg]: "avg",
  [AggregateType.Min]: "min",
  [AggregateType.Max]: "max"
};
function aggregateTypeToFetchXml(aggregate) {
  return AGGREGATE_TO_FETCH_XML[aggregate] ?? void 0;
}
function aggregateTypeFromFetchXml(value) {
  switch (value) {
    case "sum":
      return AggregateType.Sum;
    case "avg":
      return AggregateType.Avg;
    case "max":
      return AggregateType.Max;
    case "min":
      return AggregateType.Min;
    case "count":
      return AggregateType.Count;
    case "countcolumn":
      return AggregateType.CountColumn;
    default:
      return AggregateType.None;
  }
}
var DATE_GROUPING_TO_FETCH_XML = {
  [DateGrouping.Day]: "day",
  [DateGrouping.Week]: "week",
  [DateGrouping.Month]: "month",
  [DateGrouping.Quarter]: "quarter",
  [DateGrouping.Year]: "year",
  [DateGrouping.FiscalPeriod]: "fiscal-period",
  [DateGrouping.FiscalYear]: "fiscal-year"
};
function dateGroupingToFetchXml(grouping) {
  if (grouping === DateGrouping.None) return void 0;
  const value = DATE_GROUPING_TO_FETCH_XML[grouping];
  if (value === void 0) throw new Error(`FetchXML has no date grouping for ${grouping}.`);
  return value;
}
function dateGroupingFromFetchXml(value) {
  switch (value) {
    case "day":
      return DateGrouping.Day;
    case "week":
      return DateGrouping.Week;
    case "month":
      return DateGrouping.Month;
    case "quarter":
      return DateGrouping.Quarter;
    case "year":
      return DateGrouping.Year;
    case "fiscal-period":
      return DateGrouping.FiscalPeriod;
    case "fiscal-year":
      return DateGrouping.FiscalYear;
    default:
      return DateGrouping.None;
  }
}
var JOIN_OPERATOR_TO_FETCH_XML = {
  [JoinOperator.Inner]: "inner",
  [JoinOperator.LeftOuter]: "outer",
  [JoinOperator.Natural]: "natural",
  [JoinOperator.MatchFirstRowUsingCrossApply]: "matchfirstrowusingcrossapply",
  [JoinOperator.In]: "in",
  [JoinOperator.Exists]: "exists",
  [JoinOperator.Any]: "any",
  [JoinOperator.NotAny]: "not any",
  [JoinOperator.All]: "all",
  [JoinOperator.NotAll]: "not all"
};
var FETCH_XML_TO_JOIN_OPERATOR = buildReverseMap(JOIN_OPERATOR_TO_FETCH_XML);
function joinOperatorToFetchXml(joinOperator) {
  const value = JOIN_OPERATOR_TO_FETCH_XML[joinOperator];
  if (value === void 0) {
    throw new Error(`The '${joinOperator}' join operator is not supported.`);
  }
  return value;
}
function joinOperatorFromFetchXml(value) {
  return FETCH_XML_TO_JOIN_OPERATOR.get(value);
}
var ALL_FETCH_XML_LINK_TYPES = [...FETCH_XML_TO_JOIN_OPERATOR.keys()];
function logicalOperatorToFetchXml(logicalOperator) {
  return logicalOperator === LogicalOperator.Or ? "or" : "and";
}
function logicalOperatorFromFetchXml(value) {
  return value === "or" ? LogicalOperator.Or : LogicalOperator.And;
}
function orderTypeToFetchXml(orderType) {
  return orderType === OrderType.Descending ? "true" : "false";
}

// src/fetchxml/operator-metadata.ts
var TEXT = [ColumnType.String, ColumnType.Memo];
var NUMBER = [
  ColumnType.Integer,
  ColumnType.BigInt,
  ColumnType.Decimal,
  ColumnType.Double,
  ColumnType.Money
];
var DATE = [ColumnType.DateTime];
var BOOL = [ColumnType.Boolean];
var CHOICE = [ColumnType.Choice, ColumnType.State, ColumnType.Status];
var MULTI = [ColumnType.MultiSelectChoice];
var LOOKUP = [
  ColumnType.Lookup,
  ColumnType.Customer,
  ColumnType.Owner,
  ColumnType.PartyList
];
var GUID = [ColumnType.Uniqueidentifier];
var ENTITY_NAME = [ColumnType.EntityName];
function aggregateTypesForColumn(columnType) {
  if (columnType === void 0) return ALL_AGGREGATE_TYPES;
  const counting = [AggregateType.Count, AggregateType.CountColumn];
  if (NUMBER.includes(columnType)) return ALL_AGGREGATE_TYPES;
  if (DATE.includes(columnType)) {
    return [...counting, AggregateType.Min, AggregateType.Max];
  }
  return counting;
}
var ALL_AGGREGATE_TYPES = [
  AggregateType.Count,
  AggregateType.CountColumn,
  AggregateType.Sum,
  AggregateType.Avg,
  AggregateType.Min,
  AggregateType.Max
];
var ALL = [
  ...TEXT,
  ...NUMBER,
  ...DATE,
  ...BOOL,
  ...CHOICE,
  ...MULTI,
  ...LOOKUP,
  ...GUID,
  ...ENTITY_NAME
];
var ORDERED = [...TEXT, ...NUMBER, ...DATE, ...CHOICE];
var PRINCIPAL = [...LOOKUP, ...GUID];
var CONDITION_OPERATOR_NAMES = Object.freeze(
  Object.fromEntries(
    Object.entries(ConditionOperator).map(([name, value]) => [value, name])
  )
);
function conditionOperatorName(operator) {
  return CONDITION_OPERATOR_NAMES[operator] ?? String(operator);
}
var SPECS = {
  [ConditionOperator.Equal]: ["one", "column", ALL],
  [ConditionOperator.NotEqual]: ["one", "column", ALL],
  [ConditionOperator.GreaterThan]: ["one", "column", ORDERED],
  [ConditionOperator.LessThan]: ["one", "column", ORDERED],
  [ConditionOperator.GreaterEqual]: ["one", "column", ORDERED],
  [ConditionOperator.LessEqual]: ["one", "column", ORDERED],
  [ConditionOperator.Like]: ["one", "column", TEXT],
  [ConditionOperator.NotLike]: ["one", "column", TEXT],
  [ConditionOperator.In]: ["many", "column", ALL],
  [ConditionOperator.NotIn]: ["many", "column", ALL],
  [ConditionOperator.Between]: ["two", "column", [...NUMBER, ...DATE]],
  [ConditionOperator.NotBetween]: ["two", "column", [...NUMBER, ...DATE]],
  [ConditionOperator.Null]: ["none", "none", ALL],
  [ConditionOperator.NotNull]: ["none", "none", ALL],
  [ConditionOperator.Yesterday]: ["none", "none", DATE],
  [ConditionOperator.Today]: ["none", "none", DATE],
  [ConditionOperator.Tomorrow]: ["none", "none", DATE],
  [ConditionOperator.Last7Days]: ["none", "none", DATE],
  [ConditionOperator.Next7Days]: ["none", "none", DATE],
  [ConditionOperator.LastWeek]: ["none", "none", DATE],
  [ConditionOperator.ThisWeek]: ["none", "none", DATE],
  [ConditionOperator.NextWeek]: ["none", "none", DATE],
  [ConditionOperator.LastMonth]: ["none", "none", DATE],
  [ConditionOperator.ThisMonth]: ["none", "none", DATE],
  [ConditionOperator.NextMonth]: ["none", "none", DATE],
  [ConditionOperator.On]: ["one", "column", DATE],
  [ConditionOperator.OnOrBefore]: ["one", "column", DATE],
  [ConditionOperator.OnOrAfter]: ["one", "column", DATE],
  [ConditionOperator.LastYear]: ["none", "none", DATE],
  [ConditionOperator.ThisYear]: ["none", "none", DATE],
  [ConditionOperator.NextYear]: ["none", "none", DATE],
  [ConditionOperator.LastXHours]: ["one", "number", DATE],
  [ConditionOperator.NextXHours]: ["one", "number", DATE],
  [ConditionOperator.LastXDays]: ["one", "number", DATE],
  [ConditionOperator.NextXDays]: ["one", "number", DATE],
  [ConditionOperator.LastXWeeks]: ["one", "number", DATE],
  [ConditionOperator.NextXWeeks]: ["one", "number", DATE],
  [ConditionOperator.LastXMonths]: ["one", "number", DATE],
  [ConditionOperator.NextXMonths]: ["one", "number", DATE],
  [ConditionOperator.LastXYears]: ["one", "number", DATE],
  [ConditionOperator.NextXYears]: ["one", "number", DATE],
  [ConditionOperator.EqualUserId]: ["none", "none", PRINCIPAL],
  [ConditionOperator.NotEqualUserId]: ["none", "none", PRINCIPAL],
  [ConditionOperator.EqualBusinessId]: ["none", "none", PRINCIPAL],
  [ConditionOperator.NotEqualBusinessId]: ["none", "none", PRINCIPAL],
  [ConditionOperator.ChildOf]: ["one", "guid", LOOKUP],
  [ConditionOperator.Mask]: ["one", "number", NUMBER],
  [ConditionOperator.NotMask]: ["one", "number", NUMBER],
  [ConditionOperator.MasksSelect]: ["one", "number", NUMBER],
  [ConditionOperator.Contains]: ["one", "column", TEXT],
  [ConditionOperator.DoesNotContain]: ["one", "column", TEXT],
  [ConditionOperator.EqualUserLanguage]: ["none", "none", NUMBER],
  [ConditionOperator.NotOn]: ["one", "column", DATE],
  [ConditionOperator.OlderThanXMonths]: ["one", "number", DATE],
  [ConditionOperator.BeginsWith]: ["one", "column", TEXT],
  [ConditionOperator.DoesNotBeginWith]: ["one", "column", TEXT],
  [ConditionOperator.EndsWith]: ["one", "column", TEXT],
  [ConditionOperator.DoesNotEndWith]: ["one", "column", TEXT],
  [ConditionOperator.ThisFiscalYear]: ["none", "none", DATE],
  [ConditionOperator.ThisFiscalPeriod]: ["none", "none", DATE],
  [ConditionOperator.NextFiscalYear]: ["none", "none", DATE],
  [ConditionOperator.NextFiscalPeriod]: ["none", "none", DATE],
  [ConditionOperator.LastFiscalYear]: ["none", "none", DATE],
  [ConditionOperator.LastFiscalPeriod]: ["none", "none", DATE],
  [ConditionOperator.LastXFiscalYears]: ["one", "number", DATE],
  [ConditionOperator.LastXFiscalPeriods]: ["one", "number", DATE],
  [ConditionOperator.NextXFiscalYears]: ["one", "number", DATE],
  [ConditionOperator.NextXFiscalPeriods]: ["one", "number", DATE],
  [ConditionOperator.InFiscalYear]: ["one", "fiscalYear", DATE],
  [ConditionOperator.InFiscalPeriod]: ["one", "fiscalPeriod", DATE],
  [ConditionOperator.InFiscalPeriodAndYear]: ["two", "fiscalPeriodAndYear", DATE],
  [ConditionOperator.InOrBeforeFiscalPeriodAndYear]: ["two", "fiscalPeriodAndYear", DATE],
  [ConditionOperator.InOrAfterFiscalPeriodAndYear]: ["two", "fiscalPeriodAndYear", DATE],
  [ConditionOperator.EqualUserTeams]: ["none", "none", PRINCIPAL],
  [ConditionOperator.EqualUserOrUserTeams]: ["none", "none", PRINCIPAL],
  [ConditionOperator.Under]: ["one", "guid", LOOKUP],
  [ConditionOperator.NotUnder]: ["one", "guid", LOOKUP],
  [ConditionOperator.UnderOrEqual]: ["one", "guid", LOOKUP],
  [ConditionOperator.Above]: ["one", "guid", LOOKUP],
  [ConditionOperator.AboveOrEqual]: ["one", "guid", LOOKUP],
  [ConditionOperator.EqualUserOrUserHierarchy]: ["none", "none", PRINCIPAL],
  [ConditionOperator.EqualUserOrUserHierarchyAndTeams]: ["none", "none", PRINCIPAL],
  [ConditionOperator.OlderThanXYears]: ["one", "number", DATE],
  [ConditionOperator.OlderThanXWeeks]: ["one", "number", DATE],
  [ConditionOperator.OlderThanXDays]: ["one", "number", DATE],
  [ConditionOperator.OlderThanXHours]: ["one", "number", DATE],
  [ConditionOperator.OlderThanXMinutes]: ["one", "number", DATE],
  [ConditionOperator.ContainValues]: ["many", "column", MULTI],
  [ConditionOperator.DoesNotContainValues]: ["many", "column", MULTI],
  [ConditionOperator.EqualRoleBusinessId]: ["none", "none", PRINCIPAL]
};
var INFO = Object.freeze(
  Object.fromEntries(
    Object.entries(SPECS).map(([key, [arity, valueKind, columnTypes]]) => {
      const operator = Number(key);
      return [
        operator,
        {
          operator,
          name: conditionOperatorName(operator),
          fetchXml: conditionOperatorToFetchXml(operator),
          arity,
          valueKind,
          columnTypes
        }
      ];
    })
  )
);
var ALL_CONDITION_OPERATORS = Object.values(INFO).sort(
  (a, b) => a.operator - b.operator
);
function getConditionOperatorInfo(operator) {
  const info = INFO[operator];
  if (info === void 0) {
    throw new Error(`Unknown condition operator: ${operator}`);
  }
  return info;
}
function getConditionOperatorsForColumnType(columnType) {
  if (columnType === void 0) return ALL_CONDITION_OPERATORS;
  const matches = ALL_CONDITION_OPERATORS.filter((info) => info.columnTypes.includes(columnType));
  if (matches.length > 0) return matches;
  return ALL_CONDITION_OPERATORS.filter((info) => info.columnTypes === ALL);
}
function getConditionValueCount(operator) {
  switch (getConditionOperatorInfo(operator).arity) {
    case "none":
      return 0;
    case "one":
      return 1;
    case "two":
      return 2;
    default:
      return "many";
  }
}
function inferLikeOperator(operator, value) {
  const negated = operator === ConditionOperator.NotLike;
  if (operator !== ConditionOperator.Like && !negated) return operator;
  if (value === void 0 || value === "") return operator;
  const leading = value.startsWith("%");
  const trailing = value.endsWith("%") && value.length > 1;
  if (leading && trailing) {
    return negated ? ConditionOperator.DoesNotContain : ConditionOperator.Contains;
  }
  if (trailing) {
    return negated ? ConditionOperator.DoesNotBeginWith : ConditionOperator.BeginsWith;
  }
  if (leading) {
    return negated ? ConditionOperator.DoesNotEndWith : ConditionOperator.EndsWith;
  }
  return operator;
}
function stripLikeWildcards(value) {
  if (value === void 0) return "";
  return value.replace(/^%+/, "").replace(/%+$/, "");
}
function formatLikeValue(operator, text) {
  const bare = stripLikeWildcards(text);
  switch (operator) {
    case ConditionOperator.BeginsWith:
    case ConditionOperator.DoesNotBeginWith:
      return `${bare}%`;
    case ConditionOperator.EndsWith:
    case ConditionOperator.DoesNotEndWith:
      return `%${bare}`;
    case ConditionOperator.Contains:
    case ConditionOperator.DoesNotContain:
      return `%${bare}%`;
    default:
      return text;
  }
}
var COMPARABLE_FAMILIES = [
  TEXT,
  NUMBER,
  DATE,
  BOOL,
  CHOICE,
  MULTI,
  // A lookup and a plain guid column both hold record ids, so comparing them is
  // the normal way to ask "does this point at that record".
  [...LOOKUP, ...GUID]
];
function areColumnTypesComparable(source, target) {
  if (source === void 0 || target === void 0) return true;
  if (source === target) return true;
  return COMPARABLE_FAMILIES.some(
    (family) => family.includes(source) && family.includes(target)
  );
}
var COLUMN_COMPARISON_OPERATORS = [
  ConditionOperator.Equal,
  ConditionOperator.NotEqual,
  ConditionOperator.GreaterThan,
  ConditionOperator.GreaterEqual,
  ConditionOperator.LessThan,
  ConditionOperator.LessEqual
];
function supportsColumnComparison(operator) {
  return COLUMN_COMPARISON_OPERATORS.includes(operator);
}

// src/fetchxml/xml.ts
var FetchXmlParseError = class extends Error {
  /** Zero-based character offset into the source. */
  position;
  /** One-based line number. */
  line;
  /** One-based column number. */
  column;
  constructor(message, source, position) {
    const upTo = source.slice(0, position);
    const line = upTo.split("\n").length;
    const lastBreak = upTo.lastIndexOf("\n");
    const column = position - lastBreak;
    super(`${message} (line ${line}, column ${column})`);
    this.name = "FetchXmlParseError";
    this.position = position;
    this.line = line;
    this.column = column;
  }
};
var NAME_START = /[A-Za-z_:]/;
var NAME_CHAR = /[A-Za-z0-9_:.\-]/;
var WHITESPACE = /[ \t\r\n]/;
function parseXml(source) {
  const reader = new XmlReader(source);
  return reader.readDocument();
}
var XmlReader = class {
  constructor(source) {
    this.source = source;
  }
  source;
  pos = 0;
  readDocument() {
    this.skipMisc();
    if (this.pos >= this.source.length) {
      throw this.error("No root element found", this.pos);
    }
    const root = this.readElement();
    this.skipMisc();
    if (this.pos < this.source.length) {
      throw this.error("Unexpected content after the root element", this.pos);
    }
    return root;
  }
  /** Skips whitespace, comments, processing instructions and the doctype declaration. */
  skipMisc() {
    for (; ; ) {
      this.skipWhitespace();
      if (this.startsWith("<!--")) {
        this.skipUntil("-->", "Unterminated comment");
      } else if (this.startsWith("<?")) {
        this.skipUntil("?>", "Unterminated processing instruction");
      } else if (this.startsWith("<!DOCTYPE")) {
        this.skipUntil(">", "Unterminated DOCTYPE declaration");
      } else {
        return;
      }
    }
  }
  readElement() {
    const start = this.pos;
    this.expect("<");
    const name = this.readName();
    const attributes = {};
    for (; ; ) {
      this.skipWhitespace();
      if (this.startsWith("/>")) {
        this.pos += 2;
        return { name, attributes, children: [], text: "" };
      }
      if (this.startsWith(">")) {
        this.pos += 1;
        return this.readContent(name, attributes, start);
      }
      if (this.pos >= this.source.length) {
        throw this.error(`Unterminated <${name}> element`, start);
      }
      const attributeName = this.readName();
      this.skipWhitespace();
      this.expect("=");
      this.skipWhitespace();
      attributes[attributeName] = this.readQuotedValue();
    }
  }
  /** Reads children and character data up to the matching close tag. */
  readContent(name, attributes, start) {
    const children = [];
    let text = "";
    for (; ; ) {
      if (this.pos >= this.source.length) {
        throw this.error(`Unterminated <${name}> element`, start);
      }
      if (this.startsWith("</")) {
        this.pos += 2;
        const closing = this.readName();
        if (closing !== name) {
          throw this.error(`Expected </${name}> but found </${closing}>`, this.pos);
        }
        this.skipWhitespace();
        this.expect(">");
        const content = children.length > 0 && text.trim() === "" ? "" : text;
        return { name, attributes, children, text: content };
      }
      if (this.startsWith("<!--")) {
        this.skipUntil("-->", "Unterminated comment");
      } else if (this.startsWith("<![CDATA[")) {
        const end = this.source.indexOf("]]>", this.pos);
        if (end < 0) throw this.error("Unterminated CDATA section", this.pos);
        text += this.source.slice(this.pos + "<![CDATA[".length, end);
        this.pos = end + "]]>".length;
      } else if (this.startsWith("<?")) {
        this.skipUntil("?>", "Unterminated processing instruction");
      } else if (this.startsWith("<")) {
        children.push(this.readElement());
      } else {
        const next = this.source.indexOf("<", this.pos);
        const end = next < 0 ? this.source.length : next;
        text += decodeEntities(this.source.slice(this.pos, end));
        this.pos = end;
      }
    }
  }
  readName() {
    const start = this.pos;
    const first = this.source[this.pos];
    if (first === void 0 || !NAME_START.test(first)) {
      throw this.error("Expected an element or attribute name", this.pos);
    }
    this.pos += 1;
    while (this.pos < this.source.length) {
      const ch = this.source[this.pos];
      if (ch === void 0 || !NAME_CHAR.test(ch)) break;
      this.pos += 1;
    }
    return this.source.slice(start, this.pos);
  }
  readQuotedValue() {
    const quote = this.source[this.pos];
    if (quote !== '"' && quote !== "'") {
      throw this.error("Expected a quoted attribute value", this.pos);
    }
    this.pos += 1;
    const end = this.source.indexOf(quote, this.pos);
    if (end < 0) throw this.error("Unterminated attribute value", this.pos);
    const raw = this.source.slice(this.pos, end);
    this.pos = end + 1;
    return decodeEntities(raw);
  }
  startsWith(token) {
    return this.source.startsWith(token, this.pos);
  }
  expect(token) {
    if (!this.startsWith(token)) {
      throw this.error(`Expected '${token}'`, this.pos);
    }
    this.pos += token.length;
  }
  skipWhitespace() {
    while (this.pos < this.source.length) {
      const ch = this.source[this.pos];
      if (ch === void 0 || !WHITESPACE.test(ch)) break;
      this.pos += 1;
    }
  }
  skipUntil(token, message) {
    const end = this.source.indexOf(token, this.pos);
    if (end < 0) throw this.error(message, this.pos);
    this.pos = end + token.length;
  }
  error(message, position) {
    return new FetchXmlParseError(message, this.source, position);
  }
};
var NAMED_ENTITIES = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'"
};
function decodeEntities(value) {
  if (!value.includes("&")) return value;
  return value.replace(/&(#x?[0-9A-Fa-f]+|[A-Za-z][A-Za-z0-9]*);/g, (match, body) => {
    if (body.startsWith("#x") || body.startsWith("#X")) {
      const code = Number.parseInt(body.slice(2), 16);
      return Number.isNaN(code) ? match : String.fromCodePoint(code);
    }
    if (body.startsWith("#")) {
      const code = Number.parseInt(body.slice(1), 10);
      return Number.isNaN(code) ? match : String.fromCodePoint(code);
    }
    return NAMED_ENTITIES[body] ?? match;
  });
}
function escapeText(value) {
  return value.replace(/[&<>]/g, (ch) => ch === "&" ? "&amp;" : ch === "<" ? "&lt;" : "&gt;");
}
function escapeAttribute(value) {
  return value.replace(/[&<>"\t\n\r]/g, (ch) => {
    switch (ch) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      case "	":
        return "&#x9;";
      case "\n":
        return "&#xA;";
      default:
        return "&#xD;";
    }
  });
}
function serializeXml(root, options) {
  const indent = options?.indent ?? "  ";
  const newLine = options?.newLine ?? "\n";
  const lines = [];
  writeElement(root, 0);
  return lines.join(indent === "" ? "" : newLine);
  function writeElement(element, depth) {
    const pad2 = indent.repeat(depth);
    const attributes = Object.entries(element.attributes).map(([name, value]) => ` ${name}="${escapeAttribute(value)}"`).join("");
    if (element.children.length === 0 && element.text === "") {
      lines.push(`${pad2}<${element.name}${attributes} />`);
      return;
    }
    if (element.children.length === 0) {
      lines.push(`${pad2}<${element.name}${attributes}>${escapeText(element.text)}</${element.name}>`);
      return;
    }
    lines.push(`${pad2}<${element.name}${attributes}>`);
    if (element.text !== "") lines.push(`${pad2}${indent}${escapeText(element.text)}`);
    for (const child of element.children) writeElement(child, depth + 1);
    lines.push(`${pad2}</${element.name}>`);
  }
}

// src/fetchxml/fetch-xml.ts
var nextNodeId = 0;
function createNodeId() {
  nextNodeId += 1;
  return `fx${nextNodeId}`;
}
function createEntity(name = "") {
  return { id: createNodeId(), name, attributes: [], orders: [], filters: [], linkEntities: [] };
}
function createLinkEntity(init = {}) {
  return {
    id: createNodeId(),
    name: "",
    attributes: [],
    orders: [],
    filters: [],
    linkEntities: [],
    linkType: JoinOperator.Inner,
    ...init
  };
}
function createAttribute(name = "") {
  return { id: createNodeId(), name };
}
function createOrder(attribute = "", descending = false) {
  return { id: createNodeId(), attribute, descending };
}
function createFilter(type = LogicalOperator.And) {
  return { id: createNodeId(), type, conditions: [], filters: [] };
}
function createCondition(init = {}) {
  const { operator = ConditionOperator.Equal, ...rest } = init;
  return { id: createNodeId(), operator, ...rest };
}
function createFetchXmlQuery(entityName = "") {
  return { entity: createEntity(entityName) };
}
function take(rest, name) {
  const value = rest[name];
  delete rest[name];
  return value;
}
function toBool(value) {
  if (value === void 0) return void 0;
  const normalized = value.trim().toLowerCase();
  if (normalized === "true" || normalized === "1") return true;
  if (normalized === "false" || normalized === "0") return false;
  return void 0;
}
function toInt(value) {
  if (value === void 0) return void 0;
  const parsed = Number.parseInt(value.trim(), 10);
  return Number.isNaN(parsed) ? void 0 : parsed;
}
function leftovers(rest) {
  return Object.keys(rest).length > 0 ? rest : void 0;
}
var UNION_FILTER_HINT = "union";
function parseFetchXml(fetchXml) {
  if (fetchXml.trim() === "") {
    throw new FetchXmlParseError("No FetchXML provided", fetchXml, 0);
  }
  const root = parseXml(fetchXml);
  if (root.name !== "fetch") {
    throw new FetchXmlParseError(
      `Expected a <fetch> root element but found <${root.name}>`,
      fetchXml,
      0
    );
  }
  const rest = { ...root.attributes };
  const entityElement = root.children.find((child) => child.name === "entity");
  const unknownElements = root.children.filter((child) => child.name !== "entity");
  const query = {
    entity: entityElement ? parseEntity(entityElement) : createEntity(),
    top: toInt(take(rest, "top")),
    count: toInt(take(rest, "count")),
    page: toInt(take(rest, "page")),
    pagingCookie: take(rest, "paging-cookie"),
    returnTotalRecordCount: toBool(take(rest, "returntotalrecordcount")),
    aggregate: toBool(take(rest, "aggregate")),
    aggregateLimit: toInt(take(rest, "aggregatelimit")),
    distinct: toBool(take(rest, "distinct")),
    noLock: toBool(take(rest, "no-lock")),
    dataSource: take(rest, "datasource"),
    utcOffset: toInt(take(rest, "utc-offset")),
    outputFormat: take(rest, "output-format"),
    lateMaterialize: toBool(take(rest, "latematerialize")),
    minActiveRowVersion: toBool(take(rest, "min-active-row-version")),
    useRawOrderBy: toBool(take(rest, "useraworderby")),
    savedQueryId: take(rest, "savedqueryid"),
    mapping: take(rest, "mapping"),
    version: take(rest, "version"),
    options: take(rest, "options"),
    unknownAttributes: leftovers(rest),
    unknownElements: unknownElements.length > 0 ? unknownElements : void 0
  };
  return query;
}
var KNOWN_ENTITY_CHILDREN = /* @__PURE__ */ new Set(["all-attributes", "attribute", "order", "filter", "link-entity"]);
function parseEntityCore(element, rest) {
  return {
    id: createNodeId(),
    name: take(rest, "name") ?? "",
    allAttributes: element.children.some((child) => child.name === "all-attributes") || void 0,
    attributes: element.children.filter((c) => c.name === "attribute").map(parseAttribute),
    orders: element.children.filter((c) => c.name === "order").map(parseOrder),
    filters: element.children.filter((c) => c.name === "filter").map(parseFilter),
    linkEntities: element.children.filter((c) => c.name === "link-entity").map(parseLinkEntity),
    enablePrefiltering: toBool(take(rest, "enableprefiltering")),
    prefilterParameterName: take(rest, "prefilterparametername"),
    unknownElements: pickUnknownChildren(element)
  };
}
function pickUnknownChildren(element) {
  return pickUnknown(element, KNOWN_ENTITY_CHILDREN);
}
var KNOWN_FILTER_CHILDREN = /* @__PURE__ */ new Set(["condition", "filter", "link-entity"]);
function pickUnknown(element, known) {
  const unknown = element.children.filter((child) => !known.has(child.name));
  return unknown.length > 0 ? unknown : void 0;
}
function parseEntity(element) {
  const rest = { ...element.attributes };
  const entity = parseEntityCore(element, rest);
  return { ...entity, unknownAttributes: leftovers(rest) };
}
function parseLinkEntity(element) {
  const rest = { ...element.attributes };
  const core = parseEntityCore(element, rest);
  const linkType = take(rest, "link-type");
  return {
    ...core,
    from: take(rest, "from"),
    to: take(rest, "to"),
    alias: take(rest, "alias"),
    linkType: linkType === void 0 ? void 0 : joinOperatorFromFetchXml(linkType),
    visible: toBool(take(rest, "visible")),
    intersect: toBool(take(rest, "intersect")),
    // A link-type string no JoinOperator produces would otherwise vanish; keep it
    // so serialization writes back what the author had.
    unknownAttributes: leftovers(
      linkType !== void 0 && joinOperatorFromFetchXml(linkType) === void 0 ? { ...rest, "link-type": linkType } : rest
    )
  };
}
function parseAttribute(element) {
  const rest = { ...element.attributes };
  const aggregate = aggregateTypeFromFetchXml(take(rest, "aggregate"));
  const dateGrouping = dateGroupingFromFetchXml(take(rest, "dategrouping"));
  return {
    id: createNodeId(),
    name: take(rest, "name") ?? "",
    alias: take(rest, "alias"),
    aggregate: aggregate === AggregateType.None ? void 0 : aggregate,
    groupBy: toBool(take(rest, "groupby")),
    dateGrouping: dateGrouping === DateGrouping.None ? void 0 : dateGrouping,
    distinct: toBool(take(rest, "distinct")),
    userTimeZone: toBool(take(rest, "usertimezone")),
    unknownAttributes: leftovers(rest)
  };
}
function parseOrder(element) {
  const rest = { ...element.attributes };
  return {
    id: createNodeId(),
    attribute: take(rest, "attribute"),
    alias: take(rest, "alias"),
    descending: toBool(take(rest, "descending")),
    entityName: take(rest, "entityname"),
    unknownAttributes: leftovers(rest)
  };
}
function pickFilterLinks(element) {
  const links = element.children.filter((c) => c.name === "link-entity").map(parseLinkEntity);
  return links.length > 0 ? links : void 0;
}
function parseFilter(element) {
  const rest = { ...element.attributes };
  return {
    id: createNodeId(),
    type: logicalOperatorFromFetchXml(take(rest, "type")),
    conditions: element.children.filter((c) => c.name === "condition").map(parseCondition),
    filters: element.children.filter((c) => c.name === "filter").map(parseFilter),
    // Dropped entirely before this existed, which silently discarded the whole
    // related-table test from any query using an `any` / `not any` / `all` join.
    linkEntities: pickFilterLinks(element),
    unknownElements: pickUnknown(element, KNOWN_FILTER_CHILDREN),
    isQuickFindFields: toBool(take(rest, "isquickfindfields")),
    overrideQuickFindRecordLimitEnabled: toBool(
      take(rest, "overridequickfindrecordlimitenabled")
    ),
    hint: take(rest, "hint"),
    unknownAttributes: leftovers(rest)
  };
}
function parseConditionValue(element) {
  const rest = { ...element.attributes };
  return {
    value: element.text,
    uiName: take(rest, "uiname"),
    uiType: take(rest, "uitype"),
    unknownAttributes: leftovers(rest)
  };
}
function parseCondition(element) {
  const rest = { ...element.attributes };
  const rawOperator = take(rest, "operator");
  const operator = rawOperator === void 0 ? void 0 : conditionOperatorFromFetchXml(rawOperator);
  const aggregate = aggregateTypeFromFetchXml(take(rest, "aggregate"));
  const valueElements = element.children.filter((child) => child.name === "value");
  return {
    id: createNodeId(),
    attribute: take(rest, "attribute"),
    operator: operator ?? ConditionOperator.Equal,
    entityName: take(rest, "entityname"),
    value: take(rest, "value"),
    values: valueElements.length > 0 ? valueElements.map(parseConditionValue) : void 0,
    valueOfColumn: take(rest, "valueof"),
    alias: take(rest, "alias"),
    aggregate: aggregate === AggregateType.None ? void 0 : aggregate,
    uiName: take(rest, "uiname"),
    uiType: take(rest, "uitype"),
    // An operator string this build doesn't recognize is preserved verbatim rather
    // than silently rewritten to `eq`, which would change what the query matches.
    unknownAttributes: leftovers(
      rawOperator !== void 0 && operator === void 0 ? { ...rest, operator: rawOperator } : rest
    )
  };
}
function put(attributes, name, value) {
  if (value === void 0) return;
  if (typeof value === "string" && value === "") return;
  attributes[name] = typeof value === "boolean" ? String(value) : String(value);
}
function toFetchXml(query, options) {
  const attributes = {};
  put(attributes, "version", query.version);
  put(attributes, "output-format", query.outputFormat);
  put(attributes, "mapping", query.mapping);
  put(attributes, "top", query.top);
  put(attributes, "count", query.count);
  put(attributes, "page", query.page);
  put(attributes, "paging-cookie", query.pagingCookie);
  put(attributes, "aggregate", query.aggregate);
  put(attributes, "aggregatelimit", query.aggregateLimit);
  put(attributes, "distinct", query.distinct);
  put(attributes, "no-lock", query.noLock);
  put(attributes, "returntotalrecordcount", query.returnTotalRecordCount);
  put(attributes, "latematerialize", query.lateMaterialize);
  put(attributes, "min-active-row-version", query.minActiveRowVersion);
  put(attributes, "useraworderby", query.useRawOrderBy);
  put(attributes, "savedqueryid", query.savedQueryId);
  put(attributes, "utc-offset", query.utcOffset);
  put(attributes, "datasource", query.dataSource);
  put(attributes, "options", query.options);
  Object.assign(attributes, query.unknownAttributes ?? {});
  const root = {
    name: "fetch",
    attributes,
    children: [writeEntity(query.entity, "entity"), ...query.unknownElements ?? []],
    text: ""
  };
  const body = serializeXml(root, options);
  return options?.includeXmlDeclaration === true ? `<?xml version="1.0" encoding="utf-8"?>${options.indent === "" ? "" : options.newLine ?? "\n"}${body}` : body;
}
function writeEntity(entity, tagName) {
  const attributes = {};
  put(attributes, "name", entity.name);
  if (tagName === "link-entity") {
    const link = entity;
    put(attributes, "from", link.from);
    put(attributes, "to", link.to);
    put(attributes, "alias", link.alias);
    put(
      attributes,
      "link-type",
      link.linkType === void 0 ? void 0 : joinOperatorToFetchXml(link.linkType)
    );
    put(attributes, "intersect", link.intersect);
    put(attributes, "visible", link.visible);
  }
  put(attributes, "enableprefiltering", entity.enablePrefiltering);
  put(attributes, "prefilterparametername", entity.prefilterParameterName);
  Object.assign(attributes, entity.unknownAttributes ?? {});
  const children = [];
  if (entity.allAttributes === true) {
    children.push({ name: "all-attributes", attributes: {}, children: [], text: "" });
  }
  for (const attribute of entity.attributes) children.push(writeAttribute(attribute));
  for (const order of entity.orders) children.push(writeOrder(order));
  for (const filter of entity.filters) children.push(writeFilter(filter));
  for (const link of entity.linkEntities) children.push(writeEntity(link, "link-entity"));
  children.push(...entity.unknownElements ?? []);
  return { name: tagName, attributes, children, text: "" };
}
function writeAttribute(attribute) {
  const attributes = {};
  put(attributes, "name", attribute.name);
  put(attributes, "alias", attribute.alias);
  put(
    attributes,
    "aggregate",
    attribute.aggregate === void 0 ? void 0 : aggregateTypeToFetchXml(attribute.aggregate)
  );
  put(attributes, "groupby", attribute.groupBy);
  put(
    attributes,
    "dategrouping",
    attribute.dateGrouping === void 0 ? void 0 : dateGroupingToFetchXml(attribute.dateGrouping)
  );
  put(attributes, "distinct", attribute.distinct);
  put(attributes, "usertimezone", attribute.userTimeZone);
  Object.assign(attributes, attribute.unknownAttributes ?? {});
  return { name: "attribute", attributes, children: [], text: "" };
}
function writeOrder(order) {
  const attributes = {};
  put(attributes, "attribute", order.attribute);
  put(attributes, "alias", order.alias);
  put(attributes, "entityname", order.entityName);
  put(attributes, "descending", order.descending);
  Object.assign(attributes, order.unknownAttributes ?? {});
  return { name: "order", attributes, children: [], text: "" };
}
function writeFilter(filter) {
  const attributes = {};
  put(attributes, "type", logicalOperatorToFetchXml(filter.type));
  put(attributes, "hint", filter.hint);
  put(attributes, "isquickfindfields", filter.isQuickFindFields);
  put(
    attributes,
    "overridequickfindrecordlimitenabled",
    filter.overrideQuickFindRecordLimitEnabled
  );
  Object.assign(attributes, filter.unknownAttributes ?? {});
  return {
    name: "filter",
    attributes,
    children: [
      ...filter.conditions.map(writeCondition),
      ...filter.filters.map(writeFilter),
      ...(filter.linkEntities ?? []).map((link) => writeEntity(link, "link-entity")),
      ...filter.unknownElements ?? []
    ],
    text: ""
  };
}
function writeCondition(condition2) {
  const attributes = {};
  put(attributes, "entityname", condition2.entityName);
  put(attributes, "attribute", condition2.attribute);
  put(attributes, "alias", condition2.alias);
  put(
    attributes,
    "aggregate",
    condition2.aggregate === void 0 ? void 0 : aggregateTypeToFetchXml(condition2.aggregate)
  );
  if (condition2.unknownAttributes?.["operator"] === void 0) {
    put(attributes, "operator", conditionOperatorToFetchXml(condition2.operator));
  }
  const hasValues = condition2.values !== void 0 && condition2.values.length > 0;
  if (!hasValues) put(attributes, "value", condition2.value);
  put(attributes, "valueof", condition2.valueOfColumn);
  put(attributes, "uitype", condition2.uiType);
  put(attributes, "uiname", condition2.uiName);
  Object.assign(attributes, condition2.unknownAttributes ?? {});
  const children = hasValues ? (condition2.values ?? []).map((entry) => {
    const valueAttributes = {};
    put(valueAttributes, "uitype", entry.uiType);
    put(valueAttributes, "uiname", entry.uiName);
    Object.assign(valueAttributes, entry.unknownAttributes ?? {});
    return { name: "value", attributes: valueAttributes, children: [], text: entry.value };
  }) : [];
  return { name: "condition", attributes, children, text: "" };
}

// src/fetchxml/invert.ts
var OPPOSITE_PAIRS = [
  [ConditionOperator.Equal, ConditionOperator.NotEqual],
  [ConditionOperator.GreaterThan, ConditionOperator.LessEqual],
  [ConditionOperator.LessThan, ConditionOperator.GreaterEqual],
  [ConditionOperator.Like, ConditionOperator.NotLike],
  [ConditionOperator.In, ConditionOperator.NotIn],
  [ConditionOperator.Between, ConditionOperator.NotBetween],
  [ConditionOperator.Null, ConditionOperator.NotNull],
  [ConditionOperator.Contains, ConditionOperator.DoesNotContain],
  [ConditionOperator.BeginsWith, ConditionOperator.DoesNotBeginWith],
  [ConditionOperator.EndsWith, ConditionOperator.DoesNotEndWith],
  [ConditionOperator.On, ConditionOperator.NotOn],
  [ConditionOperator.EqualUserId, ConditionOperator.NotEqualUserId],
  [ConditionOperator.EqualBusinessId, ConditionOperator.NotEqualBusinessId],
  [ConditionOperator.Mask, ConditionOperator.NotMask],
  [ConditionOperator.Under, ConditionOperator.NotUnder],
  [ConditionOperator.ContainValues, ConditionOperator.DoesNotContainValues]
];
var OPPOSITES = new Map([
  ...OPPOSITE_PAIRS,
  ...OPPOSITE_PAIRS.map(([left, right]) => [right, left])
]);
function oppositeConditionOperator(operator) {
  return OPPOSITES.get(operator);
}
function canInvertFilter(filter) {
  if (filter.conditions.some((condition2) => !OPPOSITES.has(condition2.operator))) return false;
  if (!filter.filters.every(canInvertFilter)) return false;
  return (filter.linkEntities ?? []).length === 0;
}
function invertFilter(filter) {
  if (!canInvertFilter(filter)) return filter;
  return {
    ...filter,
    type: filter.type === LogicalOperator.And ? LogicalOperator.Or : LogicalOperator.And,
    conditions: filter.conditions.map(invertCondition),
    filters: filter.filters.map(invertFilter)
  };
}
function invertCondition(condition2) {
  const opposite = OPPOSITES.get(condition2.operator);
  return opposite === void 0 ? condition2 : { ...condition2, operator: opposite };
}

// src/fetchxml/mutations.ts
function mapList(items, transform) {
  let changed = false;
  const next = [];
  for (const item of items) {
    const result = transform(item);
    if (result === null) {
      changed = true;
      continue;
    }
    if (result !== item) changed = true;
    next.push(result);
  }
  return changed ? next : items;
}
function transformFilter(filter, transforms) {
  const conditions = transforms.condition ? mapList(filter.conditions, transforms.condition) : filter.conditions;
  const filters = mapList(filter.filters, (nested) => transformFilter(nested, transforms));
  const links = filter.linkEntities;
  const linkEntities = links === void 0 ? void 0 : mapList(links, (link) => transformEntity(link, transforms, true));
  const unchanged = conditions === filter.conditions && filters === filter.filters && linkEntities === links;
  const rewritten = unchanged ? filter : {
    ...filter,
    conditions,
    filters,
    // Back to absent once the last one goes, so removing a join leaves the
    // filter serializing exactly as one that never had one.
    ...linkEntities === void 0 || linkEntities.length > 0 ? { linkEntities } : { linkEntities: void 0 }
  };
  return transforms.filter ? transforms.filter(rewritten) : rewritten;
}
function transformEntity(entity, transforms, isLink) {
  const attributes = transforms.attribute ? mapList(entity.attributes, transforms.attribute) : entity.attributes;
  const orders = transforms.order ? mapList(entity.orders, transforms.order) : entity.orders;
  const filters = mapList(entity.filters, (filter) => transformFilter(filter, transforms));
  const linkEntities = mapList(
    entity.linkEntities,
    (link) => transformEntity(link, transforms, true)
  );
  const unchanged = attributes === entity.attributes && orders === entity.orders && filters === entity.filters && linkEntities === entity.linkEntities;
  let rewritten = unchanged ? entity : { ...entity, attributes, orders, filters, linkEntities };
  if (transforms.entity) {
    const result = transforms.entity(rewritten);
    if (result === null) return null;
    rewritten = result;
  }
  if (isLink && transforms.linkEntity) {
    const result = transforms.linkEntity(rewritten);
    if (result === null) return null;
    rewritten = result;
  }
  return rewritten;
}
function transformQuery(query, transforms) {
  const entity = transformEntity(query.entity, transforms, false);
  if (entity === null || entity === query.entity) return query;
  return { ...query, entity };
}
function updateQuery(query, patch) {
  return { ...query, ...patch };
}
function updateEntity(query, id, patch) {
  return transformQuery(query, {
    entity: (entity) => entity.id === id ? { ...entity, ...patch } : entity
  });
}
function updateAttribute(query, id, patch) {
  return transformQuery(query, {
    attribute: (attribute) => attribute.id === id ? { ...attribute, ...patch } : attribute
  });
}
function updateOrder(query, id, patch) {
  return transformQuery(query, {
    order: (order) => order.id === id ? { ...order, ...patch } : order
  });
}
function updateFilter(query, id, patch) {
  return transformQuery(query, {
    filter: (filter) => filter.id === id ? { ...filter, ...patch } : filter
  });
}
function updateCondition(query, id, patch) {
  return transformQuery(query, {
    condition: (condition2) => condition2.id === id ? { ...condition2, ...patch } : condition2
  });
}
function addAttribute(query, entityId, attribute) {
  return transformQuery(query, {
    entity: (entity) => entity.id === entityId ? { ...entity, attributes: [...entity.attributes, attribute] } : entity
  });
}
function addOrder(query, entityId, order) {
  return transformQuery(query, {
    entity: (entity) => entity.id === entityId ? { ...entity, orders: [...entity.orders, order] } : entity
  });
}
function addLinkEntity(query, parentId, link) {
  return transformQuery(query, {
    entity: (entity) => entity.id === parentId ? { ...entity, linkEntities: [...entity.linkEntities, link] } : entity,
    filter: (filter) => filter.id === parentId ? { ...filter, linkEntities: [...filter.linkEntities ?? [], link] } : filter
  });
}
function addFilter(query, parentId, filter) {
  return transformQuery(query, {
    entity: (entity) => entity.id === parentId ? { ...entity, filters: [...entity.filters, filter] } : entity,
    filter: (existing) => existing.id === parentId ? { ...existing, filters: [...existing.filters, filter] } : existing
  });
}
function addCondition(query, filterId, condition2) {
  return transformQuery(query, {
    filter: (filter) => filter.id === filterId ? { ...filter, conditions: [...filter.conditions, condition2] } : filter
  });
}
function removeNode(query, id) {
  const matches = (node) => node.id === id ? null : node;
  return transformQuery(query, {
    linkEntity: matches,
    attribute: matches,
    order: matches,
    filter: matches,
    condition: matches
  });
}
function moveItem(items, from, to) {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) {
    return items;
  }
  const next = [...items];
  const [moved] = next.splice(from, 1);
  if (moved !== void 0) next.splice(to, 0, moved);
  return next;
}
function moveOrder(query, entityId, from, to) {
  return transformQuery(query, {
    entity: (entity) => {
      if (entity.id !== entityId) return entity;
      const orders = moveItem(entity.orders, from, to);
      return orders === entity.orders ? entity : { ...entity, orders };
    }
  });
}
function getAllEntities(query) {
  const result = [];
  const visit = (entity) => {
    result.push(entity);
    for (const link of childLinks(entity)) visit(link);
  };
  visit(query.entity);
  return result;
}
function childLinks(entity) {
  const result = [...entity.linkEntities];
  const fromFilters = (filter) => {
    result.push(...filter.linkEntities ?? []);
    for (const nested of filter.filters) fromFilters(nested);
  };
  for (const filter of entity.filters) fromFilters(filter);
  return result;
}
function getAllLinkEntities(query) {
  const result = [];
  const visit = (entity) => {
    for (const link of childLinks(entity)) {
      result.push(link);
      visit(link);
    }
  };
  visit(query.entity);
  return result;
}
function hasUnionFilterHint(query) {
  const inFilter = (filter) => filter.hint === UNION_FILTER_HINT || filter.filters.some(inFilter) || (filter.linkEntities ?? []).some(inEntity);
  const inEntity = (entity) => entity.filters.some(inFilter) || entity.linkEntities.some(inEntity);
  return inEntity(query.entity);
}
function getLinkAliases(query) {
  return getAllLinkEntities(query).map((link) => link.alias).filter((alias) => alias !== void 0 && alias !== "");
}
function placementForJoinType(joinType) {
  switch (joinType) {
    case JoinOperator.Any:
    case JoinOperator.NotAny:
    case JoinOperator.All:
    case JoinOperator.NotAll:
      return "filter";
    default:
      return "entity";
  }
}
function joinTypeReturnsColumns(joinType) {
  switch (joinType) {
    case JoinOperator.Exists:
    case JoinOperator.In:
    case JoinOperator.Any:
    case JoinOperator.NotAny:
    case JoinOperator.All:
    case JoinOperator.NotAll:
      return false;
    default:
      return true;
  }
}
function uniqueAlias(tableName, usedAliases) {
  const base = tableName.replace(/[^a-z0-9_]/gi, "") || "link";
  let alias = base;
  let suffix = 1;
  while (usedAliases.includes(alias)) {
    suffix += 1;
    alias = `${base}${suffix}`;
  }
  return alias;
}
function findLinkContext(query, linkId) {
  let found;
  const visitFilter = (filter, owner) => {
    for (const link of filter.linkEntities ?? []) {
      if (link.id === linkId) {
        found = {
          link,
          placement: "filter",
          ownerEntityId: owner.id,
          ownerFilterId: filter.id
        };
      }
      visitEntity(link);
    }
    for (const nested of filter.filters) visitFilter(nested, owner);
  };
  const visitEntity = (entity) => {
    for (const link of entity.linkEntities) {
      if (link.id === linkId) {
        found = {
          link,
          placement: "entity",
          ownerEntityId: entity.id,
          // The group the editor already draws it inside, so moving it in is not
          // also a move on screen.
          ownerFilterId: entity.filters[0]?.id
        };
      }
      visitEntity(link);
    }
    for (const filter of entity.filters) visitFilter(filter, entity);
  };
  visitEntity(query.entity);
  return found;
}
function setLinkJoinType(query, linkId, joinType) {
  const context = findLinkContext(query, linkId);
  if (context === void 0) return query;
  const alias = aliasFor(query, context.link, joinType);
  const target = placementForJoinType(joinType);
  if (target === context.placement) {
    return updateEntity(query, linkId, { linkType: joinType, alias });
  }
  const moved = { ...context.link, linkType: joinType, alias };
  const without2 = removeNode(query, linkId);
  if (target === "entity") {
    return addLinkEntity(without2, context.ownerEntityId, moved);
  }
  if (context.ownerFilterId !== void 0) {
    return addLinkEntity(without2, context.ownerFilterId, moved);
  }
  return addFilter(without2, context.ownerEntityId, {
    ...createFilter(LogicalOperator.And),
    linkEntities: [moved]
  });
}
function aliasFor(query, link, joinType) {
  if (!joinTypeReturnsColumns(joinType)) return void 0;
  if ((link.alias ?? "").trim() !== "") return link.alias;
  const taken = getLinkAliases(query).filter((existing) => existing !== link.alias);
  return uniqueAlias(link.name, taken);
}
function invertFilterConditions(query, filterId) {
  return transformQuery(query, {
    filter: (filter) => filter.id === filterId ? invertFilter(filter) : filter
  });
}
function renameLinkAlias(query, linkId, alias) {
  const link = getAllLinkEntities(query).find((candidate) => candidate.id === linkId);
  if (link === void 0) return query;
  const next = (alias ?? "").trim();
  const previous = (link.alias ?? "").trim();
  const renamed = transformQuery(query, {
    linkEntity: (candidate) => candidate.id === linkId ? { ...candidate, alias: next === "" ? void 0 : next } : candidate
  });
  if (next === "" || previous === "" || previous === next) return renamed;
  const prefix = `${previous}.`;
  return transformQuery(renamed, {
    order: (order) => (order.entityName ?? "") === previous ? { ...order, entityName: next } : order,
    condition: (condition2) => {
      const entityName = (condition2.entityName ?? "") === previous ? next : condition2.entityName;
      const valueOfColumn = condition2.valueOfColumn?.startsWith(prefix) ? `${next}.${condition2.valueOfColumn.slice(prefix.length)}` : condition2.valueOfColumn;
      return entityName === condition2.entityName && valueOfColumn === condition2.valueOfColumn ? condition2 : { ...condition2, entityName, valueOfColumn };
    }
  });
}

// src/fetchxml/find-columns.ts
function findColumnsFilter(entity) {
  return findIn(entity.filters);
}
function findIn(filters) {
  for (const filter of filters) {
    if (filter.isQuickFindFields === true) return filter;
    const nested = findIn(filter.filters);
    if (nested) return nested;
  }
  return void 0;
}
function findColumns(entity) {
  const filter = findColumnsFilter(entity);
  if (!filter) return [];
  const seen = /* @__PURE__ */ new Set();
  const columns = [];
  for (const condition2 of filter.conditions) {
    const column = condition2.attribute?.trim();
    if (!column || condition2.entityName) continue;
    const key = column.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    columns.push(column);
  }
  return columns;
}
function setFindColumns(query, columns) {
  const seen = /* @__PURE__ */ new Set();
  const wanted = [];
  for (const raw of columns) {
    const column = raw.trim();
    if (!column || seen.has(column.toLowerCase())) continue;
    seen.add(column.toLowerCase());
    wanted.push(column);
  }
  const entity = query.entity;
  const existing = findColumnsFilter(entity);
  if (wanted.length === 0) {
    if (!existing) return query;
    return { ...query, entity: { ...entity, filters: without(entity.filters, existing.id) } };
  }
  const conditions = wanted.map(
    (attribute) => createCondition({ attribute, operator: ConditionOperator.Like, value: "{0}" })
  );
  if (existing) {
    const replaced = {
      ...existing,
      conditions,
      filters: [],
      linkEntities: void 0,
      unknownElements: void 0
    };
    return { ...query, entity: { ...entity, filters: replacing(entity.filters, replaced) } };
  }
  const added = { ...createFilter(LogicalOperator.Or), isQuickFindFields: true, conditions };
  const [first, ...rest] = entity.filters;
  const filters = first?.type === LogicalOperator.And ? [{ ...first, filters: [...first.filters, added] }, ...rest] : [...entity.filters, added];
  return { ...query, entity: { ...entity, filters } };
}
function without(filters, id) {
  const result = [];
  for (const filter of filters) {
    if (filter.id === id) continue;
    const nested = without(filter.filters, id);
    if (nested.length === filter.filters.length && nested.every((x, i) => x === filter.filters[i])) {
      result.push(filter);
      continue;
    }
    const rewritten = { ...filter, filters: nested };
    if (!isEmpty(rewritten)) result.push(rewritten);
  }
  return result;
}
function replacing(filters, replacement) {
  return filters.map((filter) => {
    if (filter.id === replacement.id) return replacement;
    const nested = replacing(filter.filters, replacement);
    return nested.every((x, i) => x === filter.filters[i]) ? filter : { ...filter, filters: nested };
  });
}
function isEmpty(filter) {
  return filter.conditions.length === 0 && filter.filters.length === 0 && (filter.linkEntities?.length ?? 0) === 0 && (filter.unknownElements?.length ?? 0) === 0;
}

// src/fetchxml/grid-criteria.ts
function sameGridCriteria(left, right) {
  try {
    return toFetchXml(parseFetchXml(left)) === toFetchXml(parseFetchXml(right));
  } catch {
    return false;
  }
}
function extractGridCriteria(fetchXml) {
  const { entity } = parseFetchXml(fetchXml);
  const namedAliases = namedAliasesOf(entity);
  return toFetchXml({
    entity: {
      id: entity.id,
      name: entity.name,
      attributes: [],
      orders: [],
      filters: entity.filters.map(withoutProjection),
      linkEntities: entity.linkEntities.map((link) => criteriaOf(link, namedAliases)).filter((link) => link !== null)
    }
  });
}
function criteriaOf(link, namedAliases) {
  const criteria = {
    ...link,
    allAttributes: void 0,
    attributes: [],
    orders: [],
    filters: link.filters.map(withoutProjection),
    linkEntities: link.linkEntities.map((nested) => criteriaOf(nested, namedAliases)).filter((nested) => nested !== null)
  };
  const isOuter = link.linkType === JoinOperator.LeftOuter;
  const tests = !isOuter || criteria.filters.length > 0 || criteria.linkEntities.length > 0 || // Named by its alias — or, for a join with none, by its table's name.
  namedAliases.has((link.alias || link.name).toLowerCase());
  return tests ? criteria : null;
}
function withoutProjection(filter) {
  return {
    ...filter,
    filters: filter.filters.map(withoutProjection),
    linkEntities: filter.linkEntities?.map(linkWithoutProjection)
  };
}
function linkWithoutProjection(link) {
  return {
    ...link,
    allAttributes: void 0,
    attributes: [],
    orders: [],
    filters: link.filters.map(withoutProjection),
    linkEntities: link.linkEntities.map(linkWithoutProjection)
  };
}
function namedAliasesOf(entity) {
  const aliases = /* @__PURE__ */ new Set();
  const visitFilter = (filter) => {
    for (const condition2 of filter.conditions) {
      if (condition2.entityName) aliases.add(condition2.entityName.toLowerCase());
      const dot = condition2.valueOfColumn?.indexOf(".") ?? -1;
      if (dot > 0) aliases.add(condition2.valueOfColumn.slice(0, dot).toLowerCase());
    }
    filter.filters.forEach(visitFilter);
    filter.linkEntities?.forEach(visitEntity);
  };
  const visitEntity = (scope) => {
    scope.filters.forEach(visitFilter);
    scope.linkEntities.forEach(visitEntity);
  };
  visitEntity(entity);
  return aliases;
}
var FROM_CRITERIA = "ppp-from-criteria";
function applyGridCriteria(fetchXml, criteria) {
  const query = parseXml(fetchXml);
  const target = entityOf(query);
  const source = entityOf(parseXml(criteria));
  if (!target || !source) throw new Error("The FetchXML names no table.");
  if (!sameText(source.attributes["name"], target.attributes["name"])) {
    throw new Error(
      `The criteria are for the table \`${source.attributes["name"]}\`, not the query's \`${target.attributes["name"]}\`.`
    );
  }
  removeProjection(source);
  for (const link of descendants(source)) {
    if (link.name === "link-entity") link.attributes[FROM_CRITERIA] = "true";
  }
  merge(target, source);
  renameDuplicateAliases(target);
  for (const link of descendants(target)) delete link.attributes[FROM_CRITERIA];
  return serializeXml(query, { indent: "" });
}
function entityOf(root) {
  const entity = root.children.find((x) => x.name === "entity");
  return entity?.attributes["name"] ? entity : void 0;
}
function merge(target, source) {
  target.children = target.children.filter((x) => x.name !== "filter");
  if (source) target.children.push(...source.children.filter((x) => x.name === "filter").map(clone));
  const unmatched = source ? source.children.filter((x) => x.name === "link-entity") : [];
  for (const link of target.children.filter((x) => x.name === "link-entity")) {
    const match = unmatched.find((x) => isSameJoin(x, link));
    if (match) {
      unmatched.splice(unmatched.indexOf(match), 1);
      setAttribute(link, "link-type", match.attributes["link-type"]);
      merge(link, match);
    } else if (showsColumns(link)) {
      link.attributes["link-type"] = "outer";
      merge(link, null);
    } else {
      target.children.splice(target.children.indexOf(link), 1);
    }
  }
  target.children.push(...unmatched.map(clone));
}
function isSameJoin(left, right) {
  return ["name", "from", "to", "alias"].every(
    (attribute) => sameText(left.attributes[attribute] ?? "", right.attributes[attribute] ?? "")
  );
}
function showsColumns(link) {
  return descendants(link).some((x) => x.name === "attribute" || x.name === "all-attributes");
}
function removeProjection(element) {
  element.children = element.children.filter((x) => !["attribute", "all-attributes", "order"].includes(x.name));
  element.children.forEach(removeProjection);
}
function renameDuplicateAliases(entity) {
  const links = descendants(entity).filter((x) => x.name === "link-entity");
  const taken = new Set(links.map((x) => x.attributes["alias"]?.toLowerCase()).filter((x) => !!x));
  const duplicates = links.filter(
    (link) => link.attributes[FROM_CRITERIA] && link.attributes["alias"] && links.some((other) => other !== link && !other.attributes[FROM_CRITERIA] && sameText(other.attributes["alias"], link.attributes["alias"]))
  );
  for (const link of duplicates) {
    const alias = link.attributes["alias"];
    let renamed = alias;
    for (let i = 2; taken.has(renamed.toLowerCase()); i++) renamed = `${alias}${i}`;
    taken.add(renamed.toLowerCase());
    link.attributes["alias"] = renamed;
    for (const condition2 of descendants(entity).filter((x) => x.name === "condition")) {
      if (sameText(condition2.attributes["entityname"], alias)) condition2.attributes["entityname"] = renamed;
      const valueOf2 = condition2.attributes["valueof"];
      if (valueOf2 && valueOf2.toLowerCase().startsWith(`${alias.toLowerCase()}.`)) {
        condition2.attributes["valueof"] = renamed + valueOf2.slice(alias.length);
      }
    }
  }
}
function descendants(element) {
  return element.children.flatMap((child) => [child, ...descendants(child)]);
}
function clone(element) {
  return { ...element, attributes: { ...element.attributes }, children: element.children.map(clone) };
}
function setAttribute(element, name, value) {
  if (value === void 0) delete element.attributes[name];
  else element.attributes[name] = value;
}
function sameText(left, right) {
  return (left ?? "").toLowerCase() === (right ?? "").toLowerCase();
}

// src/fetchxml/grid-column-filter.ts
function findGridColumnFilter(criteria, viewFetchXml, column) {
  const entity = readEntity(criteria);
  const { alias, name } = splitGridColumn(column);
  if (alias !== void 0 && !joinsAgree(entity, readEntity(viewFetchXml), alias)) return { kind: "other" };
  const conditions = conditionsOn(entity, alias, name);
  if (conditions.length === 0) return { kind: "none" };
  return conditions.length === 1 && isInTopLevelFilter(conditions[0]) ? { kind: "single", condition: serializeXml(conditions[0].condition, { indent: "" }) } : { kind: "other" };
}
function gridFilteredColumns(criteria) {
  const columns = /* @__PURE__ */ new Set();
  for (const { condition: condition2, alias } of conditionsUnder(readEntity(criteria), void 0, [])) {
    const name = condition2.attributes["attribute"];
    if (name) columns.add((alias === void 0 ? name : `${alias}.${name}`).toLowerCase());
  }
  return columns;
}
function setGridColumnFilter(criteria, viewFetchXml, column, condition2) {
  const root = parseXml(criteria);
  const entity = entityOf2(root);
  const view = readEntity(viewFetchXml);
  const { alias, name } = splitGridColumn(column);
  const existing = conditionsOn(entity, alias, name);
  if (existing.length > 1 || existing.length === 1 && !isInTopLevelFilter(existing[0]) || alias !== void 0 && !joinsAgree(entity, view, alias)) {
    throw new Error(`The column \`${column}\` is filtered in a way that can't be changed on its own.`);
  }
  let replacement;
  if (condition2 !== void 0) {
    replacement = parseXml(condition2);
    if (replacement.name !== "condition") throw new Error("A condition is a <condition> element.");
    replacement.attributes["attribute"] = name;
    if (alias === void 0) delete replacement.attributes["entityname"];
    else replacement.attributes["entityname"] = alias;
  }
  const found = existing[0];
  if (found) {
    const siblings = found.parent.children;
    if (replacement) {
      siblings.splice(siblings.indexOf(found.condition), 1, replacement);
    } else {
      siblings.splice(siblings.indexOf(found.condition), 1);
      if (siblings.length === 0) entity.children.splice(entity.children.indexOf(found.parent), 1);
    }
  } else if (replacement) {
    topLevelFilter(entity).children.push(replacement);
  }
  if (alias !== void 0) {
    if (replacement) addJoinPath(entity, view, alias);
    else removeUnusedJoinPath(entity, alias);
  }
  return serializeXml(root);
}
function defaultGridColumnFilterOperator(columnType) {
  const preferred = columnType === ColumnType.DateTime ? ConditionOperator.On : columnType === ColumnType.MultiSelectChoice ? ConditionOperator.ContainValues : ConditionOperator.Equal;
  const offered = getConditionOperatorsForColumnType(columnType);
  return offered.some((info) => info.operator === preferred) ? preferred : offered[0].operator;
}
function readEntity(fetchXml) {
  return entityOf2(parseXml(fetchXml));
}
function entityOf2(root) {
  const entity = root.children.find((x) => x.name === "entity");
  if (!entity) throw new Error("The FetchXML names no table.");
  return entity;
}
function splitGridColumn(column) {
  if (column.trim() === "") throw new Error("A column is named.");
  const dot = column.indexOf(".");
  return dot > 0 ? { alias: column.slice(0, dot), name: column.slice(dot + 1) } : { alias: void 0, name: column };
}
function gridColumnViewJoin(viewFetchXml, column) {
  const { alias } = splitGridColumn(column);
  return alias === void 0 ? void 0 : joinPath(readEntity(viewFetchXml), alias)?.at(-1);
}
function joinsAgree(criteria, view, alias) {
  const viewPath = joinPath(view, alias);
  if (!viewPath) return true;
  const criteriaPath = joinPath(criteria, alias);
  return !criteriaPath || criteriaPath.length === viewPath.length && criteriaPath.every((join, i) => isSameJoin2(join, viewPath[i]));
}
function conditionsOn(entity, alias, name) {
  return conditionsUnder(entity, void 0, []).filter(
    (found) => sameText2(found.alias, alias) && sameText2(found.condition.attributes["attribute"], name)
  );
}
function conditionsUnder(scope, scopeAlias, ancestors) {
  const path = [...ancestors, scope];
  return scope.children.flatMap((element) => {
    switch (element.name) {
      case "condition":
        return [
          {
            condition: element,
            alias: element.attributes["entityname"] || scopeAlias,
            parent: scope,
            ancestors: path
          }
        ];
      case "filter":
        return conditionsUnder(element, scopeAlias, path);
      case "link-entity":
        return conditionsUnder(element, nameOf(element), path);
      default:
        return [];
    }
  });
}
function nameOf(link) {
  return link.attributes["alias"] || link.attributes["name"];
}
function isInTopLevelFilter(found) {
  return found.parent.name === "filter" && found.ancestors.length === 2 && isAnd(found.parent);
}
function isAnd(filter) {
  const type = filter.attributes["type"];
  return type === void 0 || type === "and";
}
function topLevelFilter(entity) {
  const filters = entity.children.filter((x) => x.name === "filter");
  if (filters.length === 1 && isAnd(filters[0])) return filters[0];
  const filter = { name: "filter", attributes: { type: "and" }, children: filters, text: "" };
  entity.children = [...entity.children.filter((x) => x.name !== "filter"), filter];
  return filter;
}
function addJoinPath(entity, view, alias) {
  const path = joinPath(view, alias);
  if (!path) throw new Error(`The view has no join \`${alias}\`.`);
  let scope = entity;
  for (const join of path) {
    let match = scope.children.find((x) => x.name === "link-entity" && isSameJoin2(x, join));
    if (!match) {
      match = { name: join.name, attributes: { ...join.attributes, "link-type": "outer" }, children: [], text: "" };
      scope.children.push(match);
    }
    scope = match;
  }
}
function removeUnusedJoinPath(entity, alias) {
  const path = joinPath(entity, alias);
  if (!path) return;
  const named = new Set(
    conditionsUnder(entity, void 0, []).flatMap(({ condition: condition2 }) => {
      const names = [condition2.attributes["entityname"]];
      const valueOf2 = condition2.attributes["valueof"];
      const dot = valueOf2?.indexOf(".") ?? -1;
      if (dot > 0) names.push(valueOf2.slice(0, dot));
      return names.filter((x) => !!x).map((x) => x.toLowerCase());
    })
  );
  for (let i = path.length - 1; i >= 0; i--) {
    const join = path[i];
    const outer = join.attributes["link-type"] === "outer";
    if (join.children.length > 0 || !outer || named.has((nameOf(join) ?? "").toLowerCase())) return;
    const parent = i === 0 ? entity : path[i - 1];
    parent.children.splice(parent.children.indexOf(join), 1);
  }
}
function joinPath(scope, alias) {
  for (const join of scope.children.filter((x) => x.name === "link-entity")) {
    if (sameText2(join.attributes["alias"], alias)) return [join];
    const rest = joinPath(join, alias);
    if (rest) return [join, ...rest];
  }
  return void 0;
}
function isSameJoin2(left, right) {
  return ["name", "from", "to", "alias"].every(
    (attribute) => sameText2(left.attributes[attribute] ?? "", right.attributes[attribute] ?? "")
  );
}
function sameText2(left, right) {
  return left === void 0 || right === void 0 ? left === right : left.toLowerCase() === right.toLowerCase();
}

// src/fetchxml/link-no-data.ts
function isNoDataCondition(condition2, alias, primaryIdColumn) {
  return condition2.operator === ConditionOperator.Null && (condition2.entityName ?? "").trim() === alias && condition2.attribute === primaryIdColumn;
}
function hasNoDataFilter(query, link, primaryIdColumn) {
  const alias = (link.alias ?? "").trim();
  if (alias === "" || primaryIdColumn === void 0 || primaryIdColumn === "") return false;
  if ((link.linkType ?? JoinOperator.Inner) !== JoinOperator.LeftOuter) return false;
  return findConditions(query).some(
    (condition2) => isNoDataCondition(condition2, alias, primaryIdColumn)
  );
}
function setNoDataFilter(query, linkId, primaryIdColumn) {
  const link = getAllLinkEntities(query).find((candidate) => candidate.id === linkId);
  if (link === void 0 || primaryIdColumn === "") return query;
  const alias = (link.alias ?? "").trim() === "" ? uniqueAlias(link.name, getAliases(query)) : (link.alias ?? "").trim();
  const withLink = transformQuery(query, {
    linkEntity: (candidate) => candidate.id === linkId ? { ...candidate, alias, linkType: JoinOperator.LeftOuter, filters: [] } : candidate
  });
  const marker = createCondition({
    attribute: primaryIdColumn,
    operator: ConditionOperator.Null,
    entityName: alias
  });
  return addToParentFilter(withLink, linkId, marker);
}
function clearNoDataFilter(query, linkId, primaryIdColumn) {
  const link = getAllLinkEntities(query).find((candidate) => candidate.id === linkId);
  const alias = (link?.alias ?? "").trim();
  if (alias === "" || primaryIdColumn === void 0 || primaryIdColumn === "") return query;
  return transformQuery(query, {
    condition: (condition2) => isNoDataCondition(condition2, alias, primaryIdColumn) ? null : condition2,
    filter: (filter) => filter.conditions.length === 0 && filter.filters.length === 0 && (filter.linkEntities ?? []).length === 0 ? null : filter
  });
}
function getAliases(query) {
  return getAllLinkEntities(query).map((link) => (link.alias ?? "").trim()).filter((alias) => alias !== "");
}
function findConditions(query) {
  const found = [];
  transformQuery(query, {
    condition: (condition2) => {
      found.push(condition2);
      return condition2;
    }
  });
  return found;
}
function addToParentFilter(query, linkId, condition2) {
  const owns = (entity) => entity.linkEntities.some((link) => link.id === linkId) || entity.filters.some(function inFilter(filter) {
    return (filter.linkEntities ?? []).some((link) => link.id === linkId) || filter.filters.some(inFilter);
  });
  return transformQuery(query, {
    entity: (entity) => {
      if (!owns(entity)) return entity;
      const [first, ...rest] = entity.filters;
      if (first === void 0) {
        return {
          ...entity,
          filters: [{ ...createFilter(LogicalOperator.And), conditions: [condition2] }]
        };
      }
      if (first.type === LogicalOperator.Or) {
        return {
          ...entity,
          filters: [
            { ...createFilter(LogicalOperator.And), conditions: [condition2], filters: [first] },
            ...rest
          ]
        };
      }
      return {
        ...entity,
        filters: [{ ...first, conditions: [...first.conditions, condition2] }, ...rest]
      };
    }
  });
}

// src/fetchxml/validate.ts
var FetchXmlProblemCode = {
  /** The query never says which table to read from. */
  NoTableName: "no-table-name",
  /** An `operator` string no {@link ConditionOperator} produces. */
  UnknownOperator: "unknown-operator",
  /** A `link-type` string no {@link JoinOperator} produces. */
  UnknownJoinType: "unknown-join-type",
  /** A `<link-entity>` that never says which table it joins to. */
  JoinWithoutTable: "join-without-table",
  /** An element FetchXML does not define, kept but ignored. */
  UnknownElement: "unknown-element",
  /** An attribute FetchXML does not define, kept but ignored. */
  UnknownAttribute: "unknown-attribute",
  /** An `entityname` naming an alias no join in the query carries. */
  UnknownAlias: "unknown-alias",
  /** Two joins sharing one alias, so a reference to it is ambiguous. */
  DuplicateAlias: "duplicate-alias",
  /** A condition that never says which column it tests. */
  ConditionWithoutColumn: "condition-without-column",
  /** A predicate join written outside a `<filter>`, where it has no effect. */
  JoinOutsideFilter: "join-outside-filter",
  /** A projecting join written inside a `<filter>`, which returns no columns. */
  JoinInsideFilter: "join-inside-filter",
  /** A column in an aggregate query that is neither aggregated nor grouped. */
  AggregateColumnNotAggregated: "aggregate-column-not-aggregated",
  /** A column in an aggregate query with no alias to return its value under. */
  AggregateColumnWithoutAlias: "aggregate-column-without-alias",
  /** An aggregated or grouped column in a query that is not an aggregate query. */
  AggregateOutsideAggregateQuery: "aggregate-outside-aggregate-query",
  /** Two projected columns sharing one alias, so one name means two values. */
  DuplicateColumnAlias: "duplicate-column-alias"
};
function hasBlockingProblem(problems) {
  return problems.some((problem) => problem.severity === "error");
}
var KNOWN_OPERATORS = new Set(Object.values(ConditionOperator));
var KNOWN_JOIN_TYPES = new Set(Object.values(JoinOperator));
function validateFetchXml(query) {
  const problems = [];
  const aliasCounts = /* @__PURE__ */ new Map();
  const columnAliasCounts = /* @__PURE__ */ new Map();
  const report = (severity, code, path, ...args) => {
    problems.push({ severity, code, args, path });
  };
  const step = (path, segment) => path === "" ? segment : `${path} > ${segment}`;
  const named = (element, name) => name === void 0 || name === "" ? element : `${element}(${name})`;
  const reportUnknownAttributes = (bag, element, path, skip) => {
    for (const name of Object.keys(bag ?? {})) {
      if (name === skip) continue;
      report("warning", FetchXmlProblemCode.UnknownAttribute, path, name, element);
    }
  };
  const reportUnknownElements = (elements, path) => {
    for (const element of elements ?? []) {
      report("warning", FetchXmlProblemCode.UnknownElement, path, element.name);
    }
  };
  const visitCondition = (condition2, path) => {
    const here = step(path, named("condition", condition2.attribute));
    const rawOperator = condition2.unknownAttributes?.["operator"];
    if (rawOperator !== void 0) {
      report("error", FetchXmlProblemCode.UnknownOperator, here, rawOperator);
    } else if (!KNOWN_OPERATORS.has(condition2.operator)) {
      report("error", FetchXmlProblemCode.UnknownOperator, here, String(condition2.operator));
    }
    if ((condition2.attribute ?? "") === "") {
      report("warning", FetchXmlProblemCode.ConditionWithoutColumn, here);
    }
    const entityName = (condition2.entityName ?? "").trim();
    if (entityName !== "" && !aliasCounts.has(entityName)) {
      report("warning", FetchXmlProblemCode.UnknownAlias, here, entityName);
    }
    reportUnknownAttributes(condition2.unknownAttributes, "condition", here, "operator");
  };
  const visitFilter = (filter, path) => {
    const here = step(path, "filter");
    for (const condition2 of filter.conditions) visitCondition(condition2, here);
    for (const nested of filter.filters) visitFilter(nested, here);
    for (const link of filter.linkEntities ?? []) visitLink(link, here, "filter");
    reportUnknownElements(filter.unknownElements, here);
    reportUnknownAttributes(filter.unknownAttributes, "filter", here);
  };
  const visitLink = (link, path, placement) => {
    const here = step(path, named("link-entity", link.name));
    if (link.name === "") {
      report("error", FetchXmlProblemCode.JoinWithoutTable, here);
    }
    const rawType = link.unknownAttributes?.["link-type"];
    if (rawType !== void 0) {
      report("error", FetchXmlProblemCode.UnknownJoinType, here, rawType);
    } else {
      const joinType = link.linkType ?? JoinOperator.Inner;
      if (!KNOWN_JOIN_TYPES.has(joinType)) {
        report("error", FetchXmlProblemCode.UnknownJoinType, here, String(joinType));
      } else if (placementForJoinType(joinType) !== placement) {
        report(
          "warning",
          placement === "entity" ? FetchXmlProblemCode.JoinOutsideFilter : FetchXmlProblemCode.JoinInsideFilter,
          here
        );
      }
    }
    visitEntityCore(link, here);
    reportUnknownAttributes(link.unknownAttributes, "link-entity", here, "link-type");
  };
  const visitAttribute = (attribute, path) => {
    const here = step(path, named("attribute", attribute.name));
    const aggregated = attribute.aggregate !== void 0 && attribute.aggregate !== AggregateType.None;
    const grouped = attribute.groupBy === true;
    if (query.aggregate === true) {
      if (!aggregated && !grouped) {
        report("warning", FetchXmlProblemCode.AggregateColumnNotAggregated, here);
      }
      if ((attribute.alias ?? "") === "") {
        report("warning", FetchXmlProblemCode.AggregateColumnWithoutAlias, here);
      }
    } else if (aggregated || grouped) {
      report("warning", FetchXmlProblemCode.AggregateOutsideAggregateQuery, here);
    }
  };
  const countColumnAliases = (entity) => {
    for (const attribute of entity.attributes) {
      const alias = (attribute.alias ?? "").trim();
      if (alias !== "") columnAliasCounts.set(alias, (columnAliasCounts.get(alias) ?? 0) + 1);
    }
    const fromFilters = (filter) => [
      ...filter.linkEntities ?? [],
      ...filter.filters.flatMap(fromFilters)
    ];
    for (const link of [...entity.linkEntities, ...entity.filters.flatMap(fromFilters)]) {
      countColumnAliases(link);
    }
  };
  function visitEntityCore(entity, here) {
    for (const attribute of entity.attributes) visitAttribute(attribute, here);
    for (const filter of entity.filters) visitFilter(filter, here);
    for (const link of entity.linkEntities) visitLink(link, here, "entity");
    reportUnknownElements(entity.unknownElements, here);
  }
  const collectAliases = (entity) => {
    const fromFilters = (filter) => [
      ...filter.linkEntities ?? [],
      ...filter.filters.flatMap(fromFilters)
    ];
    for (const link of [...entity.linkEntities, ...entity.filters.flatMap(fromFilters)]) {
      const alias = (link.alias ?? "").trim();
      if (alias !== "") aliasCounts.set(alias, (aliasCounts.get(alias) ?? 0) + 1);
      collectAliases(link);
    }
  };
  collectAliases(query.entity);
  countColumnAliases(query.entity);
  for (const [alias, count] of aliasCounts) {
    if (count > 1) report("warning", FetchXmlProblemCode.DuplicateAlias, "fetch", alias);
  }
  for (const [alias, count] of columnAliasCounts) {
    if (count > 1) report("warning", FetchXmlProblemCode.DuplicateColumnAlias, "fetch", alias);
  }
  const root = step("fetch", named("entity", query.entity.name));
  if (query.entity.name === "") {
    report("error", FetchXmlProblemCode.NoTableName, "fetch");
  }
  visitEntityCore(query.entity, root);
  reportUnknownAttributes(query.entity.unknownAttributes, "entity", root);
  reportUnknownElements(query.unknownElements, "fetch");
  reportUnknownAttributes(query.unknownAttributes, "fetch", "fetch");
  return problems;
}

// src/query-aggregates.ts
var FORMATTED_AGGREGATES = [
  QueryAggregate.Sum,
  QueryAggregate.Average,
  QueryAggregate.Minimum,
  QueryAggregate.Maximum
];
var MAX_AGGREGATE_FORMAT_LENGTH = 32;
function isValidAggregateFormat(format) {
  const trimmed = format?.trim() ?? "";
  if (trimmed.length === 0) return true;
  if (trimmed.length > MAX_AGGREGATE_FORMAT_LENGTH) return false;
  const standard = /^([A-Za-z])(\d*)$/.exec(trimmed);
  if (!standard) return true;
  const precision = standard[2];
  return "CEFGNP".includes(standard[1].toUpperCase()) && precision.length <= 2 && (precision.length === 0 || Number(precision) <= 28);
}
var AGGREGATE_NAMES = [
  ["sum", QueryAggregate.Sum],
  ["average", QueryAggregate.Average],
  ["minimum", QueryAggregate.Minimum],
  ["maximum", QueryAggregate.Maximum],
  ["count", QueryAggregate.Count]
];
function gridAggregateName(aggregate) {
  return AGGREGATE_NAMES.find(([, x]) => x === aggregate)?.[0];
}
function gridAggregateNamed(name) {
  return AGGREGATE_NAMES.find(([x]) => x === name)?.[1];
}

// src/query-grouping.ts
var QUERY_DATE_GROUPINGS = [
  DateGrouping.Minute,
  DateGrouping.Hour,
  DateGrouping.Day,
  DateGrouping.Week,
  DateGrouping.Month,
  DateGrouping.Quarter,
  DateGrouping.Year,
  DateGrouping.FiscalPeriod,
  DateGrouping.FiscalYear
];
var MINUTE_STEPS = [1, 2, 3, 4, 5, 6, 10, 12, 15, 20, 30, 60];
var HOUR_STEPS = [1, 1.5, 2, 3, 4, 6, 8, 12, 24];
function isDateStep(grouping, size) {
  const [minutesPerStep, minutesPerSpan] = grouping === DateGrouping.Minute || grouping === DateGrouping.MinuteOfHour ? [1, 60] : grouping === DateGrouping.Hour || grouping === DateGrouping.HourOfDay ? [60, 24 * 60] : [0, 0];
  if (minutesPerSpan === 0 || !Number.isFinite(size) || size <= 0 || size > minutesPerSpan / minutesPerStep) return false;
  const minutes = Math.round(size * minutesPerStep * 1e9) / 1e9;
  return Number.isInteger(minutes) && minutesPerSpan % minutes === 0;
}
function dateStepsOf(grouping) {
  switch (grouping) {
    case DateGrouping.Minute:
    case DateGrouping.MinuteOfHour:
      return MINUTE_STEPS;
    case DateGrouping.Hour:
    case DateGrouping.HourOfDay:
      return HOUR_STEPS;
    default:
      return [];
  }
}

// src/grid-view-columns.ts
var GridLayout = {
  /**
   * Cards on a narrow viewport — a phone's, at the width the grid's toolbar
   * moves into its overflow menu — and table rows on a wider one.
   */
  Auto: "auto",
  /** One table row per record, under column headers. */
  Table: "table",
  /** One card per record, wrapping to fill the grid's width. */
  Cards: "cards",
  /**
   * The records' cards in a list beside a map of where they are, at their coordinates. Selecting
   * records, or clicking one, brings them into view on the map. Needs `@powerportalspro/react-maps`'
   * `<MapsProvider>`.
   */
  Map: "map",
  /**
   * The map alone, with a pin for every record at its coordinates: the grid loads its records a few
   * hundred at a time until it holds them all, or as many as `mapMaximumRows`. Needs
   * `@powerportalspro/react-maps`' `<MapsProvider>`.
   */
  MapOnly: "map-only"
};
var GridColumnPin = {
  Start: "start",
  End: "end"
};

// src/saved-views.ts
var SavedViewSchema = {
  table: "ppp_savedview",
  id: "ppp_savedviewid",
  name: "ppp_name",
  tableName: "ppp_tablename",
  controlKeys: "ppp_controlkeys",
  fetchXml: "ppp_fetchxml",
  gridLayout: "ppp_gridlayout",
  calendarLayout: "ppp_calendarlayout",
  /**
   * How a Kanban board lays the view out, as `SavedKanbanLayout` JSON. Read and written only for a
   * board, so the other controls work in an environment whose table lacks it.
   */
  kanbanLayout: "ppp_kanbanlayout",
  contact: "ppp_contact",
  owner: "ownerid"
};
var MultiTableViewSchema = {
  table: "ppp_multitableview",
  id: "ppp_multitableviewid",
  name: "ppp_name",
  controlKeys: "ppp_controlkeys",
  calendarLayout: "ppp_calendarlayout",
  timelineLayout: "ppp_timelinelayout",
  /** How a search of several tables lays the view out, as `SavedSearchLayout` JSON. */
  searchLayout: "ppp_searchlayout",
  contact: "ppp_contact",
  owner: "ownerid"
};
var MultiTableViewTableSchema = {
  table: "ppp_multitableviewtable",
  id: "ppp_multitableviewtableid",
  name: "ppp_name",
  view: "ppp_multitableview",
  tableName: "ppp_tablename",
  viewType: "ppp_viewtype",
  viewId: "ppp_viewid",
  fetchXml: "ppp_fetchxml",
  label: "ppp_label",
  sortOrder: "ppp_sortorder",
  overrides: "ppp_overrides",
  contact: "ppp_contact",
  owner: "ownerid"
};
var UserViewSettingSchema = {
  table: "ppp_userviewsetting",
  id: "ppp_userviewsettingid",
  name: "ppp_name",
  tableName: "ppp_tablename",
  controlKey: "ppp_controlkey",
  viewId: "ppp_viewid",
  isDefault: "ppp_isdefault",
  isHidden: "ppp_ishidden",
  contact: "ppp_contact",
  owner: "ownerid"
};
var SavedViewControlKind = {
  Grid: "grid",
  Calendar: "calendar",
  Timeline: "timeline",
  Board: "board",
  /**
   * A search of several tables. A view of several tables saved with no controls is offered on
   * timelines and calendars, not searches, so a view for searches always names this kind.
   */
  Search: "search",
  /** A map. As a search's, a map's views of several tables always name this kind. */
  Map: "map"
};
function namesItself(control) {
  return control.kind === SavedViewControlKind.Search || control.kind === SavedViewControlKind.Map;
}
function isValidControlKey(key) {
  return !!key && key.length <= 100 && /^[A-Za-z0-9._-]+$/.test(key);
}
var isValidKind = (kind) => typeof kind === "string" && /^[a-z]+$/.test(kind);
function savedViewControl(kind, key) {
  if (!isValidKind(kind)) throw new Error(`A control's kind is lower-case letters, not '${kind}'.`);
  const trimmed = key?.trim() || null;
  if (trimmed !== null && !isValidControlKey(trimmed)) {
    throw new Error(`A control's key can only hold letters, digits, '-', '_' and '.', up to 100 of them, not '${trimmed}'.`);
  }
  return { kind, key: trimmed };
}
var everyOfKind = (control) => ({ kind: control.kind, key: null });
function sameControl(a, b) {
  return a.kind === b.kind && (a.key === null || b.key === null ? a.key === b.key : a.key.toLowerCase() === b.key.toLowerCase());
}
function controlToJson(control) {
  return control.key === null ? `{"${control.kind}":null}` : `{"${control.kind}":"${control.key}"}`;
}
function readControl(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return void 0;
  const members = Object.entries(value);
  const member = members[0];
  if (members.length !== 1 || !member) return void 0;
  const [kind, key] = member;
  if (!isValidKind(kind) || !(key === null || typeof key === "string")) return void 0;
  if (typeof key === "string" && key.trim() && !isValidControlKey(key.trim())) return void 0;
  return savedViewControl(kind, key);
}
function parseControl(json) {
  if (!json?.trim()) return void 0;
  try {
    return readControl(JSON.parse(json));
  } catch {
    return void 0;
  }
}
function formatControls(controls) {
  const distinct = controls.filter((x, i) => controls.findIndex((y) => sameControl(x, y)) === i);
  return distinct.length ? `[${distinct.map(controlToJson).join(",")}]` : void 0;
}
function parseControls(json) {
  if (!json?.trim()) return [];
  try {
    const value = JSON.parse(json);
    return Array.isArray(value) ? value.flatMap((x) => readControl(x) ?? []) : [];
  } catch {
    return [];
  }
}
function controlKeysOf(control) {
  return control.key === null && !namesItself(control) ? void 0 : formatControls([control]);
}
var sameView = (a, b) => a.toLowerCase() === b.toLowerCase();
var escapeLike = (value) => value.replace(/_/g, "[_]");
function decidingViewSetting(settings, viewId, control) {
  let kindWide;
  for (const setting of settings) {
    if (!sameView(setting.viewId, viewId) || setting.control.kind !== control.kind) continue;
    if (control.key !== null && sameControl(setting.control, control)) return setting;
    if (setting.control.key === null) kindWide ??= setting;
  }
  return kindWide;
}
function isViewHidden(settings, viewId, control) {
  return decidingViewSetting(settings, viewId, control)?.isHidden === true;
}
function defaultViewId(settings, viewIds, control) {
  let kindWide;
  for (const viewId of viewIds) {
    const deciding = decidingViewSetting(settings, viewId, control);
    if (!deciding?.isDefault) continue;
    if (deciding.control.key !== null) return viewId;
    kindWide ??= viewId;
  }
  return kindWide;
}
function setDefaultView(settings, viewId, control, isDefault) {
  const changes = [];
  if (isDefault) {
    for (const other of settings.filter((x) => sameControl(x.control, control) && x.isDefault && !sameView(x.viewId, viewId)))
      write(settings, changes, other.viewId, control, false, other.isHidden);
  }
  const current = decidingViewSetting(settings, viewId, control);
  write(settings, changes, viewId, control, isDefault, !isDefault && current?.isHidden === true);
  return changes;
}
function setViewHidden(settings, viewId, control, isHidden) {
  const current = decidingViewSetting(settings, viewId, control);
  const changes = [];
  write(settings, changes, viewId, control, !isHidden && current?.isDefault === true, isHidden);
  return changes;
}
function forgetViewSettings(settings, viewId) {
  return settings.filter((x) => sameView(x.viewId, viewId)).map((x) => ({ kind: "delete", id: x.id }));
}
function write(settings, changes, viewId, control, isDefault, isHidden) {
  const row = settings.find((x) => sameControl(x.control, control) && sameView(x.viewId, viewId));
  const kindWide = control.key === null ? void 0 : settings.find((x) => sameControl(x.control, everyOfKind(control)) && sameView(x.viewId, viewId));
  const redundant = isDefault === (kindWide?.isDefault === true) && isHidden === (kindWide?.isHidden === true);
  if (redundant) {
    if (row) changes.push({ kind: "delete", id: row.id });
  } else if (!row) {
    changes.push({ kind: "create", viewId, control, isDefault, isHidden });
  } else if (row.isDefault !== isDefault || row.isHidden !== isHidden) {
    changes.push({ kind: "update", id: row.id, isDefault, isHidden });
  }
}
var escape = (value) => value.replace(/&/g, "&amp;").replace(/'/g, "&apos;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
var condition = (column, operator, value) => `<condition attribute='${column}' operator='${operator}'${value === void 0 ? "" : ` value='${escape(value)}'`} />`;
function savedViewsQuery(table, control, ownedByCaller) {
  const s = SavedViewSchema;
  const offered = `<filter type='or'>${condition(s.controlKeys, "null")}` + condition(s.controlKeys, "like", `%${escapeLike(controlToJson(everyOfKind(control)))}%`) + (control.key === null ? "" : condition(s.controlKeys, "like", `%${escapeLike(controlToJson(control))}%`)) + `</filter>`;
  return `<fetch><entity name='${s.table}'>` + [
    s.id,
    s.name,
    s.controlKeys,
    s.fetchXml,
    s.gridLayout,
    s.calendarLayout,
    // A board's layout only for a board: the other controls keep working where the table doesn't
    // have the column yet.
    ...control.kind === SavedViewControlKind.Board ? [s.kanbanLayout] : []
  ].map((x) => `<attribute name='${x}' />`).join("") + `<filter>${condition(s.tableName, "eq", table)}${offered}${ownedByCaller ? condition(s.owner, "eq-userid") : ""}</filter><order attribute='${s.name}' /></entity></fetch>`;
}
function userViewSettingsQuery(table, control, ownedByCaller) {
  const s = UserViewSettingSchema;
  const controls = `<filter type='or'>${condition(s.controlKey, "eq", controlToJson(everyOfKind(control)))}` + (control.key === null ? "" : condition(s.controlKey, "eq", controlToJson(control))) + `</filter>`;
  return `<fetch><entity name='${s.table}'>` + [s.id, s.viewId, s.controlKey, s.isDefault, s.isHidden].map((x) => `<attribute name='${x}' />`).join("") + `<filter>${condition(s.tableName, "eq", table)}${controls}${ownedByCaller ? condition(s.owner, "eq-userid") : ""}</filter></entity></fetch>`;
}
function viewSettingsQuery(table, viewId, ownedByCaller) {
  const s = UserViewSettingSchema;
  return `<fetch><entity name='${s.table}'><attribute name='${s.id}' /><filter>${table === null ? "" : condition(s.tableName, "eq", table)}${condition(s.viewId, "eq", viewId.toLowerCase())}${ownedByCaller ? condition(s.owner, "eq-userid") : ""}</filter></entity></fetch>`;
}
function multiTableViewsQuery(control, ownedByCaller) {
  const s = MultiTableViewSchema;
  const searching = control.kind === SavedViewControlKind.Search;
  const offered = `<filter type='or'>${namesItself(control) ? "" : condition(s.controlKeys, "null")}` + condition(s.controlKeys, "like", `%${escapeLike(controlToJson(everyOfKind(control)))}%`) + (control.key === null ? "" : condition(s.controlKeys, "like", `%${escapeLike(controlToJson(control))}%`)) + `</filter>`;
  return `<fetch><entity name='${s.table}'>` + [s.id, s.name, s.controlKeys, s.calendarLayout, s.timelineLayout, ...searching ? [s.searchLayout] : []].map((x) => `<attribute name='${x}' />`).join("") + `<filter>${offered}${ownedByCaller ? condition(s.owner, "eq-userid") : ""}</filter><order attribute='${s.name}' /></entity></fetch>`;
}
function multiTableViewTablesQuery(viewIds, ownedByCaller) {
  const s = MultiTableViewTableSchema;
  return `<fetch><entity name='${s.table}'>` + [s.view, s.tableName, s.viewType, s.viewId, s.fetchXml, s.label, s.overrides, s.sortOrder].map((x) => `<attribute name='${x}' />`).join("") + `<filter><condition attribute='${s.view}' operator='in'>${viewIds.map((id) => `<value>${escape(id)}</value>`).join("")}</condition>${ownedByCaller ? condition(s.owner, "eq-userid") : ""}</filter><order attribute='${s.sortOrder}' /></entity></fetch>`;
}
var valueOf = (record, column) => record.properties?.[column]?.value;
var VIEW_TYPES = [8944e5, 894400001, 894400002, 894400003];
var FETCH_XML_VIEW_TYPE = 894400003;
function toViewTable(row) {
  const table = valueOf(row, MultiTableViewTableSchema.tableName);
  if (typeof table !== "string" || !table) return void 0;
  const type = valueOf(row, MultiTableViewTableSchema.viewType);
  const viewId = valueOf(row, MultiTableViewTableSchema.viewId);
  const fetchXml = valueOf(row, MultiTableViewTableSchema.fetchXml);
  const label = valueOf(row, MultiTableViewTableSchema.label);
  let overrides;
  const json = valueOf(row, MultiTableViewTableSchema.overrides);
  if (typeof json === "string" && json.trim()) {
    try {
      overrides = JSON.parse(json);
    } catch {
      overrides = void 0;
    }
  }
  return {
    table,
    viewType: typeof type === "number" && VIEW_TYPES.includes(type) ? type : FETCH_XML_VIEW_TYPE,
    viewId: typeof viewId === "string" && viewId ? viewId : null,
    fetchXml: typeof fetchXml === "string" ? fetchXml : null,
    label: typeof label === "string" ? label : null,
    overrides: overrides ?? null
  };
}
function toViewTableRow(table, order) {
  const s = MultiTableViewTableSchema;
  const text = (value) => ({ $type: 14, value });
  const overrides = table.overrides ? Object.fromEntries(Object.entries(table.overrides).filter(([, value]) => value !== null && value !== void 0)) : void 0;
  return {
    [s.name]: text(table.label || table.table),
    [s.tableName]: text(table.table),
    [s.viewType]: { $type: 11, value: table.viewType ?? FETCH_XML_VIEW_TYPE },
    [s.sortOrder]: { $type: 5, value: order },
    ...table.viewId ? { [s.viewId]: text(table.viewId.toLowerCase()) } : {},
    ...table.fetchXml?.trim() ? { [s.fetchXml]: text(table.fetchXml) } : {},
    ...table.label?.trim() ? { [s.label]: text(table.label) } : {},
    ...overrides && Object.keys(overrides).length > 0 ? { [s.overrides]: text(JSON.stringify(overrides)) } : {}
  };
}
function shownViewTables(tables2, listed, shown, isActivity) {
  const everyActivity = "activitypointer";
  const same = (a, b) => a.toLowerCase() === b.toLowerCase();
  const isShown = (table) => shown.some((x) => same(x, table));
  const named = tables2.map((x) => x.table ?? "").filter((x) => !same(x, everyActivity));
  const activities = listed.filter((x) => isActivity(x) && !named.some((n) => same(n, x)));
  const result = [];
  for (const table of tables2) {
    if (!same(table.table ?? "", everyActivity)) {
      if (isShown(table.table ?? "")) result.push(table);
    } else if (activities.every(isShown)) {
      if (activities.length > 0) result.push(table);
    } else {
      for (const activity of activities.filter(isShown))
        result.push({
          table: activity,
          ...table.viewType !== void 0 ? { viewType: table.viewType } : {},
          viewId: table.viewId ?? null,
          fetchXml: table.fetchXml ?? null,
          overrides: table.overrides ?? null
        });
    }
  }
  return result;
}

// src/saved-view-columns.ts
var TOTALS_ORDER = [
  QueryAggregate.Sum,
  QueryAggregate.Average,
  QueryAggregate.Minimum,
  QueryAggregate.Maximum,
  QueryAggregate.Count
];
var writeTotals = (totals) => TOTALS_ORDER.flatMap((aggregate) => {
  const total = totals.find((x) => x.aggregate === aggregate);
  return total ? [{ type: gridAggregateName(aggregate), format: total.format?.trim() }] : [];
});
var pixelWidth = (width) => {
  const pixels = typeof width === "string" && /^\d+(\.\d+)?$/.test(width.trim()) ? Number(width) : width;
  const whole = typeof pixels === "number" && Number.isFinite(pixels) ? Math.round(pixels) : 0;
  return whole > 0 ? whole : void 0;
};
var pinOf = (pin) => pin === GridColumnPin.Start || pin === GridColumnPin.End ? pin : void 0;
var writeViewColumn = (x) => ({
  columnName: x.columnName,
  width: pixelWidth(x.width),
  hidden: x.hidden || void 0,
  pin: pinOf(x.pin),
  aggregates: x.aggregates?.length ? writeTotals(x.aggregates) : void 0,
  displayLabel: x.displayLabel || void 0
});
function readViewColumn(value) {
  if (!value || typeof value !== "object") return [];
  const column = value;
  const columnName = column.columnName;
  if (typeof columnName !== "string" || !columnName) return [];
  const totals = column.aggregates;
  const aggregates = (Array.isArray(totals) ? totals : []).flatMap((x) => {
    const total = typeof x === "string" ? { type: x } : x;
    const aggregate = total && typeof total === "object" ? gridAggregateNamed(total.type) : void 0;
    if (aggregate === void 0) return [];
    return [{ aggregate, ...typeof total.format === "string" && { format: total.format.trim() } }];
  });
  const width = pixelWidth(column.width);
  const pin = pinOf(column.pin);
  return [
    {
      columnName,
      ...width !== void 0 && { width },
      ...column.hidden === true && { hidden: true },
      ...pin && { pin },
      ...aggregates.length > 0 && { aggregates },
      ...typeof column.displayLabel === "string" && column.displayLabel && { displayLabel: column.displayLabel }
    }
  ];
}

// src/saved-grid-view-layout.ts
var SAVED_GRID_VIEW_LAYOUT_VERSION = 1;
function savedGridViewLayoutToJson(layout) {
  return JSON.stringify({
    version: layout.version,
    pageSize: layout.pageSize,
    layout: layout.layout,
    groupBy: layout.groupBy.map(writeGroupBy),
    columns: layout.columns.map(writeViewColumn)
  });
}
function parseSavedGridViewLayout(json) {
  if (!json?.trim()) return void 0;
  try {
    const value = JSON.parse(json);
    if (!value || typeof value !== "object") return void 0;
    return {
      version: typeof value.version === "number" ? value.version : SAVED_GRID_VIEW_LAYOUT_VERSION,
      ...typeof value.pageSize === "number" && { pageSize: value.pageSize },
      ...Object.values(GridLayout).includes(value.layout) && { layout: value.layout },
      groupBy: Array.isArray(value.groupBy) ? value.groupBy.flatMap(readGroupBy) : [],
      columns: Array.isArray(value.columns) ? value.columns.flatMap(readViewColumn) : []
    };
  } catch {
    return void 0;
  }
}
var DATE_GROUPING_NAMES = [
  ["minute", DateGrouping.Minute],
  ["hour", DateGrouping.Hour],
  ["week", DateGrouping.Week],
  ["month", DateGrouping.Month],
  ["quarter", DateGrouping.Quarter],
  ["year", DateGrouping.Year],
  ["fiscal-period", DateGrouping.FiscalPeriod],
  ["fiscal-year", DateGrouping.FiscalYear]
];
var readRangeSize = (rangeSize) => {
  const size = Number(rangeSize ?? 0);
  return Number.isFinite(size) && size > 0 ? size : void 0;
};
var writeGroupBy = (level) => ({
  columnName: level.columnName,
  descending: level.descending || void 0,
  dateGrouping: DATE_GROUPING_NAMES.find(([, x]) => x === level.dateGrouping)?.[0],
  rangeSize: readRangeSize(level.rangeSize)
});
function readGroupBy(value) {
  if (!value || typeof value !== "object") return [];
  const level = value;
  const columnName = level.columnName;
  if (typeof columnName !== "string" || !columnName) return [];
  const rangeSize = readRangeSize(level.rangeSize);
  return [
    {
      columnName,
      descending: level.descending === true,
      dateGrouping: DATE_GROUPING_NAMES.find(([name]) => name === level.dateGrouping)?.[1] ?? DateGrouping.Day,
      ...rangeSize !== void 0 && { rangeSize }
    }
  ];
}
function savedGridViewLayoutsArrangeAlike(a, b) {
  if (!a || !b) return false;
  return (a.layout ?? null) === (b.layout ?? null) && a.groupBy.length === b.groupBy.length && a.groupBy.every((x, i) => sameGroupBy(x, b.groupBy[i])) && a.columns.length === b.columns.length && a.columns.every((x, i) => sameColumn(x, b.columns[i]));
}
function sameGroupBy(a, b) {
  return JSON.stringify(writeGroupBy(a)) === JSON.stringify(writeGroupBy(b));
}
function sameColumn(a, b) {
  return a.columnName === b.columnName && !!a.hidden === !!b.hidden && (pinOf(a.pin) ?? null) === (pinOf(b.pin) ?? null) && (pixelWidth(a.width) ?? null) === (pixelWidth(b.width) ?? null) && totalsSignature(a.aggregates) === totalsSignature(b.aggregates);
}
var totalsSignature = (totals) => writeTotals(totals ?? []).map((x) => `${x.type}:${x.format ?? ""}`).join();
function totalsToAggregates(totals) {
  return (totals ?? []).reduce((all, total) => all | total.aggregate, 0);
}
function aggregatesToTotals(aggregates, formats) {
  const totals = TOTALS_ORDER.filter((aggregate) => (aggregates & aggregate) === aggregate).map((aggregate) => {
    const format = formats?.get(aggregate)?.trim();
    return format !== void 0 ? { aggregate, format } : { aggregate };
  });
  return totals.length ? totals : void 0;
}

// src/close-record.ts
var definitions = {
  [CloseRecordAction.Win]: { table: "opportunity", fromStates: [0], state: 1, closeTable: "opportunityclose", recordColumn: "opportunityid", hiddenReasons: [] },
  [CloseRecordAction.Lose]: { table: "opportunity", fromStates: [0], state: 2, closeTable: "opportunityclose", recordColumn: "opportunityid", hiddenReasons: [] },
  [CloseRecordAction.Resolve]: { table: "incident", fromStates: [0], state: 1, closeTable: "incidentresolution", recordColumn: "incidentid", hiddenReasons: [] },
  [CloseRecordAction.WinQuote]: { table: "quote", fromStates: [1], state: 2, closeTable: "quoteclose", recordColumn: "quoteid", hiddenReasons: [] },
  [CloseRecordAction.CloseQuote]: { table: "quote", fromStates: [1], state: 3, closeTable: "quoteclose", recordColumn: "quoteid", hiddenReasons: [7] },
  [CloseRecordAction.FulfillOrder]: { table: "salesorder", fromStates: [0, 1], state: 3, closeTable: "orderclose", recordColumn: "salesorderid", hiddenReasons: [] },
  [CloseRecordAction.CancelOrder]: { table: "salesorder", fromStates: [0, 1], state: 2, closeTable: "orderclose", recordColumn: "salesorderid", hiddenReasons: [] }
};
var StateChangeKind = {
  /** No change of state: the record stays in the one it is in, whatever its reason. */
  Same: "same",
  /** An update of the record's status makes it. */
  Update: "update",
  /** One of Dataverse's own close messages makes it ({@link StateChange.action}). */
  Close: "close",
  /** The table's transitions don't allow it. */
  Refused: "refused"
};
var sameTable = (a, b) => a.toLowerCase() === b.toLowerCase();
function isGovernedTable(states) {
  return states?.transitions !== null && states?.transitions !== void 0;
}
function stateChangeOf(states, from, to) {
  if (from === to) return { kind: StateChangeKind.Same };
  if (!isGovernedTable(states)) return { kind: StateChangeKind.Update };
  const transition = states.transitions.find(
    (x) => from !== null && from !== void 0 && Number(x.from) === from && Number(x.to) === to
  );
  if (!transition) return { kind: StateChangeKind.Refused };
  return transition.action === null || transition.action === void 0 ? { kind: StateChangeKind.Update } : { kind: StateChangeKind.Close, action: transition.action };
}
function closeRecordDefinition(action) {
  return definitions[action];
}
function hasCloseActions(table) {
  return Object.values(definitions).some((x) => sameTable(x.table, table));
}
function stateReasonsOf(state, states, reasons) {
  const ofState = (x) => x.state !== null && x.state !== void 0 && Number(x.state) === state;
  const offered = reasons.choices.filter((x) => ofState(x) && x.isHidden !== true);
  if (offered.length > 0) return offered;
  const defaultStatus = states?.choices.find((x) => Number(x.value) === state)?.defaultStatus;
  if (defaultStatus === null || defaultStatus === void 0) return [];
  const fallback = reasons.choices.find((x) => Number(x.value) === Number(defaultStatus) && ofState(x));
  return fallback ? [fallback] : [];
}
function closeReasonsFor(action, states, reasons) {
  const definition = definitions[action];
  return stateReasonsOf(definition.state, states, reasons).filter((x) => !definition.hiddenReasons.includes(Number(x.value)));
}
function closeActionForReason(states, from, reason, reasons) {
  if (reason === null || reason === void 0 || !reasons) return void 0;
  const state = reasons.choices.find((x) => Number(x.value) === reason)?.state;
  if (state === null || state === void 0) return void 0;
  const change = stateChangeOf(states, from, Number(state));
  return change.kind === StateChangeKind.Close && change.action !== void 0 && closeReasonsFor(change.action, states, reasons).some((x) => Number(x.value) === reason) ? change.action : void 0;
}

// src/kanban-date-lanes.ts
var KanbanDateLaneDrop = {
  /**
   * It takes the lane's first day — today, tomorrow, the day after tomorrow, next week's first day,
   * the first day after next week — keeping its time of day; dropped in No date, it loses its date.
   * None is dropped in a lane without a first day, such as Overdue.
   */
  ChangeDate: "changeDate",
  /** Date lanes take no card from one another: they only show when cards fall. */
  None: "none"
};
var KanbanDateLaneOrder = {
  /**
   * By the board's rank column, as every other lane: a card moved within its lane changes its rank
   * alone. Without a rank column, in the view's order, and not moved within a lane.
   */
  Rank: "rank",
  /**
   * By the date: a card dropped below another takes that card's day, keeping its own time of day, and
   * one dropped at the top takes the lane's first day. Cards of the same day stay in the order of
   * their times. The rank column isn't used.
   */
  Date: "date"
};
var KanbanDatePeriod = {
  Day: "day",
  Week: "week",
  Month: "month",
  CalendarQuarter: "calendarQuarter",
  FiscalQuarter: "fiscalQuarter",
  CalendarYear: "calendarYear",
  FiscalYear: "fiscalYear"
};
var KanbanDateBound = {
  /** An exact day. */
  on: (day) => ({ day: { year: day.year, month: day.month, day: day.day } }),
  /** The start of the period `offset` periods from the one today falls in. */
  startOf: (period, offset = 0) => ({ period, offset })
};
var lane = (key, period, offset) => ({
  key,
  from: KanbanDateBound.startOf(period, offset),
  until: KanbanDateBound.startOf(period, offset + 1)
});
var boundOf = (given) => "year" in given ? KanbanDateBound.on(given) : given;
var overdue = { key: "overdue", until: KanbanDateBound.startOf(KanbanDatePeriod.Day) };
var today = lane("today", KanbanDatePeriod.Day, 0);
var tomorrow = lane("tomorrow", KanbanDatePeriod.Day, 1);
var thisWeek = lane("this-week", KanbanDatePeriod.Week, 0);
var nextWeek = lane("next-week", KanbanDatePeriod.Week, 1);
var later = { key: "later", fillsGap: true };
var noDate = { key: "", holdsNoDate: true };
var KanbanDateLane = {
  /** Before today. */
  Overdue: overdue,
  Yesterday: lane("yesterday", KanbanDatePeriod.Day, -1),
  Today: today,
  Tomorrow: tomorrow,
  LastWeek: lane("last-week", KanbanDatePeriod.Week, -1),
  ThisWeek: thisWeek,
  NextWeek: nextWeek,
  LastMonth: lane("last-month", KanbanDatePeriod.Month, -1),
  ThisMonth: lane("this-month", KanbanDatePeriod.Month, 0),
  NextMonth: lane("next-month", KanbanDatePeriod.Month, 1),
  LastCalendarQuarter: lane("last-calendar-quarter", KanbanDatePeriod.CalendarQuarter, -1),
  ThisCalendarQuarter: lane("this-calendar-quarter", KanbanDatePeriod.CalendarQuarter, 0),
  NextCalendarQuarter: lane("next-calendar-quarter", KanbanDatePeriod.CalendarQuarter, 1),
  LastFiscalQuarter: lane("last-fiscal-quarter", KanbanDatePeriod.FiscalQuarter, -1),
  ThisFiscalQuarter: lane("this-fiscal-quarter", KanbanDatePeriod.FiscalQuarter, 0),
  NextFiscalQuarter: lane("next-fiscal-quarter", KanbanDatePeriod.FiscalQuarter, 1),
  LastCalendarYear: lane("last-calendar-year", KanbanDatePeriod.CalendarYear, -1),
  ThisCalendarYear: lane("this-calendar-year", KanbanDatePeriod.CalendarYear, 0),
  NextCalendarYear: lane("next-calendar-year", KanbanDatePeriod.CalendarYear, 1),
  LastFiscalYear: lane("last-fiscal-year", KanbanDatePeriod.FiscalYear, -1),
  ThisFiscalYear: lane("this-fiscal-year", KanbanDatePeriod.FiscalYear, 0),
  NextFiscalYear: lane("next-fiscal-year", KanbanDatePeriod.FiscalYear, 1),
  /** Everything before the lane after it: listed first, the days before the board's lanes. */
  Earlier: { key: "earlier", fillsGap: true },
  /** Everything after the lane before it: listed last, the days after the board's lanes. */
  Later: later,
  /** The cards without a date. */
  NoDate: noDate,
  /**
   * The lanes a date column gives a board that names none: Overdue, Today, Tomorrow, This week — from
   * the day after tomorrow — Next week, Later and No date.
   */
  Defaults: [overdue, today, tomorrow, thisWeek, nextWeek, later, noDate],
  /** A lane of the days from `from` up to but not including `until`: exact days, or bounds of periods. */
  between: (key, title, from, until) => ({
    key,
    ...title !== void 0 && { title },
    from: boundOf(from),
    until: boundOf(until)
  }),
  /** A lane of the days before `until`, from where the lane before it ends. */
  before: (key, title, until) => ({
    key,
    ...title !== void 0 && { title },
    until: boundOf(until)
  }),
  /** A lane of the days from `from` on. */
  starting: (key, title, from) => ({
    key,
    ...title !== void 0 && { title },
    from: boundOf(from)
  })
};
var DAY_MS = 24 * 60 * 60 * 1e3;
var msOf = (day) => Date.UTC(day.year, day.month - 1, day.day);
var dayOfMs = (ms) => {
  const date = new Date(ms);
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
};
var addDays = (day, days) => dayOfMs(msOf(day) + days * DAY_MS);
var pad = (value, width = 2) => String(value).padStart(width, "0");
var isoDay = (day) => `${pad(day.year, 4)}-${pad(day.month)}-${pad(day.day)}`;
var modulo = (value, by) => (value % by + by) % by;
var daysInMonth = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();
function anchored(index, day) {
  const year = Math.floor(index / 12);
  const month = modulo(index, 12) + 1;
  return { year, month, day: Math.min(day, daysInMonth(year, month)) };
}
function monthStart(today2, offset, months, alignedMonth, day) {
  const index = today2.year * 12 + today2.month - 1;
  let aligned2 = index - modulo(index - (alignedMonth - 1), months);
  if (msOf(anchored(aligned2, day)) > msOf(today2)) aligned2 -= months;
  return anchored(aligned2 + offset * months, day);
}
function kanbanDatePeriodStart(period, offset, today2, firstDayOfWeek2, fiscalYearStart) {
  const fiscalMonth = Number(fiscalYearStart?.month ?? 1);
  const fiscalDay = Number(fiscalYearStart?.day ?? 1);
  switch (period) {
    case KanbanDatePeriod.Day:
      return addDays(today2, offset);
    case KanbanDatePeriod.Week: {
      const weekday = new Date(msOf(today2)).getUTCDay();
      return addDays(today2, -modulo(weekday - firstDayOfWeek2, 7) + 7 * offset);
    }
    case KanbanDatePeriod.Month:
      return monthStart(today2, offset, 1, 1, 1);
    case KanbanDatePeriod.CalendarQuarter:
      return monthStart(today2, offset, 3, 1, 1);
    case KanbanDatePeriod.FiscalQuarter:
      return monthStart(today2, offset, 3, fiscalMonth, fiscalDay);
    case KanbanDatePeriod.CalendarYear:
      return monthStart(today2, offset, 12, 1, 1);
    case KanbanDatePeriod.FiscalYear:
      return monthStart(today2, offset, 12, fiscalMonth, fiscalDay);
  }
}
function validateKanbanDateLanes(lanes) {
  const keys = /* @__PURE__ */ new Set();
  for (const x of lanes) {
    if (keys.has(x.key)) throw new Error(`Two date lanes have the key '${x.key}'.`);
    keys.add(x.key);
  }
  if (lanes.filter((x) => x.holdsNoDate).length > 1) throw new Error("Only one date lane may hold the cards without a date.");
}
function resolveKanbanDateLanes(lanes, today2, firstDayOfWeek2, fiscalYearStart) {
  validateKanbanDateLanes(lanes);
  const ms = (bound2) => bound2 === void 0 ? void 0 : msOf(bound2.day ?? kanbanDatePeriodStart(bound2.period ?? KanbanDatePeriod.Day, bound2.offset ?? 0, today2, firstDayOfWeek2, fiscalYearStart));
  const nextStart = (gap) => {
    const next = lanes.slice(gap + 1).find((x) => !x.holdsNoDate);
    if (!next) return Infinity;
    return next.fillsGap ? -Infinity : ms(next.from) ?? -Infinity;
  };
  const resolved = [];
  let end = -Infinity;
  lanes.forEach((x, i) => {
    if (x.holdsNoDate) {
      resolved.push({ key: x.key, isEmpty: false, holdsNoDate: true });
      return;
    }
    const from = Math.max(ms(x.from) ?? end, end);
    const until = x.fillsGap ? nextStart(i) : ms(x.until) ?? Infinity;
    const isEmpty2 = from >= until;
    resolved.push({
      key: x.key,
      ...Number.isFinite(from) && { from: dayOfMs(from) },
      ...Number.isFinite(until) && { until: dayOfMs(until) },
      isEmpty: isEmpty2,
      holdsNoDate: false,
      ...!isEmpty2 && Number.isFinite(from) && { firstDay: dayOfMs(from) }
    });
    end = Math.max(from, until);
  });
  return resolved;
}
function kanbanDateLaneContains(lane3, day) {
  if (lane3.holdsNoDate || lane3.isEmpty) return false;
  const ms = msOf(day);
  return (lane3.from === void 0 || ms >= msOf(lane3.from)) && (lane3.until === void 0 || ms < msOf(lane3.until));
}
var escapeXml = (text) => text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&apos;");
function bound(behavior, day, timeZone) {
  switch (behavior) {
    case DateTimeBehavior.DateOnly:
      return isoDay(day);
    case DateTimeBehavior.TimeZoneIndependent:
      return `${isoDay(day)}T00:00:00`;
    default:
      return fromWallClock({ ...day, hour: 0, minute: 0, second: 0 }, timeZone).toISOString();
  }
}
function kanbanDateLaneFilter(column, behavior, lane3, timeZone) {
  const attribute = escapeXml(column);
  if (lane3.holdsNoDate) {
    return { $type: "FetchXmlFilter", filterXml: `<filter><condition attribute="${attribute}" operator="null" /></filter>` };
  }
  if (lane3.isEmpty) {
    return {
      $type: "FetchXmlFilter",
      filterXml: `<filter><condition attribute="${attribute}" operator="null" /><condition attribute="${attribute}" operator="not-null" /></filter>`
    };
  }
  let conditions = (lane3.from ? `<condition attribute="${attribute}" operator="ge" value="${bound(behavior, lane3.from, timeZone)}" />` : "") + (lane3.until ? `<condition attribute="${attribute}" operator="lt" value="${bound(behavior, lane3.until, timeZone)}" />` : "");
  if (conditions === "") conditions = `<condition attribute="${attribute}" operator="not-null" />`;
  return { $type: "FetchXmlFilter", filterXml: `<filter>${conditions}</filter>` };
}
var WIRE_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?/;
function wallClockOf2(behavior, value, timeZone) {
  if (value === null || value === void 0 || value === "") return void 0;
  if (behavior === DateTimeBehavior.UserLocal) {
    const instant = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(instant.getTime())) return void 0;
    return wallClockOf(instant, timeZone);
  }
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? void 0 : clockOfLocal(value);
  const match = WIRE_DATE.exec(value);
  if (!match) return void 0;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4] ?? 0),
    minute: Number(match[5] ?? 0),
    second: Number(match[6] ?? 0)
  };
}
var clockOfLocal = (date) => ({
  year: date.getFullYear(),
  month: date.getMonth() + 1,
  day: date.getDate(),
  hour: date.getHours(),
  minute: date.getMinutes(),
  second: date.getSeconds()
});
function kanbanDateDayOf(behavior, value, timeZone) {
  const clock = wallClockOf2(behavior, value, timeZone);
  return clock && { year: clock.year, month: clock.month, day: clock.day };
}
function kanbanDateLaneKeyOf(behavior, value, lanes, timeZone) {
  const day = kanbanDateDayOf(behavior, value, timeZone);
  if (!day) return lanes.find((x) => x.holdsNoDate)?.key;
  return lanes.find((x) => kanbanDateLaneContains(x, day))?.key;
}
function kanbanDateValueFor(behavior, current, day, timeZone) {
  const clock = behavior === DateTimeBehavior.DateOnly ? void 0 : wallClockOf2(behavior, current, timeZone);
  const hour = clock?.hour ?? 0;
  const minute = clock?.minute ?? 0;
  const second = clock?.second ?? 0;
  switch (behavior) {
    case DateTimeBehavior.DateOnly:
      return `${isoDay(day)}T00:00:00Z`;
    case DateTimeBehavior.TimeZoneIndependent:
      return `${isoDay(day)}T${pad(hour)}:${pad(minute)}:${pad(second)}`;
    default:
      return fromWallClock({ ...day, hour, minute, second }, timeZone).toISOString();
  }
}
function todayIn(timeZone, now) {
  const { year, month, day } = wallClockOf(now, timeZone);
  return { year, month, day };
}

// src/kanban.ts
var KanbanLaneKind = {
  /** A choice: a lane for each of its options. */
  Choice: 0,
  /** A status reason: a lane for each reason, each in its state. */
  StatusReason: 1,
  /** A Yes/No column: its two lanes. */
  YesNo: 2,
  /** A lookup: a lane for each record it may name. */
  Lookup: 3,
  /** A date: lanes for when it falls, counted in days of the user's time zone from today (`kanban-date-lanes.ts`). */
  Date: 4
};
var KANBAN_NO_VALUE_KEY = "";
var KANBAN_RANK_STEP = 1e6;
var EMPTY_GUID3 = "00000000-0000-0000-0000-000000000000";
function kanbanLaneKind(column) {
  switch (column.$type) {
    case ColumnType.Status:
      return KanbanLaneKind.StatusReason;
    case ColumnType.Choice:
      return KanbanLaneKind.Choice;
    case ColumnType.Boolean:
      return KanbanLaneKind.YesNo;
    case ColumnType.Lookup:
      return KanbanLaneKind.Lookup;
    case ColumnType.DateTime:
      return KanbanLaneKind.Date;
    default:
      return void 0;
  }
}
function kanbanLanes(column, states, dateLanes) {
  switch (column.$type) {
    case ColumnType.Status: {
      const transitions = isGovernedTable(states) ? states.transitions : void 0;
      return column.choices.filter((x) => !x.isHidden).map((option) => {
        const state = wholeNumber(option.state);
        const closed = states ? isClosedState(states, state) : false;
        const withinState = !transitions || !closed;
        const closeAction = transitions?.find(
          (x) => Number(x.to) === state && x.action !== null && x.action !== void 0
        )?.action ?? void 0;
        return {
          ...optionLane(option),
          ...state !== void 0 && { state },
          isClosed: closed,
          ...closeAction !== void 0 && { closeAction },
          takesDrops: withinState || transitions.some((x) => Number(x.to) === state),
          letsGo: withinState || transitions.some((x) => Number(x.from) === state)
        };
      });
    }
    case ColumnType.State:
      return [];
    case ColumnType.DateTime:
      return (dateLanes ?? KanbanDateLane.Defaults).map((lane3) => ({
        key: lane3.key,
        label: lane3.title ?? lane3.key,
        value: null,
        isClosed: false,
        takesDrops: true,
        letsGo: true
      }));
    case ColumnType.Choice: {
      const lanes = column.choices.filter((x) => !x.isHidden).map(optionLane);
      return mayBeEmpty(column) ? [...lanes, noValueLane()] : lanes;
    }
    case ColumnType.Boolean: {
      const choices = column.choices ?? [];
      return [true, false].map((value) => {
        const key = flagKey(value);
        const option = choices.find((x) => wholeNumber(x.value) === (value ? 1 : 0));
        return lane2(key, option ? option.displayName : key, { $type: ColumnType.Boolean, value }, option?.color);
      });
    }
    default:
      return [];
  }
}
function kanbanLookupLanes(column, records) {
  const lanes = [];
  const seen = /* @__PURE__ */ new Set();
  for (const record of records) {
    const key = lookupKey(record.value);
    if (key === void 0 || seen.has(key)) continue;
    seen.add(key);
    const name = record.name;
    lanes.push(
      lane2(key, name?.trim() ? name : key, {
        $type: ColumnType.Lookup,
        value: key,
        name: name ?? null,
        tableName: record.tableName
      })
    );
  }
  return mayBeEmpty(column) ? [...lanes, noValueLane()] : lanes;
}
function arrangeKanbanLanes(lanes, keys) {
  if (keys === null || keys === void 0) return lanes;
  const byKey = /* @__PURE__ */ new Map();
  for (const x of lanes) if (!byKey.has(x.key.toLowerCase())) byKey.set(x.key.toLowerCase(), x);
  const arranged = [];
  for (const key of keys) {
    const found = byKey.get(key.trim().toLowerCase());
    if (found && !arranged.some((x) => x.key === found.key)) arranged.push(found);
  }
  return arranged;
}
function kanbanLaneKey(value) {
  if (value && "$type" in value) {
    switch (value.$type) {
      case ColumnType.Choice: {
        const option = wholeNumber(value.value);
        if (option !== void 0) return String(option);
        break;
      }
      case ColumnType.Boolean:
        if (typeof value.value === "boolean") return flagKey(value.value);
        break;
      case ColumnType.Lookup: {
        const key = lookupKey(value.value);
        if (key !== void 0) return key;
        break;
      }
    }
  }
  return KANBAN_NO_VALUE_KEY;
}
function kanbanLaneFilter(column, lane3) {
  const attribute = escapeXml2(column);
  const condition2 = lane3.key === KANBAN_NO_VALUE_KEY ? `<condition attribute="${attribute}" operator="null" />` : `<condition attribute="${attribute}" operator="eq" value="${escapeXml2(lane3.key)}" />`;
  return { $type: "FetchXmlFilter", filterXml: `<filter>${condition2}</filter>` };
}
function kanbanMoveOf(states, from, to) {
  if (from.state === void 0 || to.state === void 0) return { kind: StateChangeKind.Same };
  const change = stateChangeOf(states, from.state, to.state);
  return change.kind === StateChangeKind.Same && to.isClosed && isGovernedTable(states) ? { kind: StateChangeKind.Refused } : change;
}
function kanbanRanksForDrop(ranks, dropIndex, precision, min, max) {
  dropIndex = Math.max(0, Math.min(Math.trunc(dropIndex), ranks.length));
  precision = Math.max(0, Math.min(Math.trunc(precision), 10));
  const least = min == null ? void 0 : exact(min);
  const most = max == null ? void 0 : exact(max);
  const step = exact(KANBAN_RANK_STEP);
  const fits = (rank) => (!least || compare(rank, least) >= 0) && (!most || compare(rank, most) <= 0);
  const changes = (count, rank) => Array.from({ length: count }, (_, i) => ({ index: i, rank: toNumber2(rank(i)) }));
  let unranked = 0;
  while (unranked < ranks.length && ranks[unranked] == null) unranked++;
  if (dropIndex <= unranked) {
    const count = unranked + 1;
    const first = unranked < ranks.length ? ranks[unranked] : null;
    if (first == null) return afresh(ranks.length + 1, precision, least, most);
    const below = exact(first);
    if (fits(add(below, times(step, -count)))) return changes(count, (i) => add(below, times(step, -(count - i))));
    if (least) {
      const share = floorDivide(add(below, times(least, -1)), count + 1, precision);
      if (share.units > 0n) return changes(count, (i) => add(least, times(share, i + 1)));
    }
    return afresh(ranks.length + 1, precision, least, most);
  }
  const above = ranks[dropIndex - 1];
  if (above == null) throw new Error("The cards without a rank must come first.");
  const before = exact(above);
  const next = dropIndex < ranks.length ? ranks[dropIndex] : null;
  if (next == null) {
    const after = add(before, step);
    if (fits(after)) return [{ index: dropIndex, rank: toNumber2(after) }];
    if (most) {
      const toward = round(halve(add(before, most)), precision);
      if (compare(toward, before) > 0 && compare(toward, most) <= 0) return [{ index: dropIndex, rank: toNumber2(toward) }];
    }
    return afresh(ranks.length + 1, precision, least, most);
  }
  const high = exact(next);
  const middle = round(halve(add(before, high)), precision);
  if (compare(middle, before) > 0 && compare(middle, high) < 0) return [{ index: dropIndex, rank: toNumber2(middle) }];
  return afresh(ranks.length + 1, precision, least, most);
}
function afresh(count, precision, least, most) {
  const start = least && least.units > 0n ? least : exact(0);
  const step = exact(KANBAN_RANK_STEP);
  let spacing = step;
  if (most) {
    const room = floorDivide(add(most, times(start, -1)), count + 1, precision);
    if (compare(room, step) < 0) spacing = room;
  }
  if (spacing.units <= 0n) return [];
  return Array.from({ length: count }, (_, i) => ({ index: i, rank: toNumber2(add(start, times(spacing, i + 1))) }));
}
function kanbanMoveUpdate(table, id, laneColumn, to, rankColumn, rank) {
  const properties = { [laneColumn.columnName]: movedValue(laneColumn, to.value) };
  if (rankColumn !== null && rankColumn !== void 0 && rank !== null && rank !== void 0) {
    properties[rankColumn] = { $type: ColumnType.Decimal, value: rank };
  }
  return { tableName: table, id, properties };
}
function movedValue(column, value) {
  switch (value?.$type) {
    case ColumnType.Choice:
      return { $type: ColumnType.Choice, value: value.value ?? null };
    case ColumnType.Boolean:
      return { $type: ColumnType.Boolean, value: value.value ?? null };
    case ColumnType.Lookup:
      return { $type: ColumnType.Lookup, value: value.value ?? null, name: value.name ?? null, tableName: value.tableName };
    case ColumnType.DateTime:
      return { $type: ColumnType.DateTime, value: value.value ?? null };
    default:
      return column.$type === ColumnType.Lookup ? { $type: ColumnType.Lookup, value: null, tableName: column.targetTables[0] ?? "" } : column.$type === ColumnType.DateTime ? { $type: ColumnType.DateTime, value: null } : { $type: ColumnType.Choice, value: null };
  }
}
function lane2(key, label, value, color) {
  return {
    key,
    label,
    value,
    ...color !== null && color !== void 0 && { color },
    isClosed: false,
    takesDrops: true,
    letsGo: true
  };
}
function optionLane(option) {
  const value = Number(option.value);
  return lane2(String(value), option.displayName, { $type: ColumnType.Choice, value }, option.color);
}
var noValueLane = () => lane2(KANBAN_NO_VALUE_KEY, "", null);
var mayBeEmpty = (column) => column.requiredLevel !== RequiredLevel.SystemRequired && column.requiredLevel !== RequiredLevel.ApplicationRequired;
var isClosedState = (states, state) => state !== void 0 && states.choices.some((x) => wholeNumber(x.value) === state && x.isClosed === true);
var flagKey = (value) => value ? "1" : "0";
function lookupKey(id) {
  const key = id?.toLowerCase();
  return key && key !== EMPTY_GUID3 ? key : void 0;
}
function wholeNumber(value) {
  if (value === null || value === void 0 || value === "") return void 0;
  const number = Number(value);
  return Number.isInteger(number) ? number : void 0;
}
var XML_ESCAPES = { "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&apos;", "&": "&amp;" };
var escapeXml2 = (value) => value.replace(/[<>"'&]/g, (ch) => XML_ESCAPES[ch]);
function exact(value) {
  const [mantissa = "", exponent = "0"] = String(value).toLowerCase().split("e");
  const [whole = "", fraction = ""] = mantissa.split(".");
  const places = fraction.length - Number(exponent);
  const units = BigInt(whole + fraction);
  return places >= 0 ? { units, places } : { units: units * 10n ** BigInt(-places), places: 0 };
}
function aligned(a, b) {
  const places = Math.max(a.places, b.places);
  return [a.units * 10n ** BigInt(places - a.places), b.units * 10n ** BigInt(places - b.places), places];
}
function add(a, b) {
  const [x, y, places] = aligned(a, b);
  return { units: x + y, places };
}
function compare(a, b) {
  const [x, y] = aligned(a, b);
  return x < y ? -1 : x > y ? 1 : 0;
}
var halve = (a) => ({ units: a.units * 5n, places: a.places + 1 });
function round(a, places) {
  if (a.places <= places) return a;
  const divisor = 10n ** BigInt(a.places - places);
  const negative = a.units < 0n;
  const magnitude = negative ? -a.units : a.units;
  let units = magnitude / divisor;
  const twice = magnitude % divisor * 2n;
  if (twice > divisor || twice === divisor && units % 2n === 1n) units += 1n;
  return { units: negative ? -units : units, places };
}
var toNumber2 = (a) => Number(`${a.units}e-${a.places}`);
var times = (a, n) => ({ units: a.units * BigInt(n), places: a.places });
function floorDivide(a, n, places) {
  const numerator = a.units * 10n ** BigInt(places);
  const denominator = 10n ** BigInt(a.places) * BigInt(n);
  let units = numerator / denominator;
  if (numerator % denominator !== 0n && numerator < 0n !== denominator < 0n) units -= 1n;
  return { units, places };
}

// src/saved-kanban-layout.ts
var SAVED_KANBAN_LAYOUT_VERSION = 1;
function savedKanbanLayoutToJson(layout) {
  return JSON.stringify({
    version: layout.version,
    pageSize: layout.pageSize ?? void 0,
    columns: layout.columns.map(writeViewColumn),
    collapsedLanes: layout.collapsedLanes,
    hiddenLanes: layout.hiddenLanes
  });
}
function kanbanLayoutOf(view) {
  const arranged = view.pageSize !== void 0 || view.columns.some((column) => column.hidden || (column.aggregates?.length ?? 0) > 0 || !!column.displayLabel?.trim());
  if (!arranged) return void 0;
  return {
    version: SAVED_KANBAN_LAYOUT_VERSION,
    ...view.pageSize !== void 0 && { pageSize: view.pageSize },
    columns: view.columns.map(
      (column) => ({
        columnName: column.columnName,
        ...column.hidden && { hidden: true },
        ...column.displayLabel?.trim() && { displayLabel: column.displayLabel },
        ...column.aggregates?.length && { aggregates: [...column.aggregates] }
      })
    ),
    collapsedLanes: [],
    hiddenLanes: []
  };
}
function parseSavedKanbanLayout(json) {
  if (!json?.trim()) return void 0;
  let value;
  try {
    value = JSON.parse(json);
  } catch {
    return void 0;
  }
  if (!isObject(value)) return void 0;
  const { version, pageSize, columns, collapsedLanes, hiddenLanes } = value;
  const named = Array.isArray(columns) ? columns.filter(isNamedColumn) : [];
  if (version !== void 0 && !isInt32(version) || pageSize !== void 0 && pageSize !== null && !isInt32(pageSize) || !isListOrNone(columns) || !isListOrNone(collapsedLanes) || !isListOrNone(hiddenLanes) || !named.every(isReadableColumn)) {
    return void 0;
  }
  return {
    version: isInt32(version) ? version : SAVED_KANBAN_LAYOUT_VERSION,
    ...isInt32(pageSize) && { pageSize },
    columns: named.flatMap(readViewColumn),
    collapsedLanes: laneKeys(collapsedLanes),
    hiddenLanes: laneKeys(hiddenLanes)
  };
}
var isObject = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
var isInt32 = (value) => typeof value === "number" && Number.isInteger(value) && value >= -2147483648 && value <= 2147483647;
var isListOrNone = (value) => value === void 0 || value === null || Array.isArray(value);
var isNamedColumn = (value) => isObject(value) && typeof value.columnName === "string";
var isReadableColumn = (column) => (column.width === void 0 || column.width === null || isInt32(column.width)) && (column.hidden === void 0 || typeof column.hidden === "boolean") && (column.displayLabel === void 0 || column.displayLabel === null || typeof column.displayLabel === "string");
var laneKeys = (value) => Array.isArray(value) ? value.filter((x) => typeof x === "string") : [];

// src/operation-grants.ts
function operationGrantTakes(grant, parameterNames, recordPermissions) {
  const supplied = new Set([...parameterNames].map((x) => x.toLowerCase()));
  const accepted = new Set(grant.parameters.map((x) => x.toLowerCase()));
  for (const name of supplied) {
    if (!accepted.has(name)) return false;
  }
  for (const record of grant.records ?? []) {
    if (!supplied.has(record.parameter.toLowerCase())) return false;
    const permissions = recordPermissions(record.parameter);
    const required = Number(record.permission);
    if (permissions === void 0 || (Number(permissions) & required) !== required) return false;
  }
  return true;
}
function operationGrantsAllow(grants, parameterNames, recordPermissions) {
  if (!grants) return false;
  const names = [...parameterNames];
  return grants.some((grant) => operationGrantTakes(grant, names, recordPermissions));
}
function operationRunsAlone(grants) {
  return grants?.some((grant) => grant.runsAlone === true) ?? false;
}

// src/week.ts
function firstDayOfWeek(locale) {
  try {
    const intlLocale = new Intl.Locale(locale);
    const firstDay = intlLocale.getWeekInfo?.().firstDay ?? intlLocale.weekInfo?.firstDay;
    return firstDay === void 0 ? 0 : firstDay % 7;
  } catch {
    return 0;
  }
}

// src/saved-search-layout.ts
var SAVED_SEARCH_LAYOUT_VERSION = 1;
function savedSearchLayoutToJson(layout) {
  return JSON.stringify({
    version: layout.version,
    layout: layout.layout ?? void 0,
    orderBy: layout.orderBy ?? void 0,
    descending: layout.descending ?? void 0,
    groupByTable: layout.groupByTable ?? void 0,
    pageSize: layout.pageSize ?? void 0,
    mineOnly: layout.mineOnly ?? void 0,
    activeOnly: layout.activeOnly ?? void 0,
    modifiedWithinDays: layout.modifiedWithinDays ?? void 0,
    columnLabels: layout.columnLabels.map(({ table, column, label }) => ({ table, column, label }))
  });
}
function searchColumnLabelOf(layout, table, column) {
  return layout?.columnLabels.find(
    (x) => x.table.toLowerCase() === table.toLowerCase() && x.column.toLowerCase() === column.toLowerCase()
  )?.label;
}
function parseSavedSearchLayout(json) {
  if (!json?.trim()) return void 0;
  let value;
  try {
    value = JSON.parse(json);
  } catch {
    return void 0;
  }
  if (!isObject2(value)) return void 0;
  const { version, layout, orderBy, descending, groupByTable, pageSize, mineOnly, activeOnly, modifiedWithinDays, columnLabels } = value;
  if (version !== void 0 && !isInt322(version) || layout !== void 0 && layout !== null && typeof layout !== "string" || orderBy !== void 0 && orderBy !== null && typeof orderBy !== "string" || descending !== void 0 && descending !== null && typeof descending !== "boolean" || groupByTable !== void 0 && groupByTable !== null && typeof groupByTable !== "boolean" || pageSize !== void 0 && pageSize !== null && !isInt322(pageSize) || mineOnly !== void 0 && mineOnly !== null && typeof mineOnly !== "boolean" || activeOnly !== void 0 && activeOnly !== null && typeof activeOnly !== "boolean" || modifiedWithinDays !== void 0 && modifiedWithinDays !== null && !isInt322(modifiedWithinDays) || !(columnLabels === void 0 || columnLabels === null || Array.isArray(columnLabels))) {
    return void 0;
  }
  return {
    version: isInt322(version) ? version : SAVED_SEARCH_LAYOUT_VERSION,
    ...typeof layout === "string" && { layout },
    ...typeof orderBy === "string" && { orderBy },
    ...typeof descending === "boolean" && { descending },
    ...typeof groupByTable === "boolean" && { groupByTable },
    ...isInt322(pageSize) && { pageSize },
    ...typeof mineOnly === "boolean" && { mineOnly },
    ...typeof activeOnly === "boolean" && { activeOnly },
    ...isInt322(modifiedWithinDays) && { modifiedWithinDays },
    columnLabels: Array.isArray(columnLabels) ? columnLabels.filter(isLabel) : []
  };
}
var isObject2 = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
var isInt322 = (value) => typeof value === "number" && Number.isInteger(value) && value >= -2147483648 && value <= 2147483647;
var isText = (value) => typeof value === "string" && value.length > 0;
var isLabel = (value) => isObject2(value) && isText(value.table) && isText(value.column) && isText(value.label);

// src/multi-table-page-merger.ts
var MultiTablePageMerger = class {
  /**
   * @param descending Whether the rows run last to first, as the request asked.
   * @param locale The locale text is compared in. Unset, the browser's.
   */
  constructor(descending = false, locale) {
    this.descending = descending;
    this.collator = new Intl.Collator(locale, { sensitivity: "base" });
  }
  descending;
  streams = [];
  shown = /* @__PURE__ */ new Set();
  // Every row read, shown or not; and how many of each table's, by its name in lower case.
  read = /* @__PURE__ */ new Set();
  counts = /* @__PURE__ */ new Map();
  collator;
  orderType = MultiTableOrderType.Text;
  started = false;
  /** Whether a page has been added: before one, the first request reads every stream's first page. */
  get isStarted() {
    return this.started;
  }
  /** Whether any row is left to hand out, read or not. */
  get hasMore() {
    return this.hasMoreOf(void 0);
  }
  /**
   * How many of `table`'s rows have been read — a row both its reads found once — and whether it has
   * more to read: at least that many match, and exactly that many without more. Undefined for a table
   * none of whose pages has been read.
   */
  countOf(table) {
    const streams = this.of(table);
    return streams.length === 0 ? void 0 : { read: this.counts.get(table.toLowerCase()) ?? 0, more: streams.some((x) => x.more) };
  }
  /** The tables the pages were read from, in the order the server planned them. */
  get tables() {
    return [...new Set(this.streams.map((x) => x.table))];
  }
  /** Whether any row of `table`'s — of every table's, unset — is left to hand out, read or not. */
  hasMoreOf(table) {
    const streams = this.of(table);
    this.next(streams);
    return streams.some((x) => x.more || x.rows.length > 0);
  }
  /** Forgets every page: for a new search, view, set of tables or order. */
  reset() {
    this.streams.length = 0;
    this.shown.clear();
    this.read.clear();
    this.counts.clear();
    this.started = false;
  }
  /** Keeps a response's pages: each stream's rows behind those it holds already. */
  add(response) {
    this.started = true;
    this.orderType = response.orderType ?? MultiTableOrderType.Text;
    for (const page of response.pages ?? []) {
      let stream = this.streams.find((x) => x.name.toLowerCase() === page.stream.toLowerCase());
      if (!stream) {
        stream = { name: page.stream, table: page.table, rows: [], last: void 0, page: 0, cookie: void 0, more: true };
        this.streams.push(stream);
      }
      const number = Number(page.page);
      if (number <= stream.page) continue;
      stream.page = number;
      stream.cookie = page.pagingCookie ?? void 0;
      stream.more = page.more === true;
      for (const item of page.items ?? []) {
        stream.rows.push(item);
        stream.last = item;
        const key = keyOf(item);
        if (!this.read.has(key)) {
          this.read.add(key);
          this.counts.set(item.table.toLowerCase(), (this.counts.get(item.table.toLowerCase()) ?? 0) + 1);
        }
      }
    }
    for (const failure of response.failures ?? []) {
      for (const stream of this.streams) if (stream.table.toLowerCase() === failure.table.toLowerCase()) stream.more = false;
    }
  }
  /**
   * Up to `count` rows, in order, that no stream's unread rows could come before — of `table`'s
   * streams alone, for a list grouped by table. Fewer when a stream holds the rest back:
   * {@link readsNeeded} says which to read.
   */
  take(count, table) {
    const streams = this.of(table);
    const taken = [];
    for (let next = this.next(streams); taken.length < count && next; next = this.next(streams)) {
      if (!this.isCertain(next.stream, next.item, streams)) break;
      next.stream.rows.shift();
      const key = keyOf(next.item);
      if (!this.shown.has(key)) {
        this.shown.add(key);
        taken.push(next.item);
      }
    }
    return taken;
  }
  /**
   * The streams to read the next page of before more rows can be handed out — of `table`'s alone,
   * for a list grouped by table: those with more to read that haven't read past the next row, or
   * every one with more when nothing is left to hand out.
   */
  readsNeeded(table) {
    const streams = this.of(table);
    const next = this.next(streams);
    return streams.filter((x) => x.more && (!next || x !== next.stream && !this.isPast(x, next.item))).map((x) => ({ stream: x.name, page: x.page + 1, ...x.cookie !== void 0 && { pagingCookie: x.cookie } }));
  }
  of(table) {
    return table === void 0 ? this.streams : this.streams.filter((x) => x.table.toLowerCase() === table.toLowerCase());
  }
  // The first unshown row of all the streams, each stream's in its own order.
  next(streams) {
    let first;
    for (const stream of streams) {
      while (stream.rows.length > 0 && this.shown.has(keyOf(stream.rows[0]))) stream.rows.shift();
      const row = stream.rows[0];
      if (row && (!first || this.compare(row, first.item) < 0)) first = { stream, item: row };
    }
    return first;
  }
  // Whether no stream but the row's own could still read a row that comes before it.
  isCertain(own, item, streams) {
    return streams.every((x) => x === own || !x.more || this.isPast(x, item));
  }
  // Whether a stream has read past a row: its unread rows, coming after its last in its own order,
  // can't come before the row — a later value, or the same value in a table after the row's.
  isPast(stream, item) {
    if (!stream.last) return false;
    const values = this.compareOrders(stream.last, item);
    return values > 0 || values === 0 && ordinal(stream.table, item.table) > 0;
  }
  // The order rows are handed out in: by the column they are ordered by, then table, then id.
  compare(x, y) {
    const values = this.compareOrders(x, y);
    if (values !== 0) return values;
    const tables2 = ordinal(x.table, y.table);
    return tables2 !== 0 ? tables2 : ordinal(x.id, y.id);
  }
  compareOrders(x, y) {
    const ranks = Number(x.rank ?? 0) - Number(y.rank ?? 0);
    if (ranks !== 0) return ranks;
    const order = this.compareValues(comparable(orderOf(x)), comparable(orderOf(y)));
    return this.descending ? -order : order;
  }
  // Two values, the empty one first.
  compareValues(a, b) {
    if (a === void 0 || b === void 0) return a === void 0 ? b === void 0 ? 0 : -1 : 1;
    if (this.orderType === MultiTableOrderType.Text && typeof a === "string" && typeof b === "string") return this.collator.compare(a, b);
    return a < b ? -1 : a > b ? 1 : 0;
  }
};
var keyOf = (item) => `${item.table}:${item.id}`.toLowerCase();
var ordinal = (a, b) => a < b ? -1 : a > b ? 1 : 0;
function orderOf(item) {
  const value = item.record?.properties?.[item.orderColumn ?? item.title.column];
  if (value && typeof value === "object" && value.$type === "RelatedRecordValue" && value.value && typeof value.value === "object") {
    return value.value.value;
  }
  return value?.value;
}
function comparable(value) {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    if (value.length === 0) return void 0;
    return value;
  }
  return void 0;
}

// src/index.ts
var VERSION = "0.1.0";

export { ALL_CONDITION_OPERATORS, ALL_FETCH_XML_LINK_TYPES, ALL_FETCH_XML_OPERATOR_STRINGS, AcceptInvitationResult, AddPasskeyResult, AggregateType, ArchiveFormat, ArgumentError, ArgumentNullError, AuthClient, BROWSER_TIME_ZONE_COOKIE, CONDITION_OPERATOR_NAMES, CaptchaForms, CaptchaProvider, CaptchaVerificationError, ChangeEmailResult, ChangePasswordResult, CloseRecordAction, ColumnSecurityPermission, ColumnSourceType, ColumnType, ConditionOperator, ConfirmExternalLoginResult, DateGrouping, DateTimeBehavior, DeletePersonalDataResult, DistanceUnit, EntityRole, ExternalLoginCandidateKind, FORMATTED_AGGREGATES, FetchXmlParseError, FetchXmlProblemCode, FileArchiveResponseFormat, GridColumnPin, GridExportFormat, GridLayout, INVITATION_EXTERNAL_SIGN_IN_PARAMETER, ImpersonateResult, InvalidOperationError, InvitationLookupResult, JoinOperator, KANBAN_NO_VALUE_KEY, KANBAN_RANK_STEP, KanbanDateBound, KanbanDateLane, KanbanDateLaneDrop, KanbanDateLaneOrder, KanbanDatePeriod, KanbanLaneKind, LocalizationSourceKind, LocalizationSourceStatus, LogicalOperator, LoginResult, MAX_AGGREGATE_FORMAT_LENGTH, MemoFormat, MultiTableFailureReason, MultiTableOrderType, MultiTablePageMerger, MultiTablePageOrder, MultiTableSeries, MultiTableUses, MultiTableViewSchema, MultiTableViewTableSchema, MultiTableViewType, OrderType, PagingCookies, PasskeyCanceledError, PortalUserType, PowerBIConnectionRequiredError, PowerPortalsProClient, PowerPortalsProError, QUERY_DATE_GROUPINGS, QueryAggregate, QueryGroupingStatus, RECORD_PAGES_LIFETIME_MS, RECORD_PAGES_RETRY_MS, RECORD_PAGE_ID_PLACEHOLDER, RecordPagesCache, RegisterResult, RelationshipFilterMode, RequiredLevel, ResetPasswordResult, Routes, SAVED_GRID_VIEW_LAYOUT_VERSION, SAVED_KANBAN_LAYOUT_VERSION, SAVED_SEARCH_LAYOUT_VERSION, SavedViewControlKind, SavedViewSchema, SearchKind, SortDirection, StateChangeKind, StopImpersonationResult, SwitchIdentityResult, TableSecurityPermission, TextFormat, Transport, UNION_FILTER_HINT, UnauthorizedAccessError, UserViewSettingSchema, VERSION, ViewType, activityPartyName, addAttribute, addCondition, addFilter, addLinkEntity, addOrder, aggregateTypeFromFetchXml, aggregateTypeToFetchXml, aggregateTypesForColumn, aggregatesToTotals, applyGridCriteria, areColumnTypesComparable, arrangeKanbanLanes, canInvertFilter, canReadColumn, canWriteColumn, captchaAction, clearNoDataFilter, closeActionForReason, closeReasonsFor, closeRecordDefinition, conditionOperatorFromFetchXml, conditionOperatorName, conditionOperatorToFetchXml, controlKeysOf, controlToJson, createAttribute, createCondition, createEntity, createFetchXmlQuery, createFilter, createLinkEntity, createNodeId, createOrder, dateGroupingFromFetchXml, dateGroupingToFetchXml, dateStepsOf, decidingViewSetting, decodeEntities, defaultGridColumnFilterOperator, defaultViewId, escapeAttribute, escapeText, everyOfKind, extractGridCriteria, findColumns, findColumnsFilter, findGridColumnFilter, findRecordPage, firstDayOfWeek, forgetViewSettings, formatControls, formatLikeValue, fromZonedWallClock, getAllEntities, getAllLinkEntities, getColumnPermissions, getConditionOperatorInfo, getConditionOperatorsForColumnType, getConditionValueCount, getLinkAliases, getRecordReferenceId, gridAggregateName, gridAggregateNamed, gridColumnViewJoin, gridFilteredColumns, hasBlockingProblem, hasCloseActions, hasColumn, hasNoDataFilter, hasSavedId, hasUnionFilterHint, inferLikeOperator, invertFilter, invertFilterConditions, isCaptchaRequired, isDateOnly, isDateStep, isGovernedTable, isInvitationExternalSignInReturn, isNoDataCondition, isPasskeyCancellation, isPasskeySupported, isValidAggregateFormat, isValidControlKey, isViewHidden, joinOperatorFromFetchXml, joinOperatorToFetchXml, joinTypeReturnsColumns, kanbanDateDayOf, kanbanDateLaneContains, kanbanDateLaneFilter, kanbanDateLaneKeyOf, kanbanDatePeriodStart, kanbanDateValueFor, kanbanLaneFilter, kanbanLaneKey, kanbanLaneKind, kanbanLanes, kanbanLayoutOf, kanbanLookupLanes, kanbanMoveOf, kanbanMoveUpdate, kanbanRanksForDrop, keepingGroupability, logicalOperatorFromFetchXml, logicalOperatorToFetchXml, mapProblemDetails, moveItem, moveOrder, multiTableViewTablesQuery, multiTableViewsQuery, namesItself, operationGrantTakes, operationGrantsAllow, operationRunsAlone, oppositeConditionOperator, orderTypeToFetchXml, parseControl, parseControls, parseFetchXml, parseSavedGridViewLayout, parseSavedKanbanLayout, parseSavedSearchLayout, parseXml, placementForJoinType, readColumnRawValue, readPartyList, readRangeSize, readRelatedRecords, recordPageNewUrl, recordPageUrl, relatedRecordName, relatedRecordsKey, removeNode, renameLinkAlias, resolveColumnDisplayValue, resolveKanbanDateLanes, rootRecordPageUrl, sameControl, sameGridCriteria, savedGridViewLayoutToJson, savedGridViewLayoutsArrangeAlike, savedKanbanLayoutToJson, savedSearchLayoutToJson, savedViewControl, savedViewsQuery, searchColumnLabelOf, serializeXml, setDefaultView, setFindColumns, setGridColumnFilter, setLinkJoinType, setNoDataFilter, setViewHidden, shareResponseColumnPermissions, shownViewTables, splitGridColumn, stateChangeOf, stateReasonsOf, stripLikeWildcards, supportsColumnComparison, toFetchXml, toViewTable, toViewTableRow, toZonedWallClock, todayIn, totalsToAggregates, transformQuery, uniqueAlias, updateAttribute, updateCondition, updateEntity, updateFilter, updateOrder, updateQuery, userViewSettingsQuery, validateFetchXml, validateKanbanDateLanes, viewSettingsQuery, withInvitationExternalSignIn, writeBrowserTimeZoneCookie };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map